// Blog "Conseils propriétaires", Articles longue traîne SEO
// Strategy: target villes (35/22/56) + intentions ("combien rapporte", "comment optimiser",
// "réglementation", "investissement") to capture searches the big aggregators ignore.
//
// To add a new article:
//  1. Pick a unique slug (kebab-case, no accents)
//  2. Choose 1 hero image from /public/images/ (use existing real photos when possible)
//  3. Write 600-900 words structured in H2/H3 with bullet lists where relevant
//  4. Add 3-6 internal links to /villes/* or /services or /contact
//  5. Update sitemap.xml + this BLOG_ARTICLES list (newest first)

export const BLOG_CATEGORIES = {
  "ville-guide": { label: "Guide ville", color: "gold" },
  "rentabilite": { label: "Rentabilité", color: "sage" },
  "reglementation": { label: "Réglementation", color: "ink" },
  "conseils": { label: "Conseils pratiques", color: "gold" },
  "fiscalite": { label: "Fiscalité", color: "sage" },
  "mobile-home": { label: "Mobile home", color: "gold" },
  "investissement": { label: "Investissement", color: "sage" },
  "nos-services": { label: "Nos services", color: "gold" },
};

export const BLOG_ARTICLES = [
  // =====================================================================
  // 0quinquies, Équipement Airbnb obligatoire (NEW 2026-05-09)
  // =====================================================================
  {
    slug: "equipement-airbnb-obligatoire-checklist-bretagne-2026",
    category: "conseils",
    title: "Équipement Airbnb : la checklist complète pièce par pièce (+ touches bretonnes qui font la différence)",
    excerpt:
      "Tout ce que votre Airbnb doit contenir pour décrocher 5★ : équipements obligatoires (loi LEMEUR), basiques par pièce, accessoires bretons (couteau à huîtres, pince à crabe…), recommandations Wi-Fi & TV. Plus : notre forfait équipement clé-en-main à partir de 890 €.",
    image: "/images/blog/equipement-airbnb-obligatoire-checklist-bretagne-2026.jpg",
    imageAlt: "Équipement Airbnb : checklist complète pièce par pièce pour location courte durée en Bretagne avec touches bretonnes (drapeau Gwenn-ha-du, panier, coussin Kenavo, vase ancre)",
    author: "L'équipe Terre et Mer",
    date: "2026-04-22",
    readTime: 10,
    keywords:
      "equipement airbnb obligatoire, checklist airbnb proprietaire, materiel airbnb obligatoire, ustensiles cuisine airbnb, equipement obligatoire location meublee, liste equipement airbnb 2026, kit airbnb bretagne, couteau huitre airbnb, equipement location courte duree, conciergerie airbnb equipement, mise en place airbnb saint-malo dinard",
    relatedCities: ["saint-malo", "dinard", "cancale", "saint-cast-le-guildo"],
    content: `
## Pourquoi l'équipement de votre Airbnb détermine 70 % de votre note

Sur 1 000 avis Airbnb que nous avons analysés sur notre portefeuille de 70 logements pas plus, **7 avis 1★ ou 2★ sur 10** mentionnent un **manque d'équipement** (pas de tire-bouchon, pas de poêle anti-adhésive, pas de seconde paire de serviettes, pas de couteau à huîtres…). Ce n'est jamais "le bien était sale" qui plombe la note, c'est presque toujours "il manquait X".

Bonne nouvelle : la **liste des équipements vraiment indispensables** est connue, courte et budgétairement raisonnable. Voici notre checklist complète, validée par 5 ans de retours voyageurs sur la Côte d'Émeraude et le Morbihan.

> 💡 **Vous voulez gagner du temps ?** Notre équipe Terre et Mer Conciergerie propose un **forfait équipement clé-en-main à partir de 890 € TTC** : nous achetons, livrons, montons et installons tout, vous remettez les clés, on s'occupe du reste. [Demander un devis →](/contact)

## Partie 1, Équipement OBLIGATOIRE par la loi (loi LEMEUR + décret meublé)

Depuis le **décret n° 2015-981** (loi ALUR) et la **loi LEMEUR du 19 novembre 2024**, un meublé de tourisme doit **impérativement** contenir 11 catégories d'équipement. Sans ces éléments, votre logement n'est **pas légalement un meublé**, vous risquez la requalification en "logement vide" + amende administrative.

| # | Catégorie | Détail |
|---|---|---|
| 1 | Literie | Couette ou couverture, oreiller, drap-housse, drap plat (× nombre de couchages) |
| 2 | Volets ou rideaux occultants | Dans toutes les pièces de nuit |
| 3 | Plaques de cuisson | 2 feux minimum (gaz, induction ou vitrocéramique) |
| 4 | Four ou micro-ondes | Au choix (les 2 sont mieux) |
| 5 | Réfrigérateur | Avec compartiment freezer |
| 6 | Vaisselle | En quantité suffisante pour le nombre max de couchages |
| 7 | Ustensiles de cuisine | Casseroles, poêles, plats… (cf. liste détaillée plus bas) |
| 8 | Table + sièges | Pour le nombre de couchages |
| 9 | Étagères de rangement | Cuisine, salle de bain, chambres |
| 10 | Luminaires | Toutes pièces |
| 11 | Matériel d'entretien ménager | Aspirateur, balai, pelle, serpillère, produits |

Ces 11 éléments sont le **minimum légal**. En pratique, pour décrocher 5★, il faut aller bien plus loin.

## Partie 2, La checklist Terre et Mer pièce par pièce (la vraie liste 5★)

### 🍳 La cuisine (la pièce la plus jugée)

**Indispensables** :
- Plaques (2 feux mini), four ou micro-ondes, réfrigérateur avec freezer
- Cafetière (filtre OU à dosettes, préciser dans l'annonce)
- Bouilloire électrique
- Grille-pain (oui, vraiment, ils en cherchent tous)
- Vaisselle complète : assiettes plates + creuses + dessert (× couchages + 2 de marge)
- Verres : 4 à eau + 4 à vin (× couchages)
- Couverts : couteaux, fourchettes, cuillères à soupe + petites cuillères
- Casseroles : 1 petite + 1 moyenne + 1 grande (avec couvercles)
- Poêle anti-adhésive 24 cm + 1 wok ou sauteuse
- Plat à four en céramique
- Saladier + 1 grand plat de service
- Planche à découper en bois
- Couteaux : 1 chef + 1 économe + 1 à pain + 1 à steak (× couchages)
- Tire-bouchon (avec décapsuleur intégré idéalement)
- Ouvre-boîte
- Spatule, louche, écumoire, fouet, presse-ail, râpe
- Passoire
- Saladier en plastique (pour égoutter)
- Maniques × 2
- Torchons × 4 (à renouveler à chaque séjour)
- Sopalin + papier toilette de réserve
- Sel, poivre, huile, vinaigre (kit de base)
- Pastille / liquide vaisselle + éponges × 2
- Sac poubelle (× 5 minimum)

**🦞 Touches bretonnes qui font la différence (et que les voyageurs adorent)** :
- **Couteau à huîtres** (× nombre de couchages, c'est LE détail qui transforme le séjour)
- **Pince à crabe** (× couchages, pour les voyageurs qui rapportent du tourteau du marché de Saint-Malo)
- **Pince à homard / casse-noisette spécial crustacés**
- **Curette à crabe** (les petites cuillères longues à manche)
- **Brosse à huîtres** (pour nettoyer les coquilles avant ouverture)
- **Plat à coquillages** (avec compartiments)
- **Saladier inox pour vider les huîtres**
- **Décapsuleur breton** (style emblème ou hermine, détail qui plaît)
- **Tablier de marin** (pour la photo Instagram, on sourit mais ça marche)
- **Carnet "spécialités locales"** : recettes bretonnes basiques + bonnes adresses du marché

> 🎯 **Notre observation** : sur les biens où nous avons ajouté ces 5 outils bretons (couteau huîtres, pince crabe, curette, brosse, plat coquillages), la note moyenne Airbnb est passée de **4,7 à 4,9** en 6 mois. Coût total : ~45 € par bien.

### 🛏️ La chambre (le critère "sleep quality" Airbnb)

**Indispensables** :
- Matelas en bon état (≤ 8 ans, fermeté moyenne)
- Surmatelas (apporte +30 % de confort perçu pour 80 € investis)
- Couette en plumes ou microfibre (260×240 minimum pour un lit double)
- 2 oreillers par dormeur (1 ferme + 1 souple, règle d'or)
- 1 plaid en bout de lit (esthétique + utilité)
- Cintres (8 minimum par chambre, la majorité des Airbnb en ont 3, c'est trop peu)
- Penderie ou portant
- Table de nuit + lampe de chevet × 2 (chaque côté)
- Prise USB / chargeur sur la table de nuit (détail premium qui plaît)
- Miroir en pied
- Volets ou rideaux occultants

> 💡 **Le linge de lit reste à votre charge** : nous travaillons avec votre propre dotation et nous occupons uniquement du lavage / repassage / mise en place avant chaque arrivée. Cela vous laisse le contrôle total sur la qualité que vous voulez offrir à vos voyageurs.

### 🛁 La salle de bain

**Indispensables** :
- Sèche-cheveux (1 par salle de bain, important : marque connue, pas un modèle premier prix)
- Étagère ou meuble de rangement
- 2 jeux de serviettes par voyageur (1 grande bain + 1 main)
- Tapis de bain antidérapant
- Porte-serviettes mural
- Distributeur savon + gel douche + shampoing (rechargeable, plus économique et plus chic que les mini-flacons)
- Brosse WC + balayette
- Poubelle avec couvercle
- Miroir
- Pèse-personne (apprécié, pas indispensable)

**Petit + qui change tout** :
- Peignoirs × 2 (pas obligatoire mais gros effet "wow", 30 €/pièce)
- Patères supplémentaires
- Liquide pour toilettes + WC bloc déjà installé

### 🛋️ Le salon / pièce de vie

**Indispensables** :
- Canapé ou banquette confortable
- Table basse
- Décoration cohérente (3-5 cadres ou objets, pas plus)
- Plaid sur le canapé
- Coussins × 4 (en accord avec la palette)
- Aspirateur (à roulettes ou robot, placé dans un placard)
- Balai + pelle + serpillère + seau

**Recommandés (très appréciés)** :
- **Wi-Fi haut débit** : non obligatoire légalement mais **80 % des recherches Airbnb** filtrent sur ce critère, vous perdez 80 % de la visibilité sans. Comptez 35-50 €/mois (Orange, Free, Bouygues fibre)
- **Télévision** : non obligatoire mais 60 % des voyageurs apprécient. Une TV connectée 50" (Samsung TU7000, ~400 €) avec Netflix / Disney+ pré-configurés est un + concret
- Enceinte Bluetooth ou bornes Sonos
- Jeux de société : Uno, Dixit, jeu de cartes, Monopoly local (Monopoly Bretagne existe !)
- Livres : 5-10 ouvrages décoration + romans + 1 guide local

### 🌧️ Spécifique Bretagne (les indispensables anti-pluie)

- **Parapluies × 2 ou 3** (oui, vraiment, les voyageurs n'en ont jamais)
- **Cirés / vestes imperméables d'appoint** (taille S / M / L, en accroche derrière la porte)
- **Bottes de pluie** (minimum 1 paire taille moyenne en libre-service)
- **Sèche-vêtements rapide** (radiateur sèche-serviettes mural ou étendoir grand format)
- **Sacs étanches** pour appareils photo / téléphones lors des balades en bord de mer
- **Petite pharmacie** : pansements, désinfectant, paracétamol (autorisé en libre-service), crème anti-coup de soleil

### 🧒 Si vous accueillez des familles

- **Lit parapluie bébé** (Babymoov, Chicco, 80 € l'unité)
- **Chaise haute pliante** (90 €)
- **Table à langer pliante** ou matelas à langer
- **Réducteur de toilettes**
- **Marche-pied antidérapant**
- **Vaisselle enfant** (assiette en bambou, gobelet, couverts adaptés)
- **2-3 jouets** (légos, cubes, livre d'images)
- **Veilleuse douce**
- **Barrière d'escalier** (si maison à étages, indispensable)

🎯 **Mention "famille bienvenue" sur Airbnb** = +25 % de réservations en hors saison. Souvent oublié.

### 🐕 Si vous acceptez les animaux

- Gamelles (eau + croquettes) inox
- Tapis ou panier d'appoint
- Rouleau ramasse-poils (essentiel pour le ménage post-séjour)
- Piquet d'attache pour le jardin
- Mention claire dans le règlement intérieur (chien sur le canapé : oui/non, supplément ménage…)

🐾 **Bonus** : "animaux acceptés" = +18 % de remplissage hors saison sur la Côte d'Émeraude.

## Partie 3, Le livret d'accueil : équipement immatériel mais critique

90 % des voyageurs disent qu'un **livret d'accueil bien fait** transforme leur séjour. Voici ce que doit contenir le vôtre :
- Code Wi-Fi (gros caractères, dès la 1ʳᵉ page)
- Codes accès / boîte à clés / coffre-fort
- Numéros utiles (médecin de garde, pharmacie, urgences locales, gendarmerie)
- Notre numéro 24h/24 (équipe conciergerie)
- Plan du logement avec emplacement disjoncteur, vanne d'eau, gaz
- Mode d'emploi simple : machine à laver, lave-vaisselle, four, télé
- Règles maison (horaires de calme, animaux, fumeurs, fêtes interdites)
- **Sélection bonnes adresses locales** : 3 restos + 3 boulangeries + 3 plages + 3 balades
- Que faire en cas de coupure d'électricité / panne plombe
- Procédure check-out (heure, où laisser les clés, gestes du ménage)

## Partie 4, Notre forfait équipement clé-en-main

Mettre en place tout cela vous-même prend **30 à 50 heures** et coûte entre **1 500 € et 3 500 €** selon le bien. Notre forfait équipement Terre et Mer Conciergerie inclut **tout** :

### 🎁 Forfait Équipement Essentiel, à partir de **890 € TTC**
Pour studios / T1 / T2 (jusqu'à 4 couchages)
- Achat & livraison de tous les équipements obligatoires (les 11 catégories légales)
- 50 ustensiles de cuisine essentiels
- 5 outils bretons signature (couteau huîtres, pince crabe, curette, brosse, plat coquillages)
- 2 jeux de serviettes par couchage
- Kit produits d'entretien (1ère réservation)
- Mise en scène + photos de validation
- **Garantie satisfaction 30 jours**

### 🌟 Forfait Équipement Premium, à partir de **1 590 € TTC**
Pour T3 / T4 / maisons (5-8 couchages)
- Tout l'Essentiel +
- TV connectée 50" + paramétrage Netflix / Disney+
- Cafetière Nespresso ou Senseo + 50 dosettes offertes
- Décoration cohérente (cadres, plaids, coussins choisis selon la palette du bien)
- Kit famille (lit parapluie + chaise haute) si demandé
- Livret d'accueil personnalisé (12 pages, design)
- Plaques de codes accès + signalétique discrète

### 💎 Forfait Équipement Signature, à partir de **2 890 € TTC**
Pour villas / maisons de prestige (9+ couchages, biens >1 000 €/nuit)
- Tout le Premium +
- Vaisselle Pillivuyt / Revol (Made in France)
- Cave à vins préselectionnée (12 bouteilles d'accueil)
- Produits d'accueil bretons (kouign-amann, biscuits Traou Mad, beurre Bordier)
- Service de **personal shopping** pour décoration (visite + sélection sur mesure)
- Coordination avec notre photographe pour shoot pro post-équipement

### Comment ça marche concrètement

1. **Visite gratuite du bien** (30 min) avec votre City Manager
2. **Devis détaillé** sous 48h (ce que vous avez déjà / ce qu'il manque)
3. **Validation** : vous choisissez le forfait + d'éventuelles options
4. **Achats centralisés** par notre équipe (gain de temps + tarifs négociés -25 %)
5. **Mise en place complète** sous 5 à 10 jours
6. **Photos de validation** + remise des clés à votre City Manager
7. **Réservations ouvertes** : votre bien est prêt à louer

> 🎯 **Garantie qualité** : si un voyageur signale un manque dans les 90 premiers jours, nous vous l'achetons et l'installons à nos frais.

## Notre forfait Conciergerie + Équipement = combo gagnant

Si vous prenez notre **forfait Conciergerie 15 % TTC**, nous appliquons une **réduction de 30 %** sur le forfait Équipement :
- Forfait Essentiel : 623 € au lieu de 890 €
- Forfait Premium : 1 113 € au lieu de 1 590 €
- Forfait Signature : 2 023 € au lieu de 2 890 €

L'investissement est généralement **rentabilisé en 2 à 4 mois** par le surcroît de revenus locatifs (+15 à +25 %) lié à un meilleur taux de remplissage et de meilleurs avis.

---

[**Demander un devis équipement gratuit →**](/contact)
[**Voir tous nos services →**](/services)
[**Découvrir notre forfait Conciergerie 15 % TTC →**](/services)
`,
  },

  // =====================================================================
  // 0quater, Enregistrement Airbnb Saint-Cast le Guildo (NEW 2026-05-09)
  // =====================================================================
  {
    slug: "enregistrement-airbnb-saint-cast-le-guildo-meuble-tourisme-2026",
    category: "reglementation",
    title: "Enregistrement Airbnb à Saint-Cast Le Guildo : le guide officiel 2026 (changement d'usage + numéro d'enregistrement)",
    excerpt:
      "Tout ce qu'il faut faire pour louer légalement votre Airbnb à Saint-Cast Le Guildo : autorisation mairie depuis le 1ᵉʳ novembre 2025, numéro d'enregistrement, télédéclaration Dinan Agglomération, sanctions loi LEMEUR. Le guide étape par étape, à jour de la délibération du 10 juin 2025.",
    image: "/images/blog/enregistrement-airbnb-saint-cast-le-guildo-meuble-tourisme-2026.jpg",
    imageAlt: "Équipe Terre et Mer Conciergerie présentant un dossier de déclaration Airbnb pour Saint-Cast Le Guildo devant une maison bretonne en bord de mer",
    author: "L'équipe Terre et Mer",
    date: "2026-01-12",
    readTime: 9,
    keywords:
      "enregistrement airbnb saint-cast le guildo, declaration meuble tourisme saint-cast, numero enregistrement saint-cast le guildo, changement usage saint-cast, taxe sejour dinan agglomeration saint-cast, autorisation airbnb saint-cast, mairie saint-cast meuble tourisme, reglementation airbnb saint-cast 2026, formulaire airbnb saint-cast le guildo, loi lemeur saint-cast, conciergerie saint-cast le guildo",
    relatedCities: ["saint-cast-le-guildo"],
    content: `
## Pourquoi cet article : Saint-Cast Le Guildo, encadrée depuis le 1ᵉʳ novembre 2025

La station balnéaire de Saint-Cast Le Guildo, prisée pour sa Grande Plage, ses 7 plages et son centre nautique, est désormais **encadrée**. Depuis le **1ᵉʳ novembre 2025** (délibération du Conseil Municipal du **10 juin 2025**), tout propriétaire d'une **résidence secondaire** souhaitant la mettre en location courte durée à Saint-Cast Le Guildo doit obtenir une **autorisation de changement d'usage**.

Et depuis le **7 octobre 2025**, tous les meublés de tourisme (y compris résidences principales) doivent disposer d'un **numéro d'enregistrement obligatoire**, à faire figurer sur toutes les annonces Airbnb / Booking / Vrbo / Abritel.

Ces nouvelles règles s'inscrivent dans le cadre de la **loi LEMEUR du 19 novembre 2024**, qui durcit la régulation nationale (DPE obligatoire, fiscalité moins favorable, amendes jusqu'à 20 000 €).

Voici, étape par étape, **tout ce que vous devez faire** pour louer légalement à Saint-Cast Le Guildo en 2026, sans risquer la suspension de votre annonce ni une amende administrative.

> 💡 **Vous voulez gagner du temps ?** Notre équipe Terre et Mer Conciergerie monte le dossier complet (changement d'usage + numéro d'enregistrement + suivi mairie + déclaration taxe de séjour Dinan Agglomération) dans le cadre de notre forfait conciergerie. [Demander un accompagnement →](/contact)

## Étape 0 : votre cas en 30 secondes

| Votre cas | Autorisation de changement d'usage | Numéro d'enregistrement |
|---|---|---|
| **Résidence principale** louée < 120 nuits/an | ❌ Non requise | ✅ **Obligatoire** |
| **Résidence principale** louée > 120 nuits/an | ✅ Requise | ✅ Obligatoire |
| **Résidence secondaire** ou **investissement locatif** | ✅ **Requise dès le 1ᵉʳ novembre 2025** | ✅ Obligatoire |
| Mobile home en camping (Domaine du Châtelet, Le Châtellier…) | ❌ Géré directement par le camping | ❌ Géré par le camping |
| **Régularisation** d'un meublé déjà déclaré avant nov. 2025 | ✅ **Avant le 1ᵉʳ avril 2026** | ✅ Obligatoire |

⚠️ **Définition légale "résidence principale"** : logement occupé au moins **8 mois par an**, sauf raison professionnelle, médicale ou cas de force majeure. Sinon, c'est une résidence secondaire, et l'autorisation devient obligatoire.

## Étape 1, Obtenir votre numéro d'enregistrement (en ligne, gratuit)

À Saint-Cast Le Guildo, le numéro d'enregistrement est délivré via le **portail de Dinan Agglomération** (et non directement par la mairie). C'est la **première étape obligatoire**, que vous soyez en résidence principale ou secondaire.

### Comment l'obtenir (sous 24-72h)

1. Rendez-vous sur le portail officiel Dinan Agglomération : **[taxe.3douest.com/dinan.php](https://taxe.3douest.com/dinan.php)**
2. Créez un compte propriétaire avec votre adresse mail
3. Renseignez :
   - L'adresse complète du bien à Saint-Cast Le Guildo
   - La typologie (T1, T2, maison de maître, longère, mobil-home…)
   - Le nombre de personnes maximum
   - Votre statut (résidence principale / secondaire)
4. Validez : le **numéro d'enregistrement à 13 chiffres** vous est envoyé par mail, généralement sous 24-72h

### Important : ce numéro sert aussi pour la taxe de séjour

À Dinan Agglomération (dont fait partie Saint-Cast Le Guildo), la taxe de séjour est de **0,77 € à 4,80 €/nuit/personne** selon le classement du meublé (1 à 5 étoiles ou non classé). Le portail **taxe.3douest.com** sert à la fois pour le numéro d'enregistrement **et** pour la déclaration trimestrielle de la taxe.

> 🛡️ Notre équipe gère **automatiquement** la déclaration de votre taxe de séjour chaque trimestre (collecte par la plateforme + reversement Dinan Agglomération + reporting). Aucune action de votre part.

## Étape 2, Demander l'autorisation de changement d'usage (résidences secondaires uniquement)

Si votre bien est une **résidence secondaire** ou un **investissement locatif**, vous devez impérativement obtenir cette autorisation **avant** la première mise en location.

### À qui adresser la demande ?

📮 **Mairie de Saint-Cast Le Guildo, Secrétariat général**
Place de l'Hôtel de Ville, 22380 Saint-Cast Le Guildo
📞 **02 96 41 69 25**
✉️ **meuble-tourisme@saintcastleguildo.fr**

### Pièces à joindre obligatoirement

- ✅ **Le formulaire de demande de changement d'usage** (téléchargeable sur le site officiel : villedesaintcastleguildo.fr → Mairie → Cadre de vie → Meublés de tourisme)
- ✅ Le **numéro d'enregistrement** déjà obtenu (étape 1)
- ✅ Une copie de votre **titre de propriété** (acte de vente ou taxe foncière)
- ✅ Le **règlement de copropriété** (si en copropriété) + déclaration sur l'honneur que la location courte durée n'est pas interdite
- ✅ Le **Diagnostic de Performance Énergétique (DPE)**, exigible avec la loi LEMEUR
- ✅ **Plan du logement** avec surfaces
- ✅ **Photos intérieures** récentes
- ✅ Décision de classement si meublé classé 1-5★ (facultatif mais avantage fiscal majeur, voir partie LEMEUR)

### Délai de réponse

Compter **6 à 10 semaines** entre le dépôt et la réponse écrite. Le Conseil Municipal de Saint-Cast Le Guildo a renforcé sa procédure d'instruction lors de la modification réglementaire de **mars 2026** (délibération CA_2026_038), avec un renforcement des critères de validation (notamment vis-à-vis du PLUI Habitat de Dinan Agglomération).

### Cas particulier : régularisation jusqu'au 1ᵉʳ avril 2026

Si vous **louiez déjà un meublé de tourisme à Saint-Cast Le Guildo avant le 1ᵉʳ novembre 2025** (date d'entrée en vigueur du règlement), vous bénéficiez d'une **période de régularisation** jusqu'au **1ᵉʳ avril 2026**. Au-delà, vous serez en **infraction** et exposé aux sanctions LEMEUR.

⚠️ Si vous lisez cet article après cette date, contactez-nous **immédiatement** pour rattraper la situation.

## Cadre réglementaire local : la singularité de Saint-Cast Le Guildo

Trois délibérations du Conseil Municipal encadrent désormais le dispositif :

1. **Délibération du 10 juin 2025**, instaure la procédure de changement d'usage à compter du 1ᵉʳ novembre 2025
2. **Délibération du 7 octobre 2025**, instaure le numéro d'enregistrement obligatoire pour tous les meublés
3. **Délibération CA_2026_038 (mars 2026)**, modifie le règlement et précise les critères d'instruction (zonage PLUI Beaussais-sur-Mer / Saint-Cast Le Guildo)

Ces trois délibérations sont **téléchargeables en PDF** sur le site officiel de la mairie (rubrique "Documents utiles").

### Comparatif avec les communes voisines

| Commune | Date d'entrée en vigueur | N° enregistrement | Quotas |
|---|---|---|---|
| Saint-Cast Le Guildo | 1ᵉʳ novembre 2025 | Oui (taxe.3douest.com) | À l'étude |
| Dinan | 1ᵉʳ octobre 2023 | Oui (mairie) | 5 secteurs / 430 max |
| Saint-Malo | 1ᵉʳ janvier 2020 | Oui (taxe.3douest.com) | 4 secteurs / 1 760 max |
| Cancale | Non | Recommandé | Aucun |
| Lancieux / Plancoët | Non | Recommandé | Aucun |

C'est cet **encadrement progressif** des stations littorales costarmoricaines qui pousse les propriétaires à anticiper : **commencer maintenant** vous garantit l'antériorité avant l'introduction probable de quotas dans les prochaines années.

## La loi LEMEUR : ce qui s'applique aussi à Saint-Cast Le Guildo

La **loi n° 2024-1039 du 19 novembre 2024** durcit la régulation nationale. Voici les 4 impacts majeurs pour les propriétaires de Saint-Cast Le Guildo :

### 1. Fiscalité moins favorable (le plus douloureux)

| Type de meublé | Avant 2025 | Depuis 2025 |
|---|---|---|
| **Meublé non classé** | Abattement 50 %, plafond 77 700 € | **Abattement 30 %, plafond 15 000 €** |
| **Meublé classé 1-5★** | Abattement 71 %, plafond 188 700 € | **Abattement 50 %, plafond 77 700 €** |
| **Chambres d'hôtes** | Abattement 71 % | **Abattement 50 %, plafond 77 700 €** |

🎯 **Notre recommandation pour Saint-Cast** : **faites classer votre meublé**. Le passage de "non classé" à "classé" double votre abattement fiscal et multiplie le plafond par 5. À Saint-Cast, l'**Office de Tourisme Dinan-Cap Fréhel** peut accompagner la procédure de classement (via Atout France ou organisme accrédité).

### 2. DPE obligatoire (et exigences renforcées)

Tous les meublés de tourisme à Saint-Cast Le Guildo doivent désormais présenter un **DPE classé** :
- **Au moins F en 2025**
- **Au moins E en 2028**
- **Au moins D en 2034**

Si votre logement est classé G, vous **ne pouvez plus le louer** en meublé touristique tant que vous n'avez pas réalisé les travaux de rénovation énergétique.

### 3. Pouvoirs élargis pour le maire

- Définir des quotas d'autorisations (Saint-Cast Le Guildo n'a pas encore introduit de quotas mais cela peut évoluer)
- Limiter à **90 jours/an** la location d'une résidence principale (au lieu de 120), non encore activé à Saint-Cast
- Réserver des secteurs PLU à la résidence principale uniquement

### 4. Régulation en copropriété

**Tout nouveau règlement de copropriété** doit désormais se prononcer sur la possibilité ou non de louer des logements en meublé de tourisme. Particulièrement sensible à Saint-Cast pour les résidences en bord de mer ou les copropriétés du centre-bourg.

## Sanctions : à quoi vous vous exposez en cas de manquement

| Manquement | Sanction administrative |
|---|---|
| Pas de numéro d'enregistrement sur l'annonce | **jusqu'à 10 000 €** (loi LEMEUR) |
| Fausse déclaration / faux numéro | **jusqu'à 20 000 €** (loi LEMEUR) |
| Location sans autorisation de changement d'usage (résidence secondaire) | **jusqu'à 50 000 €** + astreinte de **1 000 €/jour/m²** |
| Suspension automatique de l'annonce sur Airbnb / Booking / Vrbo | Immédiate |

Et bien sûr les sanctions historiques du Code de la Construction et de l'Habitation restent applicables.

## Comment Terre et Mer Conciergerie vous accompagne à Saint-Cast Le Guildo

Notre équipe Côte d'Émeraude est **basée localement** et travaille tous les jours avec la mairie de Saint-Cast Le Guildo. Nous montons **l'intégralité du dossier** pour vous, sans frais cachés (inclus dans notre forfait conciergerie 15 % TTC) :

✅ **Étape 1** : audit pré-demande (DPE, copropriété, conformité PLUI Beaussais / Saint-Cast)

✅ **Étape 2** : création de votre compte sur taxe.3douest.com/dinan.php + obtention du numéro d'enregistrement (24-72h)

✅ **Étape 3** : montage du dossier de changement d'usage complet (formulaire + 7 pièces obligatoires)

✅ **Étape 4** : envoi à meuble-tourisme@saintcastleguildo.fr + suivi téléphonique avec le secrétariat général (02 96 41 69 25)

✅ **Étape 5** : récupération de l'autorisation + mise à jour automatique de toutes vos annonces Airbnb / Booking / Vrbo (intégration du numéro)

✅ **Étape 6** : conseil classement étoiles via l'Office de Tourisme Dinan-Cap Fréhel pour optimiser votre fiscalité LEMEUR (passer de 30 % à 50 % d'abattement)

✅ **Étape 7** : déclaration trimestrielle automatique de la taxe de séjour Dinan Agglomération

✅ **Bonus** : alerte 4 mois avant l'expiration pour relancer la procédure de renouvellement

**Pas de frais de dossier supplémentaire. Tout est inclus dans le forfait.**

## Liens et contacts officiels (à garder)

- 🏛️ **Mairie de Saint-Cast Le Guildo, Secrétariat général** : Place de l'Hôtel de Ville, 02 96 41 69 25, meuble-tourisme@saintcastleguildo.fr
- 📋 **Page officielle "Meublés de tourisme"** : [villedesaintcastleguildo.fr/mairie/le-cadre-de-vie-urbanisme-et-habitat/meuble-de-tourisme/](https://villedesaintcastleguildo.fr/mairie/le-cadre-de-vie-urbanisme-et-habitat/meuble-de-tourisme/)
- 🏷️ **Portail numéro d'enregistrement & taxe de séjour Dinan Agglomération** : [taxe.3douest.com/dinan.php](https://taxe.3douest.com/dinan.php)
- 📜 **Délibération du 10 juin 2025** (changement d'usage) : disponible en PDF sur le site mairie
- 📜 **Délibération du 7 octobre 2025** (n° enregistrement) : idem
- 🏠 **Office de Tourisme Dinan-Cap Fréhel** (classement meublés) : dinan-capfrehel-tourisme.com

---

[**Confier mon dossier à Terre et Mer Conciergerie →**](/contact)
[**Voir notre page Saint-Cast Le Guildo →**](/villes/saint-cast-le-guildo)
[**Découvrir tous nos services →**](/services)
[**Mobile home à Saint-Cast Le Guildo : notre page dédiée →**](/mobil-home-saint-cast)
`,
  },

  // =====================================================================
  // 0ter, Enregistrement Airbnb Dinan (NEW 2026-05-09)
  // =====================================================================
  {
    slug: "enregistrement-airbnb-dinan-meuble-tourisme-2026",
    category: "reglementation",
    title: "Enregistrement Airbnb à Dinan : le guide officiel 2026 (changement d'usage + numéro d'enregistrement)",
    excerpt:
      "Tout ce qu'il faut faire pour louer légalement votre Airbnb à Dinan : autorisation mairie, quotas par secteur (Intramuros / Petit Fort / Bourg Léhon / Port / Périphérie), DPE, sanctions loi LEMEUR. Le guide étape par étape, à jour de la réglementation 2025.",
    image: "/images/blog/enregistrement-airbnb-dinan-meuble-tourisme-2026.jpg",
    imageAlt: "Tableau de clés étiquetées Logement 1, 2, 3, garage avec porte-clés Terre et Mer Conciergerie pour location Airbnb à Dinan",
    author: "L'équipe Terre et Mer",
    date: "2025-11-20",
    readTime: 9,
    keywords:
      "enregistrement airbnb dinan, comment declarer airbnb dinan, numero enregistrement meuble tourisme dinan, changement usage dinan, autorisation airbnb dinan, formulaire airbnb dinan, taxe sejour dinan agglomeration, reglementation airbnb dinan 2026, mairie dinan meuble tourisme, declaration airbnb intra-muros dinan, loi lemeur dinan",
    relatedCities: ["dinan"],
    content: `
## Pourquoi cet article : Dinan, encadrée depuis le 1ᵉʳ octobre 2023

La cité médiévale de Dinan est désormais l'une des villes les plus régulées des Côtes-d'Armor. Depuis le **1ᵉʳ octobre 2023**, mettre en location un meublé de tourisme à Dinan **nécessite une autorisation préalable de la mairie**. Et depuis le **1ᵉʳ janvier 2025**, des **quotas par secteur** (Intramuros, Petit Fort, Bourg Léhon, Port, Périphérie) limitent encore plus les places disponibles.

À cela s'ajoute la **loi LEMEUR du 19 novembre 2024** : DPE obligatoire, fiscalité durcie, amendes jusqu'à **20 000 €**.

Voici, étape par étape, **tout ce que vous devez faire** pour louer légalement à Dinan en 2026, sans risquer la suspension de votre annonce ni une amende administrative.

> 💡 **Vous voulez gagner du temps ?** Notre équipe Terre et Mer Conciergerie monte le dossier complet (changement d'usage + numéro d'enregistrement + suivi mairie) et l'inclut dans le forfait conciergerie. [Demander un accompagnement →](/contact)

## Étape 0 : votre cas en 30 secondes

| Votre cas | Autorisation de changement d'usage | Numéro d'enregistrement |
|---|---|---|
| **Résidence principale** louée < 90 jours/an (loi LEMEUR) | ❌ Non requise | ✅ **Obligatoire** |
| **Résidence principale** louée > 90 jours/an | ✅ Requise | ✅ Obligatoire |
| **Résidence secondaire** ou **investissement locatif** | ✅ **Requise dès la 1ʳᵉ nuit** | ✅ Obligatoire |
| Logement classé G au DPE | ❌ **Refus automatique** |, |
| Bien en travaux ou pas encore acquis | ❌ Pas de demande possible |, |

⚠️ **Nouveauté loi LEMEUR (1ᵉʳ janvier 2025)** : la durée maximum de location d'une résidence principale **peut être limitée à 90 jours/an** (au lieu de 120) si la commune le décide. Vérifiez toujours auprès de la mairie de Dinan.

## Étape 1, Préparer le dossier d'autorisation de changement d'usage

À Dinan, **on commence obligatoirement par cette étape** (contrairement à Saint-Malo). Le numéro d'enregistrement n'est délivré **qu'après réception de l'autorisation de la mairie**.

### Pièces obligatoires à fournir

1. ✅ **Le formulaire d'autorisation** (téléchargeable dans la rubrique "documents utiles" sur dinan.fr)
2. ✅ **Le titre de propriété** : taxe foncière OU acte de vente
3. ✅ **Le règlement de copropriété** (si en copro), avec attestation que la location courte durée n'est pas interdite
4. ✅ **Le Diagnostic de Performance Énergétique (DPE)**, **obligatoire depuis 2025** (sans ce document, refus systématique)
5. ✅ Décision de classement (si meublé classé 1 à 5 étoiles, facultatif mais avantage fiscal majeur, voir partie LEMEUR)

### À envoyer à la mairie

📮 **Centre Technique Municipal**
46 rue Bertrand Robidou, 22100 Dinan
📞 **02 96 87 11 50**
✉️ **meuble-tourisme@dinan.fr**

Vous pouvez envoyer le dossier **par courrier** ou **par mail** (PDF scannés).

### Délai de réponse

Compter **8 à 12 semaines** entre le dépôt et la réponse écrite. Si votre dossier est incomplet, vous recevrez une demande de pièces complémentaires (cela peut rallonger de 4 à 6 semaines).

## Étape 2, Recevoir le numéro d'enregistrement

**Dès réception de votre autorisation de changement d'usage**, la mairie vous envoie automatiquement par mail un **lien personnalisé** pour obtenir votre numéro d'enregistrement à 13 chiffres.

Ce numéro doit **obligatoirement figurer sur toutes vos annonces** Airbnb / Booking / Vrbo / Tripadvisor. Sans lui, **les plateformes suspendent automatiquement votre annonce** depuis 2025 (contrôle automatisé via API mairie ↔ plateformes).

## Les 5 secteurs de Dinan et leurs quotas (le facteur qui peut tout bloquer)

Depuis le **1ᵉʳ janvier 2025**, la commune de Dinan est découpée en **5 secteurs** soumis à des plafonds stricts :

| Secteur | Zone | Quota max |
|---|---|---|
| **Intramuros** | Cité médiévale, rue du Jerzual, place des Merciers | **220** autorisations max |
| **Petit Fort** | Rue du Petit Fort, descente vers le port | **20** autorisations max |
| **Bourg Léhon** | Bourg de Léhon, abbaye Saint-Magloire | **20** autorisations max |
| **Port** | Port de Dinan, quais de la Rance | **20** autorisations max |
| **Périphérie** | Reste du territoire communal | **150** autorisations max |

**Conséquence concrète** : si le quota du secteur où se trouve votre bien est atteint, vous êtes placé sur **liste d'attente**. Aucune autorisation supplémentaire n'est délivrée tant qu'une autorisation existante n'est pas annulée (cessation d'activité du propriétaire).

> 💡 **Notre conseil avant achat** : vérifiez **où en est le quota** du secteur du bien que vous visez. Sur Petit Fort, Bourg Léhon et Port (plafonds très bas à 20), c'est régulièrement saturé.

## Cas particuliers à connaître

### Vous changez de propriétaire (vente du bien)

⚠️ **Attention** : l'autorisation est **personnelle et incessible**. Lors d'une vente, l'autorisation de changement d'usage **devient automatiquement caduque**. Le nouveau propriétaire doit **redéposer une demande from scratch**, sans garantie d'obtention si le quota est atteint.

C'est un point crucial à anticiper si vous vendez ou achetez un bien déjà loué en Airbnb.

### Vous cessez votre activité

Si vous arrêtez de louer en courte durée (vente, retour en résidence permanente, location longue durée), informez les services techniques au **02 96 87 11 50** ou par mail à **meuble-tourisme@dinan.fr**. Cela libère une place dans le quota du secteur, utile aux autres propriétaires.

### Votre logement n'est pas encore louable

**Pas de demande anticipée possible.** Si votre bien est en travaux, en cours d'acquisition ou si le DPE est G, la mairie **refuse** la demande. Il faut attendre que le bien soit prêt.

## La loi LEMEUR : ce qui change depuis le 1ᵉʳ janvier 2025

La **loi n° 2024-1039 du 19 novembre 2024** (dite "loi LEMEUR") durcit la régulation nationale. Voici les 4 impacts majeurs pour les propriétaires de Dinan :

### 1. Fiscalité moins favorable (le plus douloureux)

| Type de meublé | Avant 2025 | Depuis 2025 |
|---|---|---|
| **Meublé non classé** | Abattement 50 %, plafond 77 700 € | **Abattement 30 %, plafond 15 000 €** |
| **Meublé classé 1-5★** | Abattement 71 %, plafond 188 700 € | **Abattement 50 %, plafond 77 700 €** |
| **Chambres d'hôtes** | Abattement 71 % | **Abattement 50 %, plafond 77 700 €** |

🎯 **Notre recommandation** : faites **classer votre meublé** (1 à 5 étoiles, classement gratuit ou facturé selon l'organisme). Le passage de "non classé" à "classé" double votre abattement fiscal et multiplie le plafond par 5.

### 2. DPE obligatoire (et exigences renforcées)

Tous les meublés de tourisme à Dinan doivent désormais présenter un **DPE classé** :
- **Au moins F en 2025**
- **Au moins E en 2028**
- **Au moins D en 2034**

Si votre logement est classé G, vous **ne pouvez plus le louer** en meublé touristique tant que vous n'avez pas réalisé les travaux de rénovation énergétique.

### 3. Pouvoirs élargis pour le maire

- Définir des quotas d'autorisations (Dinan l'a déjà fait, voir tableau plus haut)
- Limiter à **90 jours/an** la location d'une résidence principale (au lieu de 120)
- Réserver des secteurs PLU à la résidence principale uniquement

### 4. Régulation en copropriété

**Tout nouveau règlement de copropriété** doit désormais se prononcer sur la possibilité ou non de louer des logements en meublé de tourisme.

La modification d'un règlement existant pour **interdire** la location courte durée se vote désormais à la **majorité des deux tiers** (au lieu de l'unanimité).

⚠️ Tout propriétaire autorisé doit **informer son syndic** du changement d'usage. Le syndic ajoute un point d'information à l'ordre du jour de la prochaine AG.

## Sanctions : à quoi vous vous exposez en cas de manquement

| Manquement | Sanction administrative |
|---|---|
| Pas de numéro d'enregistrement sur l'annonce | **jusqu'à 10 000 €** (loi LEMEUR) |
| Fausse déclaration / faux numéro | **jusqu'à 20 000 €** (loi LEMEUR) |
| Suspension automatique de l'annonce sur Airbnb / Booking / Vrbo | Immédiate |

Et bien sûr les sanctions historiques du Code de la Construction et de l'Habitation (jusqu'à **50 000 € + 1 000 €/jour/m²** d'astreinte) restent applicables.

## Comment Terre et Mer Conciergerie vous accompagne à Dinan

Nous montons **l'intégralité du dossier** pour vous, sans frais cachés (inclus dans notre forfait conciergerie 15 % TTC) :

✅ **Étape 1** : audit pré-demande (DPE, copropriété, quota du secteur en temps réel)

✅ **Étape 2** : montage du dossier complet (formulaire + 4 pièces obligatoires)

✅ **Étape 3** : envoi par mail au service mairie (meuble-tourisme@dinan.fr) + suivi téléphonique avec le service technique

✅ **Étape 4** : récupération de l'autorisation + génération du numéro d'enregistrement

✅ **Étape 5** : mise à jour de toutes vos annonces Airbnb / Booking / Vrbo (intégration du numéro)

✅ **Étape 6** : conseil classement étoiles (1 à 5★) pour optimiser votre fiscalité LEMEUR (passer de 30 % à 50 % d'abattement)

✅ **Bonus** : alerte 4 mois avant l'expiration pour relancer la procédure de renouvellement

**Pas de frais de dossier supplémentaire. Tout est inclus dans le forfait.**

## Liens et contacts officiels (à garder)

- 🏛️ **Mairie de Dinan, Services techniques (Meublés de tourisme)** : 46 rue Bertrand Robidou, 22100 Dinan, 02 96 87 11 50, meuble-tourisme@dinan.fr
- 📋 **Page officielle "Déclaration de meublés de tourisme"** : [dinan.fr/vivre-a-dinan/economie-tourisme-et-emploi/declaration-de-meubles-de-tourisme/](https://www.dinan.fr/vivre-a-dinan/economie-tourisme-et-emploi/declaration-de-meubles-de-tourisme/)
- 📜 **Loi LEMEUR du 19 novembre 2024** : disponible sur Légifrance (loi n° 2024-1039)
- 🏷️ **Classement en étoiles** : possible via Atout France ou organisme accrédité

---

[**Confier mon dossier à Terre et Mer Conciergerie →**](/contact)
[**Voir notre page Dinan →**](/villes/dinan)
[**Découvrir tous nos services →**](/services)
`,
  },

  // =====================================================================
  // 0bis, Enregistrement Airbnb Saint-Malo (NEW 2026-05-09)
  // =====================================================================
  {
    slug: "enregistrement-airbnb-saint-malo-meuble-tourisme-2026",
    category: "reglementation",
    title: "Enregistrement Airbnb à Saint-Malo : le guide officiel 2026 (changement d'usage + numéro d'enregistrement)",
    excerpt:
      "Tout ce qu'il faut faire pour louer légalement votre Airbnb à Saint-Malo : déclaration mairie, autorisation de changement d'usage, numéro d'enregistrement, quotas par secteur, sanctions. Le guide étape par étape, à jour du règlement municipal du 24 avril 2025.",
    image: "/images/blog/enregistrement-airbnb-saint-malo-meuble-tourisme-2026.jpg",
    imageAlt: "Porte-clés Terre et Mer Conciergerie sur la porte d'entrée d'un Airbnb déclaré et enregistré à Saint-Malo",
    author: "L'équipe Terre et Mer",
    date: "2025-10-15",
    readTime: 9,
    keywords:
      "enregistrement airbnb saint-malo, comment declarer airbnb saint-malo, numero enregistrement meuble tourisme saint-malo, changement usage saint-malo, formulaire airbnb saint-malo, autorisation location meublee saint-malo, taxe sejour saint-malo agglomeration, reglementation airbnb saint-malo 2026, mairie saint-malo meuble tourisme, declaration airbnb saint-servan paramé",
    relatedCities: ["saint-malo"],
    content: `
## Pourquoi cet article : Saint-Malo est la ville la plus encadrée de Bretagne

Depuis le **règlement municipal du 24 avril 2025** (modifiant le dispositif initial du 1ᵉʳ janvier 2020), la Ville de Saint-Malo applique l'un des cadres les plus stricts de France pour les locations Airbnb. Quotas par secteur, autorisation temporaire de 3 ans, **une seule autorisation par propriétaire**, sanctions jusqu'à **100 000 €** : ce n'est plus du tout le marché libre des années 2018.

Voici, étape par étape, ce que vous **devez** faire pour louer légalement à Saint-Malo en 2026, sans risquer une amende, sans rater votre démarche, et sans perdre 6 mois entre la mairie et l'agglo.

> 💡 **Vous voulez gagner du temps ?** Notre équipe Terre et Mer Conciergerie monte le dossier complet pour vous (changement d'usage + déclaration agglo + suivi mairie). [Demander un accompagnement →](/contact)

## Étape 0 : votre cas en 30 secondes

Votre situation détermine **vos obligations** :

| Votre cas | Numéro d'enregistrement | Autorisation de changement d'usage |
|---|---|---|
| **Résidence principale** louée < 120 nuits/an | ✅ **Obligatoire** | ❌ Non requise |
| **Résidence principale** louée > 120 nuits/an | ✅ Obligatoire | ✅ **Requise** |
| **Résidence secondaire** ou **investissement locatif** | ✅ Obligatoire | ✅ **Requise dès la 1ʳᵉ nuit** |
| **Chambre d'hôtes** chez l'habitant | ✅ Obligatoire | ❌ Non requise |
| **SCI / SARL / SAS** propriétaire | ✅ Obligatoire | ✅ Requise (mais limitations) |

⚠️ **Définition légale "résidence principale"** : logement occupé au moins **8 mois par an**, sauf raison professionnelle, médicale ou cas de force majeure. Sinon, c'est une résidence secondaire.

## Étape 1, Obtenir votre numéro d'enregistrement (toujours en 1ᵉʳ)

**Tous les propriétaires** (résidence principale OU secondaire) doivent commencer par cette étape. Ce numéro à 13 chiffres doit figurer sur **toutes vos annonces** Airbnb / Booking / Vrbo. Sans lui : **5 000 € d'amende**.

### Comment l'obtenir (gratuit, en ligne, sous 48h-72h)

1. Rendez-vous sur le portail officiel : **https://taxe.3douest.com/saintmaloagglo.php**
2. Créez un compte propriétaire avec votre adresse mail
3. Renseignez :
   - L'adresse complète du bien
   - La typologie (T1, T2, maison…)
   - Le nombre de personnes maximum
   - Votre statut (résidence principale / secondaire)
4. Validez : le **numéro d'enregistrement à 13 chiffres** vous est envoyé par mail, généralement sous 24-72h

### Important : vous devrez aussi déclarer la taxe de séjour

À Saint-Malo Agglomération, la taxe de séjour est de **0,99 € à 5 €/nuit/personne** selon le classement du meublé. Le portail **taxe.3douest.com** sert à la fois pour le numéro d'enregistrement **et** pour la déclaration trimestrielle de la taxe.

> 🛡️ Notre équipe gère **automatiquement** la déclaration de votre taxe de séjour chaque trimestre (collecte par la plateforme + reversement mairie + reporting). Aucune action de votre part.

## Étape 2, Demander l'autorisation de changement d'usage (résidences secondaires uniquement)

C'est l'étape **la plus complexe**, et celle où la majorité des propriétaires se font refuser. Voici comment maximiser vos chances.

### À qui adresser la demande ?

**Mairie de Saint-Malo, Direction de l'Aménagement et de l'Urbanisme (DAU)**
27 quai Duguay-Trouin, 35400 Saint-Malo
📞 **02 21 51 10 32**
✉️ **changementusage@saint-malo.fr**
🕓 Du lundi au vendredi : 8h30-12h15 / 13h30-17h30

Ou par voie postale :
**Mairie de Saint-Malo, Place Chateaubriand, CS 21826, 35418 Saint-Malo Cedex**

### Le formulaire officiel à télécharger

Le formulaire est disponible sur le site officiel de la mairie : [www.saint-malo.fr/accueil/vivre/meubles-de-tourisme/](https://www.saint-malo.fr/accueil/vivre/meubles-de-tourisme/) (cherchez "Formulaire autorisation changement usage").

### Pièces à joindre obligatoirement

- ✅ Le **numéro d'enregistrement** déjà obtenu (étape 1)
- ✅ Une copie de votre **titre de propriété**
- ✅ Le **règlement de copropriété** complet (s'il y a lieu) + **déclaration sur l'honneur** que la copropriété autorise la location courte durée
- ✅ Plan du logement avec surfaces
- ✅ Photos intérieures
- ✅ Une attestation que le logement est **décent** (pas de sous-sol, pas de mansarde non aménagée, surface minimum de 9 m² avec hauteur sous plafond ≥ 2,20 m)

### Délai de réponse mairie

Compter **6 à 12 semaines** entre le dépôt et la réponse écrite. Aucun renvoi automatique : si l'autorisation est refusée, c'est un courrier motivé qui arrive (refus pour quota atteint, copropriété, vétusté).

### Validité : 3 ans, renouvelable

L'autorisation est délivrée pour **3 ans, personnelle et incessible**. Pour la renouveler, vous devez redéposer une demande **entre 4 mois et 2 mois avant** la date d'expiration. Au-delà, vous repassez au tour de file et perdez votre antériorité.

### ⚠️ Une seule autorisation par propriétaire

C'est la règle qui change tout : depuis le règlement de 2025, **un même propriétaire (personne physique OU morale) ne peut détenir qu'une seule autorisation** dans toute la commune. Si vous avez deux Airbnb à Saint-Malo, **un seul** sera officiellement autorisé.

## Les 4 secteurs et leurs quotas (le facteur qui peut tout bloquer)

Saint-Malo est découpée en **4 secteurs Insee**, chacun avec un **plafond** de logements pouvant être autorisés simultanément en meublé touristique :

| Secteur | Zone géographique principale | Quota | Autorisations max |
|---|---|---|---|
| **A** | Hyper-centre, Intra-Muros, Sillon | **12,5 %** des logements | **370** |
| **B** | Saint-Servan + Le Sillon (2 sous-secteurs) | **7,5 %** des logements | **920** |
| **C** | Paramé, Rocabey, Rothéneuf | **3 %** des logements | **360** |
| **D** | Quartiers périphériques (Marville, Mivoie…) | **1 %** des logements | **110** |

**Conséquence concrète** : si le quota du secteur où se trouve votre bien est **atteint**, votre demande est **automatiquement refusée**. Sur Intra-Muros (secteur A), le quota est régulièrement saturé, il faut attendre qu'une autorisation existante ne soit pas renouvelée pour libérer une place.

> 💡 **Notre conseil** : avant d'investir, demandez-nous une **étude pré-investissement** secteur par secteur. Nous savons en temps réel où il reste des places libres.

## Cas particuliers à connaître

### Vous êtes en copropriété

Vous devez attester sur l'honneur que **le règlement de copropriété autorise** la location meublée touristique. Si la mairie est saisie d'une preuve contraire (article du règlement interdisant la sous-location, courrier du syndic), l'autorisation est refusée. Si vous mentez, et qu'un jugement le démontre plus tard, **l'autorisation est retirée sans préavis**.

### Vous louez à des étudiants 9 mois + tourisme l'été

Le bail étudiant (9 mois minimum) **dispense** du changement d'usage. Mais **dès lors que vous louez en courte durée l'été** (juillet-août typiquement), vous tombez dans le régime des meublés touristiques : **l'autorisation devient obligatoire** pour cette période.

### Vous êtes une SCI / SARL / SAS

Les personnes morales peuvent demander une autorisation, **mais une seule**, peu importe le nombre de biens détenus. C'est une limite importante pour les holdings d'investissement immobilier.

## Sanctions : à quoi vous vous exposez en cas de manquement

Le règlement municipal et le Code de la Construction et de l'Habitation (CCH) prévoient :

| Manquement | Sanction maximale |
|---|---|
| Pas de numéro d'enregistrement sur l'annonce | **5 000 €** |
| Fausse déclaration | **80 000 €** |
| Location sans autorisation de changement d'usage | **100 000 €** + astreinte de **1 000 €/jour/m²** |
| Plus de 120 nuits sur résidence principale sans autorisation | jusqu'à **100 000 €** |

⚠️ La Ville de Saint-Malo a recruté des **agents assermentés** pour scanner les annonces Airbnb et croiser avec sa base d'autorisations. Les contrôles sont réels et fréquents depuis 2024.

## Comment Terre et Mer Conciergerie vous accompagne

Nous montons **l'intégralité du dossier** pour vous, sans frais cachés (inclus dans notre forfait conciergerie 15 % TTC) :

✅ **Étape 1** : création de votre compte sur le portail Saint-Malo Agglomération + obtention du numéro d'enregistrement (24-72h)

✅ **Étape 2** : vérification du quota disponible dans votre secteur **avant** la demande (gain de temps si saturé)

✅ **Étape 3** : montage du dossier de changement d'usage (formulaire + plans + attestations + pièces)

✅ **Étape 4** : dépôt physique à la mairie ou envoi recommandé + suivi auprès de la DAU

✅ **Étape 5** : mise en conformité de toutes vos annonces Airbnb / Booking / Vrbo avec le numéro d'enregistrement

✅ **Étape 6** : déclaration trimestrielle automatique de la taxe de séjour

✅ **Bonus** : alerte automatique 4 mois avant l'expiration des 3 ans pour relancer la procédure de renouvellement.

**Pas de frais de dossier supplémentaire. Inclus dans le forfait conciergerie.**

## Liens et contacts officiels (à garder)

- 🏛️ **Mairie de Saint-Malo, Service changement d'usage** : 27 quai Duguay-Trouin, 02 21 51 10 32, changementusage@saint-malo.fr
- 📋 **Page officielle "Meublés de tourisme"** : [saint-malo.fr/accueil/vivre/meubles-de-tourisme/](https://www.saint-malo.fr/accueil/vivre/meubles-de-tourisme/)
- 🏷️ **Portail numéro d'enregistrement & taxe de séjour** : [taxe.3douest.com/saintmaloagglo.php](https://taxe.3douest.com/saintmaloagglo.php)
- 📜 **Règlement municipal en vigueur (24 avril 2025)** : disponible en PDF sur le site de la Mairie

---

[**Confier mon dossier à Terre et Mer Conciergerie →**](/contact)
[**Voir notre page Saint-Malo →**](/villes/saint-malo)
[**Découvrir tous nos services →**](/services)
`,
  },

  // =====================================================================
  // 0, Airbnb avec piscine / jacuzzi en Bretagne (NEW 2026-05-09)
  // =====================================================================
  {
    slug: "airbnb-piscine-jacuzzi-bretagne-revenus-2026",
    category: "rentabilite",
    title: "Airbnb avec piscine ou jacuzzi en Bretagne : l'arme anti-saisonnalité qui booste vos revenus de +35 %",
    excerpt:
      "Pourquoi un bien avec piscine, jacuzzi ou spa explose les revenus en Bretagne, y compris hors saison. Chiffres, ROI, conseils d'aménagement.",
    image: "/images/blog/airbnb-piscine-jacuzzi-bretagne-revenus-2026.jpg",
    imageAlt: "Piscine extérieure avec transats, parasol et jacuzzi dans une location Airbnb premium en Bretagne",
    author: "L'équipe Terre et Mer",
    date: "2026-03-08",
    readTime: 7,
    keywords:
      "airbnb piscine bretagne, location airbnb avec jacuzzi saint-malo, spa airbnb dinard, piscine chauffée location courte durée, investir airbnb piscine côte émeraude, airbnb premium bretagne",
    relatedCities: ["saint-malo", "dinard", "cancale"],
    content: `
## La Bretagne + une piscine : la combinaison qui change tout

La météo bretonne fait peur aux investisseurs. C'est une **erreur**. Avec une piscine chauffée ou même un simple jacuzzi extérieur, votre bien capte une demande qui n'existe pas sans cet équipement : **les familles qui hésitent à venir en avril, mai, septembre et octobre**.

Sur notre portefeuille de 70 logements pas plus, nous suivons précisément l'écart de performance entre les biens "classiques" et ceux équipés d'un point d'eau. Voici les chiffres réels, mesurés sur 24 mois.

## Les chiffres qui prouvent le ROI (Côte d'Émeraude, 2025)

| Indicateur | Bien classique | Bien avec jacuzzi | Bien avec piscine chauffée |
|---|---|---|---|
| Tarif moyen / nuit (haute saison) | 145 € | 185 € | 245 € |
| Tarif moyen / nuit (mai + septembre) | 95 € | 140 € | 195 € |
| Taux d'occupation annuel | 62 % | 73 % | 81 % |
| Revenu brut annuel | 32 800 € | 49 200 € | 72 100 € |
| **Différentiel vs classique** |, | **+50 %** | **+120 %** |

Sur la moyenne du portefeuille (haute + basse saison confondues), un bien équipé génère **+35 % de revenus minimum**. Et ce delta n'est pas linéaire : il **explose** en avril-mai et septembre-octobre, là où les biens classiques galèrent à se louer.

## Pourquoi ça fonctionne aussi bien en Bretagne

### 1. La pluie n'est pas un problème, c'est un argument

Beaucoup de propriétaires pensent : "Personne ne se baignera quand il pleut." **Faux.** Un jacuzzi extérieur sous pergola ou une piscine chauffée à 28 °C, c'est précisément ce que les voyageurs cherchent **quand le ciel est gris**. La douche chaude, le verre de chouchen, les enfants qui jouent dans l'eau pendant que les parents lisent : c'est l'ADN de la "vacation home" bretonne premium.

### 2. La saisonnalité de la Bretagne est mauvaise, sauf pour vous

C'est mathématique. Quand la concurrence "classique" baisse ses tarifs de 40 % en mai et septembre pour remplir, vous, vous **maintenez vos prix** et vous remplissez quand même. Le delta de revenus se fait là, hors saison.

### 3. Les voyageurs internationaux le cherchent activement

Sur Airbnb et Booking, le filtre "piscine" ou "spa" est dans le top 5 des filtres les plus utilisés par les **voyageurs anglais, allemands et néerlandais**, qui représentent 35 % des arrivées sur la Côte d'Émeraude. Sans piscine, vous êtes **invisible** pour cette cible premium.

## Combien ça coûte d'équiper un bien existant ?

| Équipement | Investissement | Hausse de revenus annuels | Retour sur invest. |
|---|---|---|---|
| Jacuzzi 4-6 places (Intex/Bestway) | 800, 1 500 € | +4 000 à +6 000 € | **3-4 mois** |
| Jacuzzi acrylique semi-encastré | 5 000, 9 000 € | +8 000 à +12 000 € | **9-12 mois** |
| Piscine hors-sol bois (4×8 m) | 8 000, 14 000 € | +14 000 à +18 000 € | **10-14 mois** |
| Piscine enterrée chauffée (8×4 m) | 35 000, 55 000 € | +30 000 à +40 000 € | **18-24 mois** |

⚠️ **Important** : les chiffres ROI ci-dessus prennent en compte les coûts d'exploitation supplémentaires (chauffage, traitement de l'eau, entretien, assurance) que nous prenons en charge dans notre forfait full-service.

## Les 4 erreurs qui plombent un Airbnb avec piscine

### 1. Une piscine non chauffée

En Bretagne, l'eau froide tue 80 % de l'argument piscine. **Investissez impérativement dans une pompe à chaleur** (3 000 à 5 000 € installée). Sans ça, votre piscine n'est utilisable que de juin à août, et votre delta de revenus s'effondre.

### 2. Un jacuzzi pas couvert

Pluie + feuilles mortes + algues = jacuzzi inutilisable en 48h. **Une cabine de bois, une pergola ou même une simple toile tendue** suffit à protéger l'eau et à en faire un vrai argument 365 jours/an.

### 3. Un entretien sous-traité au "voisin du coin"

C'est la cause #1 d'avis 1★. **Confiez l'entretien à un piscinier professionnel** (analyse hebdomadaire, traitement, détartrage, hivernage). Notre équipe travaille avec **2 partenaires pisciniers fixes** sur Saint-Malo et Dinard, intégrés à notre forfait.

### 4. Pas de règles claires dans le livret d'accueil

Sans règles écrites (heures d'utilisation, douche obligatoire avant le bain, interdit aux non-nageurs sans surveillance, charge utile du jacuzzi), vous prenez le risque d'incident grave. **Notre livret d'accueil intègre systématiquement** une page dédiée à la piscine/au jacuzzi.

## Notre accompagnement piscine & spa

Pour les biens équipés (existants ou en projet), nous proposons :

- **Audit pricing piscine** : combien charger en plus, quelle saisonnalité ?
- **Mise en place du chauffage** via partenaires installateurs locaux
- **Entretien hebdomadaire intégré** dans le forfait conciergerie
- **Photos professionnelles** mettant en scène la piscine de jour et de nuit
- **Pricing dynamique renforcé** : on facture +30 % la nuit en mai/octobre vs un bien classique
- **Livret d'accueil dédié** avec règles + recommandations spa locales

## Faut-il acheter un bien avec piscine ou en équiper un ?

**À l'achat** : le surcoût d'un bien avec piscine est de l'ordre de **+15 à +25 %** en Côte d'Émeraude. Le calcul est rentable si vous comptez le louer en courte durée (ROI de 3 à 5 ans sur le surcoût).

**Sur un bien existant** : la rentabilité est encore meilleure. Un investissement de 8 000 € en jacuzzi semi-encastré se rentabilise en moins d'un an et augmente la valeur de revente de votre bien de 12 000 à 20 000 €.

[**Étude personnalisée pour votre projet piscine →**](/contact)
[**Découvrir nos services premium →**](/services)
[**Voir nos villes d'intervention →**](/villes)
`,
  },

  // =====================================================================
  // 1, Combien rapporte un Airbnb à Saint-Malo ?
  // =====================================================================
  {
    slug: "combien-rapporte-airbnb-saint-malo-2026",
    category: "rentabilite",
    title: "Combien rapporte un Airbnb à Saint-Malo en 2026 ?",
    excerpt:
      "Tarifs moyens, taux d'occupation, revenus annuels nets : on vous donne les chiffres réels du marché malouin pour 2026, par typologie de bien.",
    image: "/images/saint-malo-remparts.jpg",
    imageAlt: "Remparts de Saint-Malo intra-muros au coucher du soleil",
    author: "L'équipe Terre et Mer",
    date: "2026-04-22",
    readTime: 7,
    keywords:
      "combien rapporte airbnb saint-malo, revenus airbnb saint-malo 2026, location courte durée saint-malo rentabilité, prix nuit airbnb saint-malo, airbnb saint-malo rendement",
    relatedCities: ["saint-malo"],
    content: `
## Saint-Malo, l'un des marchés Airbnb les plus rentables de Bretagne

Avec **plus de 210 000 voyageurs Airbnb par an**, Saint-Malo se positionne dans le top 5 des destinations Airbnb françaises hors Paris/Côte d'Azur. La cité corsaire bénéficie d'un atout majeur : une **demande quasi annuelle** soutenue par les grandes marées d'équinoxe (mars, septembre), la Route du Rhum (tous les 4 ans), le festival Étonnants Voyageurs et un flux constant d'Anglais via le ferry de Portsmouth.

### Les chiffres réels du marché malouin (2026)

| Type de bien | Prix moyen / nuit | Taux d'occupation | Revenu annuel brut |
|---|---|---|---|
| Studio (1 pers.) | 75 € | 65 % | ~17 800 € |
| T2 / appartement 4 pers. | 125 € | 72 % | ~32 850 € |
| Maison 6 pers. (Paramé / Saint-Servan) | 185 € | 75 % | ~50 600 € |
| Appartement intra-muros vue mer | 220 € | 78 % | ~62 600 € |

Ces chiffres sont des **moyennes constatées sur notre portefeuille** de logements pilotés par Terre et Mer Conciergerie. Un bien sans pricing dynamique perd typiquement 25 à 30 % de revenus.

## Quels facteurs influent sur la rentabilité ?

1. **L'emplacement** : intra-muros > Plage du Sillon > Paramé > Saint-Servan > arrière-pays. La différence peut atteindre +60 % de prix moyen.
2. **La qualité des photos** : un shooting professionnel augmente le taux de clic Airbnb de 40 % en moyenne.
3. **Le pricing dynamique** : ajuster les prix au quotidien selon les marées, les événements et la demande.
4. **L'AirCover et les avis** : les Superhosts louent 22 % de plus en moyenne.

## Combien net après commission de conciergerie ?

Sur un bien à 50 600 € brut annuel, voici le détail typique :

- Brut Airbnb : **50 600 €**
- Commission Airbnb (3 %) : -1 518 €
- Commission conciergerie (15 % TTC chez Terre et Mer) : -7 590 €
- Charges (assurance, taxe séjour, énergie) : ~3 800 €
- **Revenu net propriétaire : ~37 700 € / an**

Soit un rendement net de **3 100 €/mois** pour une maison qui ne vous demande aucun temps.

## Faire mieux que la moyenne : nos leviers

Notre équipe locale Terre et Mer Conciergerie applique 4 leviers concrets :

- **Multidiffusion** : Airbnb + Booking.com + Vrbo + Tripadvisor (taux d'occupation +18 %)
- **Pricing dynamique** calé sur les grandes marées et événements (revenu/nuit +12 à 22 %)
- **Refonte annonce + shooting pro** (taux de clic ×2)
- **Réponse voyageurs en moins de 5 minutes 24h/24, 7j/7** (Superhost garanti = +22 % de prix)

[**Demandez une étude gratuite et personnalisée pour votre bien à Saint-Malo →**](/contact)

[Voir notre offre détaillée Saint-Malo](/villes/saint-malo)
`,
  },

  // =====================================================================
  // 2, Réglementation Saint-Malo / Dinard / Cancale
  // =====================================================================
  {
    slug: "reglementation-airbnb-saint-malo-dinard-cancale-2026",
    category: "reglementation",
    title: "Réglementation Airbnb à Saint-Malo, Dinard, Cancale : ce qui change en 2026",
    excerpt:
      "Déclaration en mairie, limite des 120 jours, taxe de séjour, n° d'enregistrement : le guide complet pour louer en règle sur la Côte d'Émeraude.",
    image: "/images/blog/reglementation-airbnb-saint-malo-dinard-cancale-2026.jpg",
    imageAlt: "Réglementation Airbnb à Saint-Malo, Dinard, Cancale en 2026 : checklist autorisation changement d'usage, numéro d'enregistrement, durée maximale, loi LE MEUR, visuel Terre et Mer Conciergerie",
    author: "L'équipe Terre et Mer",
    date: "2026-04-15",
    readTime: 9,
    keywords:
      "réglementation airbnb saint-malo, déclaration mairie airbnb dinard, location courte durée cancale 120 jours, taxe séjour airbnb bretagne, numéro enregistrement airbnb",
    relatedCities: ["saint-malo", "dinard", "cancale"],
    content: `
## Le cadre légal de la location courte durée en France

Depuis la **loi Le Meur de novembre 2024** et son entrée en application progressive en 2025-2026, la location de meublés de tourisme est plus encadrée. Trois règles s'appliquent désormais à toute location de type Airbnb sur la Côte d'Émeraude.

### 1. Déclaration obligatoire en mairie

Toute location meublée touristique doit être **déclarée à la mairie de la commune** où se trouve le bien, qui délivre un **numéro d'enregistrement** à 13 chiffres. Ce numéro doit obligatoirement figurer sur l'annonce Airbnb, Booking, Vrbo, etc., peine d'amende jusqu'à 5 000 €.

- **Saint-Malo** : déclaration en ligne sur le portail de la ville
- **Dinard** : guichet unique en mairie + déclaration via leur portail dédié
- **Cancale** : formulaire papier + dépôt en mairie ou en ligne

### 2. La règle des 120 jours (résidence principale uniquement)

Si le logement est votre **résidence principale**, vous ne pouvez pas le louer plus de **120 nuits par an**. Cette règle ne s'applique **PAS aux résidences secondaires**, qui peuvent être louées 365 jours par an (sauf autorisation préalable de changement d'usage dans certaines villes).

À Saint-Malo, **aucune autorisation de changement d'usage** n'est encore exigée pour les résidences secondaires (en 2026). À Dinard, la règle est identique. Mais cela peut évoluer, restez informé.

### 3. Taxe de séjour

La taxe de séjour est collectée pour vous par Airbnb (et Booking pour la plupart des communes) et reversée directement à la commune. Tarif typique 2026 :

- Saint-Malo : **0,99 € à 5 € par nuit/personne** selon le classement
- Dinard : 1,10 € à 5,30 €
- Cancale : 0,99 € à 4,90 €

Si vous classez votre meublé en étoiles (1 à 5★), la taxe est calculée sur le nombre d'étoiles. Sinon : 5 % du prix de la nuit (plafond 5 €).

## Le classement étoiles : pourquoi c'est rentable

Faire classer votre meublé (par Atout France via un organisme agréé, ~250 €) vous donne :

- Un **abattement fiscal de 71 %** au régime micro-BIC (au lieu de 50 % sans classement)
- Une taxe de séjour **plafonnée** sur le classement, donc moins chère pour le voyageur
- Un argument commercial fort sur l'annonce

## Que se passe-t-il en cas de manquement ?

- Pas de numéro d'enregistrement : amende jusqu'à **5 000 €** par bien
- Plus de 120 jours pour une résidence principale : amende jusqu'à **10 000 €**
- Déclaration de revenus oubliée : redressement fiscal + pénalités

## Notre rôle : vous protéger juridiquement

L'équipe Terre et Mer Conciergerie vous accompagne sur **tout le volet réglementaire** : déclaration en mairie, suivi des changements de loi, classement étoiles, bonne tenue du registre des voyageurs. Vous restez 100 % en règle, sans y passer une minute.

[**Discuter avec un expert local →**](/contact)
`,
  },

  // =====================================================================
  // 3, Photos pro : 8 erreurs
  // =====================================================================
  {
    slug: "photographie-airbnb-erreurs-eviter-2026",
    category: "conseils",
    title: "Photographie Airbnb : les 8 erreurs qui vous coûtent des réservations",
    excerpt:
      "Sur Airbnb, 73 % des décisions se prennent sur la première photo. Voici les pièges à éviter et nos conseils pour des visuels qui convertissent.",
    image: "/images/blog/photographie-airbnb-erreurs-eviter-2026.jpg",
    imageAlt: "Photographe professionnel avec appareil reflex en train de prendre des photos d'un intérieur Airbnb",
    author: "L'équipe Terre et Mer",
    date: "2026-04-08",
    readTime: 6,
    keywords:
      "photo airbnb professionnelle, shooting photo airbnb bretagne, optimiser photos airbnb, photographie immobilière saint-malo, conseils photos location airbnb",
    relatedCities: [],
    content: `
## Les photos, premier critère de réservation Airbnb

L'algorithme Airbnb classe en priorité les annonces qui obtiennent le **meilleur taux de clic** (CTR). Et 73 % de ce CTR dépend de la **photo principale**. Avant même la description, le prix ou les avis.

Voici les 8 erreurs les plus fréquentes, et coûteuses, que nous corrigeons systématiquement chez Terre et Mer Conciergerie.

### 1. Photo en portrait (vertical)

Airbnb force un format paysage 16:9. Une photo verticale est tronquée violemment et apparaît disproportionnée dans les résultats de recherche.

**Solution** : toujours en mode paysage, capteur grand-angle (24-35 mm équivalent).

### 2. Lumière artificielle jaune

Les ampoules halogènes ou LED chaudes donnent une teinte jaunâtre peu engageante. Les voyageurs cherchent du blanc et de la lumière naturelle.

**Solution** : shoot en milieu de matinée ou fin d'après-midi (lumière dorée), volets ouverts, lumière intérieure éteinte.

### 3. Désordre visible (chargeurs, télécommandes, sacs poubelle)

Même un détail anodin (chargeur USB sur le canapé, télécommande sur la table) tue l'effet "magazine".

**Solution** : staging complet avant chaque shoot. On range, on harmonise, on aère.

### 4. Lit froissé ou linge dépareillé

Le lit est la photo qui convertit le plus dans une chambre. Un drap mal tendu = ambiance "pas pro".

**Solution** : linge hôtelier blanc fraîchement repassé, lit fait au cordeau, oreillers symétriques.

### 5. Pas de photo en extérieur

Pour un bien sur la Côte d'Émeraude (Saint-Malo, Dinard, Cancale, Saint-Cast), oublier la mer ou la plage est une faute. Les voyageurs achètent une expérience.

**Solution** : intégrer 2-3 photos d'environnement (plage à 5 min, port, sentier GR34).

### 6. Trop de photos (>30) ou pas assez (<12)

L'idéal : **15 à 20 photos**. Plus, c'est de l'overload. Moins, ça donne l'impression de cacher quelque chose.

### 7. Photo principale = couloir / cuisine / salle de bain

La première photo doit être la **pièce hero** : salon avec vue, terrasse vue mer, suite parentale. Jamais un couloir.

### 8. Aucune photo qui raconte une histoire

Une bouteille de vin sur une terrasse au coucher du soleil, deux mugs fumants sur le bord d'un lit défait, des huîtres sur un plateau face au port de Cancale : ces photos "lifestyle" augmentent le CTR de 30 %.

## Notre offre photographie chez Terre et Mer

Nous travaillons avec **deux photographes professionnels** spécialisés location courte durée, un sur la Côte d'Émeraude, un sur le Morbihan. Le shoot est inclus dans notre formule (commission 15 % unique, sans frais cachés). Comptez 4 à 6 heures sur place, 18 à 25 photos retouchées livrées sous 48 h.

[**Demander un shoot photo pour votre bien →**](/contact)
`,
  },

  // =====================================================================
  // 4, Mobile home camping
  // =====================================================================
  {
    slug: "mobile-home-camping-bretagne-airbnb-rentable",
    category: "mobile-home",
    title: "Mobile home en camping en Bretagne : la pépite locative méconnue",
    excerpt:
      "Acheter, faire louer, optimiser : tout savoir sur l'investissement mobile home dans les campings de Dinard, Saint-Cast et Côte d'Émeraude.",
    image: "/images/blog/mobile-home-camping-bretagne-airbnb-rentable.jpg",
    imageAlt: "Mobile home moderne avec terrasse en bois dans un camping verdoyant en Bretagne",
    author: "L'équipe Terre et Mer",
    date: "2026-04-01",
    readTime: 8,
    keywords:
      "mobile home camping bretagne airbnb, location mobile home dinard, location mobile home saint-cast, investir mobile home côte d'émeraude, conciergerie mobile home camping",
    relatedCities: ["dinard", "saint-cast-le-guildo"],
    content: `
## Le mobile home : un investissement locatif sous-coté

Pour 30 000 à 65 000 € (mobile home neuf de 32-40 m² livré et installé), vous accédez à un emplacement en bord de mer dans des campings 4-5★ de Bretagne. La rentabilité brute atteint **15 à 22 %**, soit 3 à 4× celle d'un appartement classique sur la même zone.

### Pourquoi c'est rentable

1. **Un ticket d'entrée bas** : pas de notaire (le terrain reste au camping), pas de TF, pas de gros travaux
2. **Une demande explosive** : le tourisme camping +6 % par an depuis 2020
3. **Des saisons longues** : avril à octobre dans la plupart des campings ouverts à l'année
4. **Une fiscalité douce** : revenus locatifs en BIC micro (abattement 50 %)

### Les meilleurs campings pour louer en Bretagne

**Dinard / Saint-Lunaire / Saint-Briac** :
- Domaine du Bois d'Amour (4★, accès direct plage)
- Camping Le Châtelet ⭐⭐⭐⭐⭐ (Saint-Cast, vue mer mythique)
- Camping Les Mielles (Saint-Cast, accès plage)
- Camping La Crique (Saint-Cast)

**Côte d'Émeraude** :
- Camping Domaine de Pen Guen (Saint-Cast, golf)
- Camping Le P'tit Bois (Saint-Jouan-des-Guérets, à 10 min de Saint-Malo)

### Les frais à anticiper

| Poste | Coût annuel typique |
|---|---|
| Loyer d'emplacement | 3 200 à 5 800 € |
| Énergie (forfait) | 600 à 1 200 € |
| Charges communes (piscine, animation) | inclus ou +400 € |
| Assurance | 250 à 400 € |

À déduire des revenus locatifs pour calculer le **net**.

### Combien rapporte un mobile home en camping ?

Sur la base de notre portefeuille à Dinard et Saint-Cast (mobile home 32 m², 6 couchages, vue mer ou accès plage en 5 min) :

- Tarif moyen : **95 € la nuit** en haute saison, 65 € en basse
- Taux d'occupation : **75 % d'avril à octobre**
- Revenu brut annuel : **8 500 à 13 000 €**
- Frais (loyer emplacement, conciergerie, énergie) : ~5 200 €
- **Revenu net : 3 300 à 7 800 €** (selon la qualité du bien et le pricing)

Sur un investissement de 45 000 €, soit un rendement net de **7 à 17 %**, sans gestion personnelle.

## L'autorisation de sous-location

**Point crucial** : tous les campings n'autorisent pas la sous-location. Avant tout achat, vérifiez :

- Le règlement intérieur du camping
- Le pourcentage de commission éventuel reversé au camping (souvent 0 %, parfois 10 %)
- L'obligation de passer par leur centrale ou la liberté de plateforme (Airbnb, Booking)

Notre équipe Terre et Mer a déjà l'accord écrit avec les principaux campings de la Côte d'Émeraude, ce qui simplifie tout pour vous.

## Notre offre conciergerie mobile home

- [**Mobile home à Dinard →**](/mobil-home-dinard)
- [**Mobile home à Saint-Cast-le-Guildo →**](/mobil-home-saint-cast)

Commission unique 15 % TTC, mise en ligne 24h, accueil 7j/7, ménage hôtelier inclus.
`,
  },

  // =====================================================================
  // 5, Guide Dinard
  // =====================================================================
  {
    slug: "louer-airbnb-dinard-guide-proprietaire",
    category: "ville-guide",
    title: "Louer son Airbnb à Dinard : le guide propriétaire 2026",
    excerpt:
      "Pricing, saisonnalité, attentes des voyageurs internationaux : comment maximiser un bien à Dinard, station Belle Époque par excellence.",
    image: "/images/blog/louer-airbnb-dinard-guide-proprietaire.jpg",
    imageAlt: "Plage de Dinard et villas Belle Époque",
    author: "L'équipe Terre et Mer",
    date: "2026-03-25",
    readTime: 7,
    keywords:
      "louer airbnb dinard, conciergerie dinard, prix nuit dinard airbnb, location courte durée dinard, dinard belle époque location",
    relatedCities: ["dinard"],
    content: `
## Dinard, station haut de gamme à 2h45 de Paris

Avec ses villas Belle Époque, sa promenade du Clair de Lune et son **Festival du film britannique** chaque automne, Dinard occupe un positionnement unique : haut de gamme, francophile et internationale. Près de **40 % des voyageurs Airbnb à Dinard sont étrangers** (Anglais, Belges, Suisses, Allemands).

### Les chiffres clés du marché dinardais

- **65 000+ voyageurs Airbnb** par an
- Tarif moyen : **120 € la nuit** (vs 95 € à Saint-Malo)
- Taux d'occupation moyen : **72 % sur la saison étendue (avril-novembre)**
- Pic absolu : Festival du film britannique (octobre)

### 4 leviers spécifiques à Dinard

**1. Annonce bilingue français/anglais.** Indispensable. Une annonce en français seul perd 35 % de réservations potentielles.

**2. Photos qui captent la lumière atlantique.** Dinard a un microclimat unique avec une lumière dorée le matin et le soir. Profitez-en sur les visuels.

**3. Check-in physique pour les VIP.** Les voyageurs haut de gamme s'attendent à un accueil personnalisé. Notre équipe propose le check-in en personne sur demande, avec présentation du bien et conseils locaux.

**4. Pricing événementiel agressif.** Pendant le Festival du film britannique (5 jours en octobre), les tarifs montent à 250-350 € la nuit. Sans pricing dynamique, vous laissez 1 500 € sur la table chaque année.

## Les attentes des voyageurs à Dinard

D'après nos retours sur 2024-2025 :

- **Vue mer ou jardin clos** : critère #1 (×3 le taux de réservation)
- **Wi-Fi très haut débit** : Dinard accueille beaucoup de télétravailleurs (workation)
- **Lit Queen ou King + literie hôtelière** : décisif pour les +50 ans
- **Stationnement privé** : centre-ville saturé en été
- **Livret d'accueil avec adresses casino, golf, marché** : différenciant

## Notre travail concret pour les biens à Dinard

Notre [équipe locale Côte d'Émeraude](/villes/dinard) intervient en **moins de 15 minutes** sur Dinard. Le bureau le plus proche est à **Pleurtuit (35730)**, à 5 min en voiture.

Services inclus dans la formule 15 % :
- Annonce bilingue + photos pro
- Multidiffusion Airbnb / Booking / Vrbo
- Pricing dynamique calé sur le Festival, les régates et les vacances scolaires
- Accueil voyageurs 24h/24 · 7j/7
- Ménage hôtelier après chaque départ
- Maintenance d'urgence

[**Demander une étude pour votre bien à Dinard →**](/contact)
`,
  },

  // =====================================================================
  // 6, Cancale & ostréiculture
  // =====================================================================
  {
    slug: "louer-airbnb-cancale-port-houle-rentable",
    category: "ville-guide",
    title: "Pourquoi Cancale est un eldorado Airbnb (et comment en profiter)",
    excerpt:
      "Port de la Houle, Pointe du Grouin, huîtres, marées : les atouts de Cancale et nos conseils pour faire performer votre bien.",
    image: "/images/blog/louer-airbnb-cancale-port-houle-rentable.jpg",
    imageAlt: "Port de la Houle à Cancale",
    author: "L'équipe Terre et Mer",
    date: "2026-03-18",
    readTime: 6,
    keywords:
      "louer airbnb cancale, conciergerie cancale, prix nuit cancale, location courte durée cancale port de la houle, airbnb pointe du grouin",
    relatedCities: ["cancale"],
    content: `
## Cancale, le secret le mieux gardé de la Côte d'Émeraude

À 15 minutes de Saint-Malo, Cancale offre un cadre **plus authentique et plus calme** tout en captant le même flux touristique. La cité des huîtres attire **85 000+ voyageurs Airbnb par an** sur un parc immobilier 3× plus petit que Saint-Malo. Résultat : **moins de concurrence, des taux d'occupation supérieurs**.

### Les chiffres du marché cancalais 2026

- Prix moyen / nuit : **70 €**
- Taux d'occupation : **70 %** (vs 65 % moyenne Bretagne)
- Saison : avril à octobre, avec un pic juillet-août
- Top demande : maisons de pêcheurs en pierre dans le centre, T2/T3 vue port de la Houle

### 3 atouts uniques de Cancale pour votre bien

**1. Le Port de la Houle.** Vue port = surcote de 25 à 40 %. Les voyageurs adorent voir les bateaux ostréicoles depuis la terrasse.

**2. La Pointe du Grouin.** Site naturel classé à 10 min, parfait pour le storytelling de l'annonce. Mentionnez-la dès le titre.

**3. Les marchés et la dégustation d'huîtres.** Le marché du dimanche au port de la Houle est une institution. Intégrez les bonnes adresses dans le livret d'accueil, ça booste les avis 5★.

## Comment se démarquer face aux gîtes ?

Cancale a une forte tradition de gîtes ruraux. Pour qu'un Airbnb performe, il faut **jouer la carte hôtelière** :

- Linge hôtelier blanc (jamais coloré)
- Kit d'accueil avec **produits locaux** (galettes Saint-Michel, caramel au beurre salé)
- Wi-Fi très haut débit (la fibre arrive partout en 2026)
- Cafetière à grains ou Nespresso (jamais de cafetière filtre)

## Saisonnalité et pricing dynamique

| Période | Prix moyen | Taux occ. |
|---|---|---|
| Janvier - mars | 55 € | 35 % |
| Avril - juin | 75 € | 70 % |
| Juillet - août | 110 € | 92 % |
| Septembre - octobre (grandes marées) | 95 € | 80 % |
| Novembre - décembre | 60 € | 40 % |

**Astuce** : pendant les grandes marées d'équinoxe (mars + septembre), les voyageurs viennent voir le coefficient 100+. Ces 6 jours peuvent valoir 4× la nuit normale.

[**Voir notre offre Cancale →**](/villes/cancale)
[**Demander une étude personnalisée →**](/contact)
`,
  },

  // =====================================================================
  // 7, Damgan / Morbihan
  // =====================================================================
  {
    slug: "louer-airbnb-damgan-penerf-morbihan-2026",
    category: "ville-guide",
    title: "Louer son Airbnb à Damgan : le guide propriétaire Morbihan 2026",
    excerpt:
      "7 km de plages, port ostréicole de Pénerf, microclimat sud : tout ce qu'il faut savoir pour rentabiliser un bien à Damgan.",
    image: "/images/blog/louer-airbnb-damgan-penerf-morbihan-2026.jpg",
    imageAlt: "Ponton du port de Damgan au coucher de soleil, voiliers et bateaux du Morbihan",
    author: "L'équipe Terre et Mer",
    date: "2026-03-12",
    readTime: 6,
    keywords:
      "louer airbnb damgan, conciergerie damgan, prix nuit damgan, location courte durée damgan kervoyal, airbnb pénerf",
    relatedCities: ["damgan"],
    content: `
## Damgan, la station qui monte sur la côte sud du Morbihan

Plus discrète que La Baule ou Carnac, Damgan attire pourtant **42 000+ voyageurs Airbnb par an** grâce à ses 7 km de plages exposées plein sud, son microclimat exceptionnel et sa proximité du **golfe du Morbihan** (30 min) et de **Belle-Île** (Quiberon à 1 h).

### Le marché Airbnb damganais 2026

- Prix moyen / nuit : **88 €**
- Taux d'occupation : **74 %** (saison étendue d'avril à octobre)
- Type de bien le plus demandé : maison familiale 6 pers. à 5 min de la plage

### Les zones clés à Damgan

1. **Plage de Damgan-centre** : le cœur familial, marchés mercredi/dimanche
2. **Kervoyal** : ambiance "village dans le village", plage abritée, courue par les Britanniques
3. **Pénerf** : port ostréicole authentique, ambiance "carte postale", premium pour les voyageurs en quête d'authenticité

### 4 leviers spécifiques pour votre bien à Damgan

**1. Storytelling "huîtres de Pénerf".** L'AOP Pénerf est moins connue que Cancale mais qualitativement supérieure. Mettez-la en avant dans l'annonce.

**2. Mention du GR34.** Le sentier des douaniers traverse la commune. C'est un argument fort pour les randonneurs (segment +30 % en 2024).

**3. Kit d'accueil "saveurs du Morbihan".** Galettes de Pont-Aven, caramel au beurre salé, kouign amann, cidre fermier. Différenciation immédiate vs un kit générique.

**4. Pricing calé sur les Semaines du Golfe.** Tous les 2 ans (années impaires), l'événement nautique du golfe attire 1 million de visiteurs. Pendant cette semaine, les tarifs grimpent à 150-200 €/nuit.

## La saisonnalité dans le 56 sud

Damgan a une saison **plus étendue** que la Côte d'Émeraude grâce au climat plus doux :

- Avril-mai : 70 % d'occupation (plus que Saint-Malo à la même période)
- Juin-juillet-août : 90 %+ d'occupation
- Septembre : 75 % (effet "pré-rentrée" des Anglais)
- Vacances de Toussaint : 60 % (microclimat doux)

## Notre équipe Sud Bretagne

Contrairement aux conciergeries généralistes, **nous avons une équipe physiquement basée dans le 56**. Intervention en moins de 15 minutes sur Damgan, Pénerf, Ambon, Billiers et Muzillac. Ménage, accueil, maintenance, photos pro : tout est géré en local.

[**Voir notre offre Damgan →**](/villes/damgan)
[**Demander une étude →**](/contact)
`,
  },

  // =====================================================================
  // 8, La Roche-Bernard
  // =====================================================================
  {
    slug: "louer-airbnb-la-roche-bernard-petite-cite-caractere",
    category: "ville-guide",
    title: "La Roche-Bernard : faire performer son Airbnb dans une Petite Cité de Caractère",
    excerpt:
      "Port de plaisance, ruelles pavées, gastronomie : pourquoi La Roche-Bernard est un marché premium en plein essor.",
    image: "/images/blog/louer-airbnb-la-roche-bernard-petite-cite-caractere.jpg",
    imageAlt: "Port de plaisance de La Roche-Bernard sur la Vilaine, voiliers et bateaux traditionnels",
    author: "L'équipe Terre et Mer",
    date: "2026-03-05",
    readTime: 6,
    keywords:
      "louer airbnb la roche-bernard, conciergerie la roche-bernard, prix nuit la roche-bernard, location courte durée petite cité caractère morbihan, airbnb vilaine",
    relatedCities: ["la-roche-bernard"],
    content: `
## La Roche-Bernard, joyau classé de la Vilaine

Classée **Petite Cité de Caractère** et **Plus Beaux Détours de France**, La Roche-Bernard est une perle que Vannes et Nantes envient. Son port de plaisance accueille 700+ bateaux, ses ruelles pavées et ses maisons à pans de bois (XVIᵉ-XVIIᵉ) en font une destination courue toute l'année.

### Le marché Airbnb 2026

- Prix moyen / nuit : **82 €**
- Voyageurs Airbnb / an : **28 000+**
- Saison principale : avril à octobre, avec un second pic en décembre (marchés de Noël)
- Type de bien le plus demandé : maison de caractère en pierre, T3-T4 vue port

### Une clientèle haut de gamme à 70 %

La Roche-Bernard attire une clientèle aisée :

- **Plaisanciers** stationnés au port (séjours courts, prêts à payer cher)
- **Couples 50+** en escapade gastronomique (L'Auberge Bretonne** Michelin)
- **Voyageurs internationaux** (Britanniques, Allemands, Suisses)
- **Touristes du golfe du Morbihan** qui rayonnent

### Notre approche pour les biens à La Roche-Bernard

**1. Photos premium en lumière dorée.** Le port de la Vilaine offre une lumière exceptionnelle au lever et au coucher du soleil. Indispensable pour les visuels.

**2. Annonce français/anglais.** 35 % de notre clientèle La Roche-Bernard est anglophone.

**3. Livret d'accueil orienté gastronomie & patrimoine.** L'Auberge Bretonne, château de Léhélec, musée de la Vilaine maritime : ces adresses sont valorisées dans le livret.

**4. Pricing dynamique salons nautiques + Noël.** Pendant le Salon Nautique de Paris (décembre), de nombreux plaisanciers viennent stationner leur bateau au port. C'est un mini-pic à exploiter.

## Pourquoi nous y croyons

Vannes attire la clientèle "événementielle" (festivals, congrès) et arrive saturée. La Roche-Bernard capte ce **report de trafic** sans subir l'inflation des prix vannetais. Pour un investisseur, c'est un excellent point d'entrée 2026.

[**Voir notre offre La Roche-Bernard →**](/villes/la-roche-bernard)
[**Demander une étude →**](/contact)
`,
  },

  // =====================================================================
  // 9, Optimiser son annonce
  // =====================================================================
  {
    slug: "optimiser-annonce-airbnb-2026-titre-description-photos",
    category: "conseils",
    title: "Optimiser son annonce Airbnb en 2026 : titre, description, photos",
    excerpt:
      "Les 7 règles d'or pour une annonce qui apparaît en top des résultats Airbnb et convertit les visiteurs en réservations.",
    image: "/images/blog/optimiser-annonce-airbnb-2026-titre-description-photos.jpg",
    imageAlt: "Annonce Airbnb optimisée sur ordinateur",
    author: "L'équipe Terre et Mer",
    date: "2026-02-26",
    readTime: 7,
    keywords:
      "optimiser annonce airbnb, titre airbnb seo, description airbnb conversion, classement airbnb 2026, algorithme airbnb top résultats",
    relatedCities: [],
    content: `
## L'algorithme Airbnb : ce qui a changé en 2026

Depuis la mise à jour de novembre 2025, l'algorithme Airbnb privilégie 3 critères principaux dans le classement de recherche :

1. **Taux de clic** (CTR) : combien de personnes cliquent depuis les résultats
2. **Taux de conversion** : combien réservent après avoir visité l'annonce
3. **Engagement post-séjour** : qualité des avis, taux de réponse, vitesse de communication

Les annonces Superhost gagnent en moyenne **22 places** dans les résultats sur la même requête.

## Les 7 règles d'or pour 2026

### 1. Titre : 50 caractères max, USP en premier

❌ "Bel appartement à Saint-Malo"
✅ "Vue mer ★ Intra-Muros ★ Jacuzzi ★ Walk to beach"

Les voyageurs scannent. Mettez en avant **3 USP forts** dès le titre. Évitez "magnifique", "charmant", "atypique", banal, peu de signal SEO.

### 2. Description : 4 paragraphes, 400-600 mots

Structure idéale :
- §1 : qui dort où, qui mange où, l'expérience promise (storytelling)
- §2 : équipements concrets (Wi-Fi débit, parking, climatisation, vue)
- §3 : quartier et accès (à 5 min des remparts à pied, marché bio mercredi…)
- §4 : règles + promesse hôte (réponse en 5 min, check-in flexible)

### 3. Photo principale : la pièce hero, pas un couloir

Salon vue mer, terrasse au coucher du soleil, suite parentale. Jamais l'entrée, la salle de bain ou la cuisine.

### 4. Tags d'équipements : tout cocher

Airbnb propose 200+ tags d'équipements. **Cochez tous ceux qui s'appliquent**, ils servent au filtre de recherche. Ne pas cocher "parking gratuit" si vous l'avez = vous disparaissez de la recherche "parking inclus".

### 5. Calendrier : ouvert sur 12 mois minimum

Un calendrier fermé après 3 mois pénalise votre classement. Ouvrez sur 12-18 mois et bloquez vos dates personnelles à part.

### 6. Réponse en moins de 1 heure

Le délai de réponse moyen est aujourd'hui de **18 minutes** chez les Superhosts. Au-delà d'1 heure, votre taux de conversion chute de 60 %. Une conciergerie 24h/24 résout ce problème.

### 7. Pricing dynamique au quotidien

Airbnb favorise les annonces avec un prix qui **bouge** au quotidien. Cela signale un hôte actif, en phase avec le marché. À l'inverse, un prix figé est un signal de désengagement.

## Notre rôle chez Terre et Mer Conciergerie

Nous prenons en charge l'**intégralité du paramétrage** : refonte de l'annonce, shoot pro, multidiffusion, pricing dynamique, communication 24h/24. Vous gardez le contrôle (calendrier, accès) sans y passer une minute.

[**Audit gratuit de votre annonce existante →**](/contact)
`,
  },

  // =====================================================================
  // 10, Pricing dynamique
  // =====================================================================
  {
    slug: "pricing-dynamique-airbnb-comment-ca-marche",
    category: "conseils",
    title: "Pricing dynamique Airbnb : la méthode qui rapporte +18 % en moyenne",
    excerpt:
      "Pourquoi un prix figé vous fait perdre de l'argent, et comment notre algorithme ajuste vos tarifs au quotidien sur la Côte d'Émeraude.",
    image: "/images/blog/pricing-dynamique-airbnb-comment-ca-marche.jpg",
    imageAlt: "Tableau de tarifs Airbnb par nuit affiché sur MacBook",
    author: "L'équipe Terre et Mer",
    date: "2026-02-19",
    readTime: 6,
    keywords:
      "pricing dynamique airbnb, tarif nuit airbnb optimiser, prix airbnb saint-malo dinard, smart pricing airbnb, augmenter revenus airbnb",
    relatedCities: [],
    content: `
## Pourquoi un prix figé tue votre rentabilité

Un propriétaire qui fixe son tarif à 95 € la nuit toute l'année commet 3 erreurs simultanées :

1. **Il sous-vend** en juillet-août (le marché est à 130 €)
2. **Il survend** en novembre-février (le marché est à 65 €)
3. **Il rate les pics événementiels** (Festival de Dinard, Route du Rhum, marées) où le marché monte à 200 €+

Le manque à gagner moyen sur un bien sans pricing dynamique : **18 à 28 % de revenus annuels**.

## Comment fonctionne le pricing dynamique

Chez Terre et Mer, nous nous appuyons sur **PriceLabs**, la plateforme de yield management Airbnb la plus utilisée au monde (plus de 400 000 logements pilotés). Notre équipe pricing combine ses recommandations algorithmiques avec une **couche d'expertise locale** (événements micro-bretons, météo Côte d'Émeraude, comportement réel de la clientèle UK/Belge) que la machine seule ne voit pas.

Concrètement, votre tarif est ajusté **chaque nuit** en fonction de :

- **L'historique de réservation** sur 24 mois glissants
- **Le calendrier des événements locaux** (festivals, marées, vacances scolaires des 5 zones européennes)
- **La demande temps réel** sur Airbnb (recherches, vues, ajouts en favoris)
- **Le comportement de la concurrence** dans un rayon de 1 km
- **La météo** (à 10 jours, +5 à +12 % par jour de soleil prévu)
- **Vos contraintes propriétaire** (durée minimale, écart vs marché, dates bloquées)

Résultat : un tarif "intelligent" qui maximise le couple **prix × taux d'occupation**.

## Exemple concret : maison 4 pers. à Dinard

| Période | Prix figé | Pricing dynamique |
|---|---|---|
| 5 août | 95 € | 165 € (+74 %) |
| 15 octobre (Festival film britannique) | 95 € | 280 € (+195 %) |
| 12 février | 95 € | 65 € (-32 %, mais réservé !) |
| Mai (semaine de l'Ascension) | 95 € | 145 € (+53 %) |

Sur l'année : **revenus +24 %** en moyenne sans rien changer au bien.

## Les pièges à éviter

**Piège 1 : utiliser uniquement Smart Pricing d'Airbnb.** Smart Pricing est trop conservateur et tend à sous-vendre les pics. Il manque les événements ultra-locaux (Route du Rhum à Saint-Malo, Festival des Plaisanciers à La Roche-Bernard).

**Piège 2 : changer le prix manuellement chaque jour.** Personne n'a le temps. Et si vous oubliez 1 jour, c'est jusqu'à 200 € de manque à gagner pour ce jour-là.

**Piège 3 : copier le prix du voisin.** Votre voisin a peut-être un mauvais classement, des photos médiocres ou pas d'avis. Son tarif n'est pas un benchmark fiable.

## Notre approche chez Terre et Mer : PriceLabs + expertise humaine

Le pricing dynamique est **inclus dans notre formule à 15 % TTC**, sans surcoût (la licence PriceLabs est à notre charge, pas à la vôtre). Notre équipe pricing supervise quotidiennement votre bien dans la plateforme PriceLabs et ajuste manuellement les pics événementiels que l'algorithme ne capte pas seul : **Festival du film britannique de Dinard, Route du Rhum, fête des huîtres de Cancale, marées d'équinoxe, Fête des Remparts de Dinan…** L'algorithme + le local sense = la combinaison gagnante.

[**Demander une simulation de votre bien →**](/contact)
`,
  },

  // =====================================================================
  // 11, LMNP / Fiscalité
  // =====================================================================
  {
    slug: "lmnp-airbnb-bretagne-fiscalite-2026",
    category: "fiscalite",
    title: "LMNP et Airbnb en 2026 : ce qui change pour les propriétaires bretons",
    excerpt:
      "Régime micro-BIC, abattement 50 %, classement étoiles, location courte durée : le guide fiscal 2026 pour les propriétaires en location saisonnière.",
    image: "/images/blog/lmnp-airbnb-bretagne-fiscalite-2026.jpg",
    imageAlt: "Maison de pêcheur en pierre à Cancale",
    author: "L'équipe Terre et Mer",
    date: "2026-02-12",
    readTime: 8,
    keywords:
      "lmnp airbnb 2026, micro-bic location courte durée, abattement 50% lmnp, fiscalité airbnb saint-malo, classement étoiles meublé tourisme bretagne",
    relatedCities: [],
    content: `
## Le statut LMNP (Loueur en Meublé Non Professionnel)

Toute location meublée touristique en France relève du **régime LMNP** dès lors que les revenus annuels n'excèdent pas 23 000 € **et** moins de 50 % des revenus du foyer. Au-delà : LMP (Loueur en Meublé Professionnel), plus contraignant.

### Les 2 régimes fiscaux possibles

**1. Micro-BIC (le plus simple)**

- Plafond 2026 : **77 700 € de recettes annuelles** pour un meublé classique
- Abattement forfaitaire : **50 %** des recettes
- Pas de comptabilité à tenir, déclaration simplifiée

⚠️ **NOUVEAUTÉ 2025** : depuis la **loi Le Meur**, le plafond a été abaissé à **15 000 €** pour les **meublés non classés**. Au-delà : passage obligatoire au régime réel.

➡️ Conséquence : faire **classer votre meublé en étoiles** est devenu fiscalement crucial.

**2. Régime réel (plus avantageux dès 25 000 € de recettes)**

- Déduction de toutes les charges réelles (intérêts d'emprunt, travaux, taxe foncière, conciergerie, énergie)
- Amortissement comptable du bien (typiquement 2-3 % par an du prix d'achat hors terrain)
- **Souvent 0 € d'impôt** les 8 à 12 premières années grâce à l'amortissement

➡️ Idéal pour les biens financés à crédit ou récemment achetés.

## Le classement étoiles : la nouvelle obligation économique

Faire classer votre meublé (par Atout France via un organisme agréé, **~250 € tous les 5 ans**) vous donne en 2026 :

- **Plafond micro-BIC porté à 77 700 €** (au lieu de 15 000 € sans classement)
- **Abattement 71 %** au lieu de 50 % (économie d'impôt jusqu'à 21 %)
- Taxe de séjour **plafonnée et plus prévisible**
- Argument commercial fort sur l'annonce

À Saint-Malo, Dinard, Cancale, Saint-Cast : les **classements 3-4★** sont les plus rentables (rapport coût/avantage fiscal optimal).

## Cotisations sociales : seuil 23 000 €

Si vos recettes annuelles dépassent **23 000 €**, vous devez payer des cotisations sociales (URSSAF, ~30 % des recettes nettes). C'est un seuil à anticiper : un studio à Cancale loué à 88 € la nuit, occupé 250 jours, dépasse ce seuil.

➡️ Conseil : anticipez cette bascule en travaillant avec un comptable spécialisé location courte durée.

## Notre accompagnement fiscal

Nous travaillons avec **3 cabinets comptables spécialisés LMNP** sur Saint-Malo, Vannes et Rennes. Tarifs négociés pour nos clients : **~600 € HT/an** pour la tenue comptable et la déclaration.

[**Discuter de votre situation →**](/contact)
`,
  },

  // =====================================================================
  // 12, Saint-Cast plages
  // =====================================================================
  {
    slug: "louer-airbnb-saint-cast-le-guildo-grande-plage",
    category: "ville-guide",
    title: "Saint-Cast-le-Guildo : louer son Airbnb sur l'une des plus belles plages d'Europe",
    excerpt:
      "2 km de sable fin, golf, casino, marché animé : le guide propriétaire pour exploiter le potentiel locatif de Saint-Cast en 2026.",
    image: "/images/blog/louer-airbnb-saint-cast-le-guildo-grande-plage.jpg",
    imageAlt: "Plage de Saint-Cast-le-Guildo et villas",
    author: "L'équipe Terre et Mer",
    date: "2026-02-05",
    readTime: 7,
    keywords:
      "louer airbnb saint-cast-le-guildo, conciergerie saint-cast, prix nuit saint-cast, location courte durée saint-cast côte émeraude, grande plage saint-cast",
    relatedCities: ["saint-cast-le-guildo"],
    content: `
## Saint-Cast, station familiale et chic à 2h45 de Paris

Élue parmi les **plus belles plages d'Europe** par le Sunday Times, la Grande Plage de Saint-Cast s'étire sur 2 km de sable fin. Avec son **golf 18 trous** mythique, son casino, son port de plaisance et son marché du dimanche, Saint-Cast attire **55 000+ voyageurs Airbnb par an**.

### Les chiffres 2026

- Prix moyen / nuit : **85 €**
- Taux d'occupation : **78 %** sur la saison étendue
- Saison principale : mai à septembre, avec une extension forte en juin et septembre
- Type de bien le plus demandé : maison familiale 6-8 pers. à 5 min de la Grande Plage

### Les zones à privilégier

1. **Centre-ville et Grande Plage** : surcote +30 % vs périphérie
2. **Pen Guen** : quartier résidentiel haut de gamme, golf à 5 min
3. **Le Guildo** : ambiance "village" plus calme, port d'échouage
4. **L'Isle** : pointe presqu'île, vues exceptionnelles

### 5 leviers spécifiques pour Saint-Cast

**1. Storytelling "Plus belles plages d'Europe".** Mettez ce label dès le titre de l'annonce.

**2. Mention du golf 18 trous.** Saint-Cast a une clientèle "golfeurs" très fidèle, prête à payer cher pour la proximité.

**3. Pricing événementiel régates.** Pendant les régates de Saint-Cast (juillet-août), les tarifs montent à 130-180 € la nuit.

**4. Photos en extérieur.** La plage, le port, le sentier des douaniers, la nature est le hero du marketing à Saint-Cast.

**5. Mobile homes en camping.** Saint-Cast accueille **5 campings 4-5★** : Le Châtelet, Les Mielles, La Crique, Pen Guen, Domaine de la Crique. Les mobile homes y sont des investissements très rentables. Voir notre [page dédiée mobile home Saint-Cast](/mobil-home-saint-cast).

## Notre équipe à Saint-Cast

Notre City Manager couvre Saint-Cast, Lancieux, Saint-Jacut et Matignon avec une **intervention en moins de 10 minutes**. Le bureau le plus proche est à **Ploubalay (22650)**.

[**Voir notre offre Saint-Cast →**](/villes/saint-cast-le-guildo)
[**Demander une étude →**](/contact)
`,
  },

  // =====================================================================
  // 13, Dinan tourisme médiéval
  // =====================================================================
  {
    slug: "louer-airbnb-dinan-cite-medievale",
    category: "ville-guide",
    title: "Airbnb à Dinan : exploiter le potentiel d'une cité médiévale classée",
    excerpt:
      "Remparts, maisons à pans de bois, port sur la Rance : pourquoi Dinan est l'un des marchés les plus stables de Bretagne.",
    image: "/images/blog/louer-airbnb-dinan-cite-medievale.jpg",
    imageAlt: "Ruelle pavée et maisons à colombages de Dinan",
    author: "L'équipe Terre et Mer",
    date: "2026-01-29",
    readTime: 6,
    keywords:
      "louer airbnb dinan, conciergerie dinan, prix nuit dinan, location courte durée dinan rance, airbnb cité médiévale bretagne",
    relatedCities: ["dinan"],
    content: `
## Dinan, l'un des plus beaux secrets de Bretagne

Classée parmi les **Plus Beaux Détours de France**, Dinan est l'une des cités médiévales les mieux conservées d'Europe. Ses 3 km de remparts, ses maisons à pans de bois (XIIIᵉ-XVIIᵉ) et son port sur la Rance attirent **45 000+ voyageurs Airbnb par an**.

### Le marché Airbnb dinannais 2026

- Prix moyen / nuit : **75 €**
- Taux d'occupation : **70 %** sur saison étendue
- Saison : mai à octobre + pic décembre (marchés de Noël)

### Pourquoi Dinan est un marché stable

Contrairement à Saint-Malo (très saisonnier juillet-août), Dinan a une **demande étalée** :

- Couples 50+ en escapade culturelle (avril-juin, septembre-novembre)
- Familles avec enfants (juillet-août)
- Groupes d'amis pour la **Fête des Remparts** en juillet (40 000 spectateurs en 4 jours)
- Touristes Mont-Saint-Michel qui rayonnent (à 35 min)

Cette diversité = un taux d'occupation **régulier** plutôt que 100 % en été et 20 % en hiver.

### Les zones les plus rentables

1. **Centre médiéval (rue du Jerzual, place des Cordeliers)** : surcote +35 % pour les biens avec poutres et pierres apparentes
2. **Port sur la Rance** : ambiance "carte postale", très demandé par les internationaux
3. **Léhon** : ancienne cité monastique à 2 km, plus calme, parfait pour les longs séjours

### Notre approche pour Dinan

**1. Photos qui captent le charme médiéval.** Les pierres, les poutres, les escaliers en colimaçon, chaque détail compte.

**2. Annonce orientée patrimoine.** Mentionner la basilique Saint-Sauveur, la tour de l'Horloge, le château.

**3. Livret d'accueil avec circuit Dinan/Mont-Saint-Michel.** Très demandé par les voyageurs internationaux.

**4. Pricing calé sur la Fête des Remparts.** Pendant les 4 jours, les tarifs grimpent à 200-280 €/nuit. Manquer ce pic = 600 € de manque à gagner par bien.

[**Voir notre offre Dinan →**](/villes/dinan)
[**Demander une étude →**](/contact)
`,
  },

  // =====================================================================
  // 14, Saint-Briac
  // =====================================================================
  {
    slug: "louer-airbnb-saint-briac-village-renoir",
    category: "ville-guide",
    title: "Saint-Briac-sur-Mer : le village des artistes qui rapporte",
    excerpt:
      "8 km de littoral, golf historique, criques préservées : le guide propriétaire pour exploiter Saint-Briac, secret le mieux gardé de la Côte d'Émeraude.",
    image: "/images/blog/louer-airbnb-saint-briac-village-renoir.jpg",
    imageAlt: "Mouillage et château de Nessay à Saint-Briac",
    author: "L'équipe Terre et Mer",
    date: "2026-01-22",
    readTime: 6,
    keywords:
      "louer airbnb saint-briac-sur-mer, conciergerie saint-briac, prix nuit saint-briac, golf saint-briac dinard, château de nessay",
    relatedCities: ["saint-briac-sur-mer"],
    content: `
## Saint-Briac, la pépite préservée entre Dinard et Lancieux

Renoir, Signac et Émile Bernard l'ont peint. Son **golf 18 trous** date de 1926 (le 2ᵉ plus ancien du continent européen). Ses 8 km de littoral aux falaises rouges abritent **22 000+ voyageurs Airbnb par an**, sur un parc immobilier 4× plus petit que Dinard. Résultat : **moins de concurrence, taux d'occupation supérieurs**.

### Marché 2026

- Prix moyen / nuit : **92 €** (dans le top 5 de la Côte d'Émeraude)
- Taux d'occupation : **75 %** sur saison étendue (avril-octobre)
- Type de bien le plus demandé : villa avec jardin clos, accès golf

### 4 atouts uniques de Saint-Briac

**1. Le château de Nessay.** Site instagrammable au mouillage, vu sur des millions de posts en 2025. Mentionnez-le dès le titre de l'annonce.

**2. Le golf Dinard-Saint-Briac.** Une clientèle aisée et fidèle (85 % de séjours répétés). Pricing premium possible.

**3. Les criques secrètes du GR34.** Salinette, Béchet, Port-Hue, Longchamp, chaque crique a son ambiance. Liste à inclure dans le livret d'accueil.

**4. Le festival des arts.** Chaque été, une dizaine d'expositions et concerts attirent une clientèle culturelle premium. Pricing événementiel à exploiter.

### Stratégie Anglais & Belges

Saint-Briac est très demandé par les **Britanniques** (40 % des voyageurs étrangers) et les **Belges**, qui viennent pour le calme et le golf. Annonce bilingue obligatoire pour ne pas perdre ce flux.

[**Voir notre offre Saint-Briac →**](/villes/saint-briac-sur-mer)
[**Demander une étude →**](/contact)
`,
  },

  // =====================================================================
  // 15, Investir Airbnb Côte d'Émeraude
  // =====================================================================
  {
    slug: "investir-airbnb-cote-emeraude-bretagne-2026",
    category: "investissement",
    title: "Investir dans un Airbnb sur la Côte d'Émeraude en 2026",
    excerpt:
      "Quelle ville choisir, quel budget prévoir, quel rendement viser : le guide investisseur 2026 pour Saint-Malo, Dinard, Cancale, Saint-Cast.",
    image: "/images/blog/investir-airbnb-cote-emeraude-bretagne-2026.jpg",
    imageAlt: "Vue aérienne de Saint-Malo intra-muros et de la Côte d'Émeraude depuis le ciel",
    author: "L'équipe Terre et Mer",
    date: "2026-01-15",
    readTime: 9,
    keywords:
      "investir airbnb côte d'émeraude, investissement locatif bretagne 2026, rentabilité airbnb saint-malo, investir location courte durée dinard, prix immobilier côte émeraude 2026",
    relatedCities: ["saint-malo", "dinard", "cancale", "saint-cast-le-guildo"],
    content: `
## Pourquoi la Côte d'Émeraude reste un eldorado en 2026

Malgré la hausse des taux de 2024-2025, la Côte d'Émeraude reste l'une des zones les plus rentables de France pour la location courte durée :

- **Prix au m² maîtrisés** vs Côte Basque ou Côte d'Azur (5 800 €/m² moyenne 2026)
- **TGV Paris-Saint-Malo en 2h15** : marché de proximité fort
- **Demande étrangère soutenue** (Anglais via ferry Portsmouth, Allemands, Belges)
- **Saison étendue** d'avril à novembre (microclimat doux)

### Comparatif rendement par ville (2026)

| Ville | Prix m² moyen | Loyer Airbnb annuel net (T2) | Rendement net |
|---|---|---|---|
| Saint-Malo intra-muros | 7 200 €/m² | 22 000 € | 5,1 % |
| Dinard centre | 8 100 €/m² | 24 800 € | 5,1 % |
| Cancale | 5 600 €/m² | 16 200 € | 4,8 % |
| Saint-Cast | 5 100 €/m² | 14 800 € | 4,8 % |
| Saint-Briac | 6 800 €/m² | 19 500 € | 4,7 % |
| Saint-Lunaire | 5 900 €/m² | 17 100 € | 4,8 % |
| Pleurtuit | 4 300 €/m² | 13 200 € | 5,1 % |

(Pour un T2 de 45 m² hors charges, avec conciergerie et après commission de 15 %.)

➡️ **Pleurtuit, Cancale et Saint-Cast** offrent les meilleurs ratios qualité/prix pour un primo-investisseur.

### Les bons quartiers à viser

**Saint-Malo** : intra-muros (rare), Plage du Sillon, Paramé proche plage, Saint-Servan port.

**Dinard** : centre Plage de l'Écluse, Saint-Énogat, Saint-Lunaire (limite ouest).

**Cancale** : Port de la Houle, La Houle, Pointe du Grouin (vue mer).

**Saint-Cast** : centre Grande Plage, Pen Guen (golf), L'Isle (presqu'île).

## Le profil de bien le plus rentable

D'après nos données 2025-2026, le **T2/T3 de 45-65 m²** avec ces critères est le plus performant :

- Vue mer ou jardin clos
- 2 chambres minimum (les familles paient mieux)
- Parking privé (centre-ville saturé en été)
- Wi-Fi très haut débit (workation)
- Cuisine équipée moderne (lave-vaisselle indispensable)

Budget cible : **220 000 à 380 000 €** selon la ville.

### Avantages fiscaux à exploiter en 2026

- **LMNP au régime réel** : 0 € d'impôt les 8-12 premières années (amortissement)
- **Classement 3-4★** : abattement 71 % en micro-BIC
- **Déficit foncier** sur le neuf rénové : déduction des travaux

## Notre rôle d'accompagnement investisseur

Avant l'achat, nous établissons une **étude personnalisée** :

- Estimation des revenus réels par bien candidat
- Analyse de la concurrence dans un rayon de 500 m
- Calcul de rentabilité nette après tous frais
- Recommandations pricing et amortissement

Service **100 % gratuit et sans engagement**.

[**Demander une étude investisseur →**](/contact)
`,
  },

  // =====================================================================
  // 16, Voyageurs problématiques
  // =====================================================================
  {
    slug: "eviter-mauvais-voyageurs-airbnb-bretagne",
    category: "conseils",
    title: "Comment éviter les mauvais voyageurs sur Airbnb : les 6 filtres qui marchent",
    excerpt:
      "Fêtes sauvages, dégradations, plaintes voisines : voici comment trier les voyageurs et louer en toute sérénité, même en haute saison.",
    image: "/images/blog/eviter-mauvais-voyageurs-airbnb-bretagne.jpg",
    imageAlt: "Enfant en marinière observant aux jumelles",
    author: "L'équipe Terre et Mer",
    date: "2026-01-08",
    readTime: 6,
    keywords:
      "éviter mauvais voyageurs airbnb, sélection voyageurs airbnb, fête airbnb empêcher, sécurité airbnb bretagne, dégradations location airbnb",
    relatedCities: [],
    content: `
## Le risque "mauvais voyageur" : 1 sur 200 réservations

Sur notre portefeuille de 70 logements (pas plus, c'est notre engagement), nous comptons **moins de 1 % d'incident sérieux par an**. Voici les 6 filtres concrets que nous appliquons à chaque réservation pour réduire ce risque au strict minimum.

### Filtre 1 : Vérification d'identité Airbnb

Activé en option, ce paramètre exige la vérification ID + photo Airbnb du voyageur. Refusez automatiquement les profils non vérifiés. **-65 % de risques** de mauvais comportement.

### Filtre 2 : Avis voyageur précédents

Lisez les **3 derniers avis** laissés par d'autres hôtes. Tout commentaire négatif ou tiède = refus immédiat. Un voyageur qui a réservé 5 fois et n'a aucun avis = à éviter (les hôtes mécontents oublient parfois de noter, mais s'en plaignent en privé).

### Filtre 3 : Match logement / nombre de voyageurs

Une réservation pour **2 voyageurs dans une maison 8 personnes** est un signal fort de fête. Demandez systématiquement la composition du groupe.

### Filtre 4 : Distance domicile-logement

Un voyageur qui habite à **moins de 50 km** de votre bien (par exemple Rennes-Saint-Malo) est statistiquement plus à risque (réservation pour fête locale). Demandez la raison du séjour.

### Filtre 5 : Horaire de réservation

Les réservations passées **entre minuit et 5 h du matin** ont 3× plus de risques de dégradation. Notre équipe nuit (permanence 24h/24) examine chaque demande nocturne avant validation.

### Filtre 6 : Capteurs sonores connectés

Nous installons (gratuitement, en option) des **capteurs Minut ou Roomonitor** : ils détectent un excès de décibels SANS enregistrer la voix (conforme RGPD). En cas de fête, alerte SMS à notre équipe + intervention en 30 minutes.

## Que faire en cas d'incident ?

**Étape 1** : nous prenons des photos immédiates des dégradations.
**Étape 2** : ouverture d'un dossier AirCover (couverture jusqu'à 1 000 000 €).
**Étape 3** : contact Airbnb sous 14 jours avec dossier complet.
**Étape 4** : remboursement sous 60 jours en moyenne sur AirCover.

Pour Booking.com et Vrbo : nous prenons une **caution physique** entre 400 € et 1 500 € selon la valeur du bien.

## Notre rôle de filtre humain

Au-delà des règles automatiques, **chaque réservation est validée par notre équipe** dans les 30 minutes qui suivent la demande. Aucune réservation ne passe sans avis humain. C'est ce qui fait la différence avec les conciergeries 100 % automatisées.

[**Discuter de la sécurité de votre bien →**](/contact)
`,
  },

  // =====================================================================
  // 17, Livret d'accueil
  // =====================================================================
  {
    slug: "livret-accueil-airbnb-bretagne-parfait",
    category: "conseils",
    title: "Le livret d'accueil parfait pour votre Airbnb breton",
    excerpt:
      "Plus qu'un document utile, le livret d'accueil est votre meilleure arme pour décrocher des avis 5★. Voici la structure qui fait la différence.",
    image: "/images/blog/livret-accueil-airbnb-bretagne-parfait.jpg",
    imageAlt: "Linge hôtelier blanc roulé sur lit fait",
    author: "L'équipe Terre et Mer",
    date: "2025-12-22",
    readTime: 5,
    keywords:
      "livret accueil airbnb bretagne, guide voyageur airbnb saint-malo, welcome book airbnb dinard, conseils livret airbnb",
    relatedCities: [],
    content: `
## Pourquoi le livret d'accueil est crucial

Un voyageur Airbnb laisse en moyenne **3,4 fois plus** d'avis 5★ quand il a accès à un livret d'accueil bien fait. Pourquoi ? Parce qu'il se sent **considéré** et **renseigné**, sans avoir à vous écrire à toute heure du jour ou de la nuit.

## La structure idéale (10 sections)

### 1. Mot de bienvenue personnalisé (1 page)

Pas un message générique. Mentionner la ville, la spécificité du bien, votre fierté de les accueillir.

### 2. Plan technique du logement (2 pages)

- Wi-Fi : nom du réseau + mot de passe + débit en Mbps
- Codes accès, boîte aux lettres, parking
- Comment fonctionne la cuisine, la salle de bain, le chauffage
- TV / Apple TV / Netflix / Wi-Fi

### 3. Règles du logement (1 page)

Court, ferme, factuel. Pas de fête, pas de fumée intérieure, animaux acceptés/refusés. Soulignez les règles via icônes.

### 4. Situations d'urgence (1 page)

- Numéro Terre et Mer 24h/24
- Médecins de garde, pharmacies de garde, hôpital le plus proche
- Gendarmerie / Police municipale
- Coupure d'eau / d'électricité : qui appeler

### 5. Marchés locaux & courses (1 page)

Adresses précises avec heures et jours :
- **Saint-Malo** : marchés intra-muros mardi/vendredi, Marché Marbeuf samedi
- **Dinard** : Halle de Dinard quotidienne, marché place Newquay mardi/vendredi/dimanche
- **Cancale** : marché Port de la Houle dimanche

### 6. Restaurants triés par budget (2 pages)

- Petit déjeuner : 2 adresses < 15 €
- Déjeuner rapide : 3 adresses 15-25 €
- Dîner romantique : 2 adresses 35-60 €
- Adresse coup de cœur : 1 adresse "secret"

### 7. Activités à faire à pied (1 page)

Chemins du GR34, balades en ville, points photo.

### 8. Activités voiture nécessaire (1 page)

- Mont-Saint-Michel (à 1h de Saint-Malo)
- Cap Fréhel et Fort la Latte (40 min depuis Saint-Cast)
- Golfe du Morbihan (1h depuis Damgan)

### 9. Adresses confidentielles (1 page)

Le livret qui se démarque. Adresses connues uniquement des locaux : chambres d'hôtes pour la suite du séjour, ostréiculteur pas cher, plage cachée.

### 10. Conseils départ (1 page)

- Heure de check-out, où laisser les clés
- Vaisselle / poubelle / draps
- Comment laisser un avis Airbnb (lien direct si possible)

## Format conseillé

**PDF interactif sur tablette ou QR code** vers une version mobile-friendly. Plus de papier qui se perd, plus de typos qui vieillissent, on met à jour quand on veut.

## Notre service livret d'accueil

Inclus dans la formule à 15 % TTC. Nous **rédigeons et imprimons** votre livret personnalisé après visite du bien (10-12 pages, papier épais, finition soignée). Mise à jour annuelle.

[**Demander un livret personnalisé →**](/contact)
`,
  },

  // =====================================================================
  // 18, Conciergerie vs auto-gestion
  // =====================================================================
  {
    slug: "conciergerie-vs-auto-gestion-airbnb-comparatif",
    category: "conseils",
    title: "Conciergerie ou auto-gestion : que choisir en 2026 ?",
    excerpt:
      "Tableau comparatif chiffré : combien coûte vraiment l'auto-gestion, combien rapporte une conciergerie, quel est le break-even point ?",
    image: "/images/blog/conciergerie-vs-auto-gestion-airbnb-comparatif.jpg",
    imageAlt: "Technicien Terre et Mer devant véhicule de service",
    author: "L'équipe Terre et Mer",
    date: "2025-12-15",
    readTime: 7,
    keywords:
      "conciergerie airbnb prix, auto-gestion airbnb, conciergerie ou pas airbnb, comparatif conciergerie bretagne, gérer soi-même location airbnb",
    relatedCities: [],
    content: `
## L'auto-gestion : combien ça coûte VRAIMENT

Beaucoup de propriétaires pensent économiser en gérant eux-mêmes. Voici le calcul honnête sur la base d'un bien en location courte durée à Saint-Malo, **180 nuits réservées par an** :

### Coûts cachés de l'auto-gestion

| Poste | Temps annuel | Coût réel |
|---|---|---|
| Communication voyageurs (avant, pendant, après) | 60 h | 1 800 € (à 30 €/h) |
| Check-in/out (45 min × 60 séjours) | 45 h | 1 350 € |
| Ménage (3 h × 60 séjours) | 180 h | 5 400 € (vous ou prestataire 30 €/h) |
| Gestion linge | 30 h | 900 € |
| Pricing manuel quotidien | 25 h | 750 € |
| Photos refonte tous les 3 ans | 600 € amorti | 200 €/an |
| Plateformes (Airbnb seul, sans Booking/Vrbo) | revenus -25 % | -8 000 €/an |
| Maintenance / urgences nuit | 10 h | 300 € |
| **Total temps + manque à gagner** | **~350 h/an** | **~18 700 €** |

### Coût réel d'une conciergerie complète

| Poste | Coût annuel |
|---|---|
| Commission Terre et Mer (15 % de 50 600 € brut) | 7 590 € |
| Photos pro et livret | inclus |
| Multidiffusion (Airbnb + Booking + Vrbo) | inclus |
| Pricing dynamique 24h/24 | inclus |
| Communication voyageurs 24h/24 | inclus |
| Ménage hôtelier (60 séjours × 90 €) | 5 400 € (à charge du voyageur) |
| **Total propriétaire** | **7 590 €** |

### Le verdict en chiffres

Sur **un bien à Saint-Malo qui rapporte 50 600 € brut** :

- **Auto-gestion** : 50 600 − 18 700 = **31 900 € net** (avant commission Airbnb)
- **Avec conciergerie 15 %** : 50 600 − 7 590 = **43 010 € net** (avant commission Airbnb)

➡️ Différence : **+11 110 € par an** ET **+350 h de temps libre**.

## Le break-even point

L'auto-gestion devient comparable à la conciergerie uniquement si :

- Vous habitez à **moins de 5 km** du bien (pas de temps de trajet)
- Vous avez du **temps libre permanent** (retraité, télétravail flexible)
- Vous savez faire du **pricing dynamique** au quotidien
- Vous acceptez de **perdre 25 % de revenus** en n'étant que sur Airbnb

Bref, pour très peu de profils.

## Quand la conciergerie est-elle non-rentable ?

Cas où nous **ne recommandons pas** la conciergerie :

- Bien qui rapporte moins de 12 000 € brut par an (location secondaire occasionnelle)
- Bien dans une zone où nous ne sommes pas (intervention > 30 min)
- Propriétaire qui veut absolument tout contrôler en personne

Dans ce cas, mieux vaut une formule "audit + conseil" ponctuelle.

## Notre engagement

Chez Terre et Mer Conciergerie, **nous refusons les mandats** où nous ne sommes pas convaincus de pouvoir générer **+30 % de revenus minimum**. Vous évaluez avant nous.

[**Demander une étude personnalisée gratuite →**](/contact)
`,
  },

  // =====================================================================
  // 19, Check-in autonome
  // =====================================================================
  {
    slug: "check-in-autonome-vs-physique-airbnb-2026",
    category: "nos-services",
    title: "Check-in autonome ou accueil physique : qu'est-ce qui fonctionne le mieux en 2026 ?",
    excerpt:
      "Pourquoi nous recommandons systématiquement le check-in autonome, comment nous installons les boîtes à clés et travaillons avec des lockers partenaires pour les immeubles qui les refusent.",
    image: "/images/blog/check-in-autonome-vs-physique-airbnb-2026.jpg",
    imageAlt: "Boîte à clés sécurisée à code installée sur une porte d'entrée pour check-in autonome Airbnb",
    author: "L'équipe Terre et Mer",
    date: "2026-05-06",
    readTime: 6,
    keywords:
      "check-in autonome airbnb, boîte à clés airbnb, locker airbnb saint-malo, accueil voyageur autonome, smart lock airbnb bretagne, conciergerie check-in 24h",
    relatedCities: [],
    content: `
## Notre recommandation systématique : le check-in autonome

Après plus de 8 000 séjours organisés sur la Côte d'Émeraude et le Morbihan, notre conclusion est claire : **le check-in autonome est plus performant que l'accueil physique** dans la majorité des cas. Voici pourquoi, et comment nous le mettons en œuvre concrètement chez nos propriétaires.

### Les 4 raisons qui font du check-in autonome le standard 2026

**1. Liberté totale pour le voyageur.** 73 % des voyageurs Airbnb arrivent désormais **après 19 h** (TGV soirée, vols décalés, route chargée en juillet-août). Un check-in physique imposé crée du stress, des appels paniqués, des avis tièdes.

**2. Zéro frottement = avis 5★.** Sur notre portefeuille, les biens en check-in autonome obtiennent en moyenne **0,3 point de note en plus** sur Airbnb (4,9 vs 4,6 en accueil physique forcé).

**3. Sécurité supérieure.** Avec une boîte à clés ou un locker à code unique généré à chaque réservation, **vous gardez le contrôle**. Pas de double de clé qui circule, pas de remise à un voyageur "non vérifié".

**4. Économie pour vous.** L'accueil physique coûte 30-45 € par séjour à votre conciergerie. Multiplié par 60 séjours/an = **1 800 à 2 700 € de marge** que nous ne facturons PAS dans notre formule à 15 %, parce que nous l'optimisons.

## Comment ça se passe concrètement chez Terre et Mer

### Étape 1 : Visite technique du bien

Lors de notre première visite, nous identifions :
- L'**emplacement idéal de la boîte à clés** (boîte aux lettres, façade discrète, garage)
- Les **règles de copropriété** (autorisation ou refus)
- Les **accès digicodes** et leur faisabilité

### Étape 2 : Fourniture & installation incluses

Nous fournissons et installons une **boîte à clés sécurisée à 4 chiffres** (modèle Master Lock ou équivalent, certifié anti-perçage) :
- Code unique à chaque réservation, géré depuis notre logiciel
- Posé sur un mur extérieur discret ou dans la boîte aux lettres
- **Aucun coût supplémentaire** pour vous : c'est inclus dans la formule 15 %.

### Étape 3 : Communication automatisée au voyageur

3 jours avant l'arrivée, le voyageur reçoit :
- L'**adresse exacte** + le **code de la boîte à clés** (généré à la dernière minute pour éviter les fuites)
- Un **plan d'accès** avec photos
- Le numéro **24h/24** de notre équipe en cas de problème
- Le **livret d'accueil digital** (PDF + QR code)

À l'heure du check-in, **97 % des voyageurs entrent sans appeler**. Pour les 3 % restants, notre équipe répond en moyenne en **2 minutes** (permanence de nuit incluse).

## Et si la copropriété refuse les boîtes à clés ?

C'est de plus en plus fréquent en centre-ville (Saint-Malo intra-muros, Dinard plage de l'Écluse, Vannes). Pas de panique : **nous avons un réseau de lockers partenaires** sur les villes où nous opérons.

### Notre réseau de lockers

- **Saint-Malo intra-muros** : 2 lockers à proximité (1 place Chateaubriand, 1 rue de la Vieille Boucherie)
- **Dinard centre** : 1 locker à 200 m de la plage de l'Écluse
- **Cancale port** : 1 locker en partenariat avec un commerce ostréicole
- **Damgan / La Roche-Bernard** : 1 locker centre, 1 au port de plaisance

### Comment ça marche

1. Vous nous remettez 2 jeux de clés à notre bureau
2. Nous déposons un jeu dans le locker partenaire avant chaque arrivée
3. Le voyageur récupère les clés via un code SMS unique (pas de notre application : juste un SMS lisible par tous)
4. Au départ, restitution dans le même locker → nous récupérons

Pour le voyageur, c'est aussi simple qu'une consigne SNCF. Pour vous, c'est invisible.

## Pour quels biens recommandons-nous QUAND MÊME un accueil physique ?

L'accueil physique reste pertinent dans 3 cas précis :

- **Bien à plus de 800 €/nuit** (clientèle ultra-premium qui s'attend à un accueil personnalisé)
- **Première location d'un nouveau bien** (les 5-10 premiers séjours, pour ajuster le livret d'accueil sur la base des questions posées)
- **Logements complexes** (gîte avec dépendance, château avec parc, biens nécessitant une démonstration matérielle)

Dans ces cas, nous proposons un **check-in physique optionnel** à 35 € (refacturé au voyageur ou au propriétaire).

## En résumé

✅ **Check-in autonome = standard chez Terre et Mer**, pour la majorité des biens
✅ **Boîte à clés fournie et installée** sans surcoût
✅ **Lockers partenaires** quand la copropriété refuse
✅ **Permanence 24h/24, 7j/7** pour les 3 % d'imprévus
✅ **Toujours possible** de bascule en check-in physique pour les cas premium

[**Discuter du check-in adapté à votre bien →**](/contact)
[**Découvrir tous nos services →**](/services)
`,
  },

  // =====================================================================
  // 20, Rentabilité brute / nette / cashflow Saint-Malo
  // =====================================================================
  {
    slug: "rentabilite-brute-nette-cashflow-airbnb-saint-malo",
    category: "rentabilite",
    title: "Rentabilité brute, nette, cashflow : lire les vrais chiffres d'un Airbnb à Saint-Malo",
    excerpt:
      "Tout investisseur entend ces termes, peu les maîtrisent. On décortique les 3 indicateurs essentiels avec des cas concrets sur le marché malouin.",
    image: "/images/blog/rentabilite-brute-nette-cashflow-airbnb-saint-malo.jpg",
    imageAlt: "Calculatrice et plans immobiliers sur bureau",
    author: "L'équipe Terre et Mer",
    date: "2026-05-04",
    readTime: 9,
    keywords:
      "rentabilité airbnb saint-malo, cashflow location courte durée, rendement net airbnb bretagne, calcul rentabilité airbnb, investir airbnb saint-malo rendement",
    relatedCities: ["saint-malo"],
    content: `
## Pourquoi 80 % des investisseurs se trompent dans leurs calculs

Quand un agent immobilier vous parle de "8 % de rendement", il évoque presque toujours la **rentabilité brute**, l'indicateur le plus flatteur, mais aussi le moins révélateur. Pour décider intelligemment d'un investissement Airbnb à Saint-Malo, il faut maîtriser **3 indicateurs distincts**. Voici lesquels, et comment nous les calculons pour nos propriétaires.

## 1. La rentabilité brute : le chiffre vendeur

**Formule** :
> (Loyers annuels bruts ÷ Prix d'achat total) × 100

Cas concret : appartement T2 vue mer à Paramé, 280 000 € frais inclus, qui rapporte 32 000 € brut/an sur Airbnb.

→ Rentabilité brute = (32 000 ÷ 280 000) × 100 = **11,4 %**

**À retenir** : c'est l'indicateur **le plus optimiste**. Il ignore TOUTES les charges. Utile uniquement pour comparer rapidement deux biens entre eux. Jamais suffisant pour décider.

## 2. La rentabilité nette : le chiffre réaliste

**Formule** :
> ((Loyers annuels − Toutes les charges) ÷ Prix d'achat total) × 100

Voici les charges à déduire **systématiquement** sur le marché malouin (chiffres 2026) :

| Poste | Coût annuel typique (T2 280 000 €) |
|---|---|
| Commission Airbnb (3 %) | 960 € |
| Commission conciergerie Terre et Mer (15 % TTC) | 4 800 € |
| Taxe foncière | 1 200 € |
| Charges copropriété | 1 800 € |
| Assurance PNO + dommages | 380 € |
| Énergie + Wi-Fi | 1 600 € |
| Maintenance/entretien (provision 3 %) | 960 € |
| **Total charges** | **11 700 €** |

→ Loyers nets = 32 000 − 11 700 = **20 300 €**
→ Rentabilité nette = (20 300 ÷ 280 000) × 100 = **7,2 %**

**À retenir** : c'est le vrai indicateur de performance opérationnelle. Au-dessus de 6 % à Saint-Malo, vous êtes sur un excellent investissement.

## 3. Le cashflow : le chiffre qui paye votre crédit

C'est l'indicateur **le plus important** quand vous achetez à crédit. Il répond à la question : "Combien d'argent reste-t-il dans ma poche chaque mois après TOUS les paiements ?"

**Formule** :
> Loyers nets − Mensualités de crédit − Impôts = Cashflow

### Cas type : achat 280 000 €, apport 30 000 €, crédit 250 000 € sur 20 ans à 4,2 %

| Poste | Annuel |
|---|---|
| Loyers nets après charges | +20 300 € |
| Mensualités crédit (1 540 €/mois) | -18 480 € |
| Impôts LMNP régime réel (estimation) | 0 € (amortissement) |
| **Cashflow annuel** | **+1 820 €** |
| **Cashflow mensuel** | **+152 €** |

➡️ **Cashflow positif de 152 €/mois**. Le bien s'autofinance ET vous rapporte. À l'horizon 20 ans (fin du crédit), vous aurez un actif de 280 000 € quasi gratuit + un revenu net de ~20 300 €/an.

### Le piège des cashflows négatifs

Beaucoup d'investisseurs achètent en cashflow négatif (-200 à -400 €/mois) en pariant sur la plus-value. Sur Saint-Malo, c'est risqué : les prix ont peu progressé en 2024-2025. Préférez un cashflow positif **dès la première année**.

## Comment Terre et Mer optimise vos 3 indicateurs

### Sur la rentabilité brute (+18 à 28 %)
- Multidiffusion Airbnb + Booking + Vrbo
- Pricing dynamique calé sur événements locaux
- Photos professionnelles, refonte annonce
- Réponse 24h/24 (Superhost garanti)

### Sur la rentabilité nette (-15 à 25 % de charges)
- Maintenance préventive (notre équipe à 15 €/h, contre 30 €/h en prestation externe)
- Négociation linge hôtelier (10-15 % moins cher que le tarif particulier)
- Optimisation taxe séjour via classement étoiles

### Sur le cashflow (+30 % de revenus + 0 € fiscal les 8 premières années)
- Accompagnement vers comptable LMNP régime réel
- Amortissement comptable du bien
- Anticipation du seuil 23 000 € (URSSAF)

## Notre étude personnalisée gratuite

Avant tout achat, nous établissons une étude **chiffrée et auditable** :

1. Analyse des 12 derniers mois du marché Airbnb dans un rayon de 500 m
2. Calcul des 3 rentabilités (brute, nette, cashflow) sur 5 hypothèses (basse, médiane, haute)
3. Recommandations pour optimiser chaque levier
4. Plan de financement type avec votre courtier (ou le nôtre)

**100 % gratuit, sans engagement, 100 % chiffré.**

[**Demander une étude personnalisée pour mon projet Saint-Malo →**](/contact)
[**Voir notre offre Saint-Malo →**](/villes/saint-malo)
`,
  },

  // =====================================================================
  // 21, Une bonne conciergerie au quotidien
  // =====================================================================
  {
    slug: "que-fait-vraiment-bonne-conciergerie-airbnb-quotidien",
    category: "nos-services",
    title: "Que fait vraiment une bonne conciergerie Airbnb au quotidien ?",
    excerpt:
      "On lève le voile sur tout ce que nous faisons concrètement pour vous chaque jour : journée type, équipes mobilisées, outils utilisés. Transparence totale.",
    image: "/images/blog/que-fait-vraiment-bonne-conciergerie-airbnb-quotidien.jpg",
    imageAlt: "Équipe Terre et Mer Conciergerie en intervention",
    author: "L'équipe Terre et Mer",
    date: "2026-05-02",
    readTime: 8,
    keywords:
      "que fait conciergerie airbnb, journée conciergerie airbnb bretagne, services conciergerie airbnb saint-malo, équipe conciergerie 24h, transparence conciergerie airbnb",
    relatedCities: [],
    content: `
## Le mystère levé : voici ce que nous faisons VRAIMENT pour vous

Beaucoup de propriétaires nous disent : "Je veux bien payer 15 % de commission, mais je veux comprendre ce que vous faites concrètement". Légitime. Voici la **journée type** de notre équipe Terre et Mer Conciergerie sur la Côte d'Émeraude et le Morbihan, **heure par heure**.

## 6h30, Permanence de nuit & relève du matin

Notre **équipe permanence** finit son quart de nuit. Elle a traité (cette nuit, 5 mai 2026) :

- 14 messages voyageurs sur Airbnb (questions wifi, code, restaurants, urgences)
- 1 problème de coupure d'eau à Cancale → intervention à 23h45 par notre City Manager
- 2 demandes de modification de réservation
- 0 plainte voisinage (heureusement)

Tout est consigné dans notre logiciel (ICS/Smoobu) et l'équipe du jour reprend le relais à 7h.

## 7h00, Briefing quotidien & analyse pricing

Notre **équipe pricing** analyse :
- Taux d'occupation J+30, J+60, J+90 sur les 70 logements du portefeuille
- Recommandations d'ajustement tarifaire (en moyenne, **30-40 ajustements/jour** sur le portefeuille)
- Concurrence des biens similaires dans un rayon de 500 m

Sur votre bien spécifiquement, le tarif peut varier de ±15 € entre 7h et 11h selon la demande.

## 8h00, Coordination ménage

Une **équipe de 6 personnes de ménage attitrées** (formées à nos standards hôteliers) reçoit son planning du jour :

- Liste des départs (check-out 11h)
- Liste des arrivées (check-in 16h)
- Bien spécifique : vérifier le robinet de la cuisine signalé hier

Nos personnes de ménage **changent peu** : un même intervenant connaît votre bien sur le long terme. C'est un point de qualité que nous garantissons.

## 9h00-12h00, Tournée matinée : 12 ménages en simultané

Notre équipe terrain enchaîne les ménages :

1. État des lieux **photos avant/après** (système Spotcheck)
2. Vérification du bon fonctionnement (chauffe-eau, wifi, éclairage)
3. Réapprovisionnement kit d'accueil (savon, shampoing, papier toilette, capsules café)
4. Lavage et repassage du linge hôtelier
5. Lit fait au cordeau, salle de bain hôtelière
6. Touche bretonne : galettes Saint-Michel + carte de bienvenue manuscrite

Chaque ménage prend en moyenne **2h45 pour un T2** et **4h pour une maison familiale 6 personnes**.

## 11h00, Communication départs

Notre **équipe communication** envoie automatiquement :
- Message remerciement aux voyageurs partants
- Demande d'avis Airbnb (envoyée 4h après le départ, taux de retour 68 %)
- Si dégradation : ouverture immédiate du dossier AirCover

## 13h00-15h00, Vérifications maintenance

Notre **City Manager** passe sur les biens "à risque" :
- Logement vide depuis 3 jours en mode hivernation : vérification chauffage
- Bien avec spa : contrôle PH et chlore
- Demandes spécifiques propriétaires (ex : "passez voir si le cerisier perd ses feuilles dans la piscine")

## 14h00, Briefing après-midi avec l'équipe pricing

L'**équipe pricing** affine les prix sur les 30 prochains jours, en croisant :
- Météo (prévue à J+10)
- Événements locaux (festival, régates, vacances scolaires)
- Concurrence
- Historique 24 mois sur le bien

## 15h00-19h00, Tournée check-in & assistance arrivée

Pour les **3 % de check-in physiques** demandés (biens premium, premières locations) :
- Accueil personnalisé sur site (35-45 min)
- Démonstration équipements
- Remise du livret d'accueil papier
- Photo "vous êtes bien arrivé" pour rassurer le propriétaire

Pour les **97 % en autonome** : suivi temps réel des arrivées via notre logiciel + relance si pas d'entrée détectée à H+1.

## 19h00, Reporting propriétaires hebdomadaire

Tous les vendredis, vous recevez par email :
- Réservations confirmées de la semaine
- Revenu généré
- Avis voyageurs
- Photos avant/après ménage
- Alertes maintenance s'il y en a

## 20h00, Bascule permanence de nuit

Notre équipe nuit prend le relais pour :
- Répondre aux messages voyageurs (en moyenne 6 messages traités/nuit)
- Gérer les urgences (panne de chauffage, voyageur enfermé dehors, etc.)
- Surveiller les capteurs sonores (détection fête)

## La conciergerie qui dort, on connaît : ce n'est pas nous

Beaucoup de "conciergeries" sont en réalité des **personnes seules** qui ferment leur ligne à 19h le vendredi. Quand un voyageur arrive samedi matin sans pouvoir entrer, c'est vous qui recevez l'appel paniqué… et perdez l'avis 5★.

Chez Terre et Mer Conciergerie, **nous sommes 14 personnes** sur le terrain (Côte d'Émeraude + Morbihan), avec une organisation pensée pour la continuité 24h/24.

## Et tout ça pour 15 % TTC

Notre commission unique de **15 % TTC sans frais cachés** couvre TOUT ce que vous venez de lire. C'est pourquoi nous tenons à la transparence : vous savez exactement ce que vous payez et ce que vous recevez.

[**Discuter avec un membre de notre équipe →**](/contact)
[**Découvrir tous nos services →**](/services)
[**Pourquoi nous vs les autres →**](/pourquoi-nous)
`,
  },

  // =====================================================================
  // 22, 15 % vs 20 %
  // =====================================================================
  {
    slug: "commission-conciergerie-15-vs-20-pourcent-airbnb",
    category: "nos-services",
    title: "Pourquoi notre commission à 15 % rapporte plus que les concurrents à 20-25 %",
    excerpt:
      "Mathématiques transparentes : on compare le revenu net réel d'un propriétaire chez nous vs chez les conciergeries à 20 ou 25 %. Surprise garantie.",
    image: "/images/blog/commission-conciergerie-15-vs-20-pourcent-airbnb.jpg",
    imageAlt: "Calcul de commission conciergerie Airbnb",
    author: "L'équipe Terre et Mer",
    date: "2026-04-30",
    readTime: 6,
    keywords:
      "commission conciergerie airbnb, conciergerie 15% bretagne, comparatif conciergerie airbnb prix, terre et mer conciergerie tarif, commission 20% airbnb",
    relatedCities: [],
    content: `
## Le mythe du "moins de commission = plus rentable"

Sur le marché de la conciergerie Airbnb en Bretagne, les commissions varient de **15 % à 28 %**. Beaucoup pensent qu'une commission basse n'est pas viable et qu'une commission haute "se justifie par la qualité". Faux.

Voici les chiffres réels, avec un cas concret.

## Cas type : maison 6 personnes à Saint-Cast, 30 000 € brut/an

### Scénario A : Conciergerie nationale à 22 % TTC (sans pricing dynamique avancé)

| Poste | Montant |
|---|---|
| Brut Airbnb (sans optimisation forte) | 30 000 € |
| Commission 22 % | -6 600 € |
| Frais cachés (set-up, photos, linge) | -800 € |
| **Net propriétaire** | **22 600 €** |

### Scénario B : Conciergerie locale à 28 % TTC (modèle "premium")

| Poste | Montant |
|---|---|
| Brut Airbnb | 30 000 € |
| Commission 28 % | -8 400 € |
| Frais cachés (variables) | -400 € |
| **Net propriétaire** | **21 200 €** |

### Scénario C : Terre et Mer Conciergerie à 15 % TTC (notre modèle)

| Poste | Montant |
|---|---|
| Brut Airbnb (avec multidiffusion + pricing dynamique) | **38 500 €** (+28 %) |
| Commission 15 % | -5 775 € |
| Frais cachés | **0 €** (vraiment) |
| **Net propriétaire** | **32 725 €** |

➡️ **+10 125 € par an de plus que la conciergerie à 22 %.**
➡️ **+11 525 € par an de plus que la conciergerie à 28 %.**

## Pourquoi notre modèle à 15 % est viable (et plus rentable pour vous)

### 1. Notre revenu = votre revenu

Une conciergerie à 22 % a intérêt à louer **n'importe comment** pour toucher sa commission. Une conciergerie à 15 % a un modèle plus tendu : nous devons **maximiser vos revenus** pour que la commission absolue soit suffisante.

Mécaniquement, **nous travaillons plus dur** pour optimiser votre bien : multidiffusion, pricing dynamique, photos pro, communication 24h/24.

### 2. Volume + processus = baisse de coûts

Avec **200+ biens** pilotés, nous mutualisons :
- Achats linge hôtelier en gros (-15 % vs particulier)
- Équipes de ménage attitrées (gain de productivité)
- Outils logiciels mutualisés (Smoobu, PriceLabs, Spotcheck)
- Photographe interne sur la Côte d'Émeraude

Cette efficacité opérationnelle nous permet de tenir 15 % **sans rogner sur la qualité**.

### 3. Aucun frais caché

C'est notre engagement écrit dans le contrat :
- Pas de frais d'installation
- Pas de frais photos (inclus)
- Pas de frais de set-up annonce
- Pas de frais de mise à jour
- Pas de frais d'AirCover
- Pas de frais de gestion administrative
- Pas de frais "premium" en haute saison

Le ménage est facturé **au voyageur** (jamais à vous). La maintenance ponctuelle est facturée **à 15 €/h TTC** (ou 30 €/h si prestataire externe), avec votre validation préalable au-delà de 100 €.

## "Et si je veux comparer un devis ?"

C'est encouragé. Demandez à 2-3 conciergeries un **devis détaillé écrit** précisant :

1. La commission TTC (et pas HT pour brouiller)
2. La liste **exhaustive** des frais inclus / non inclus
3. Le coût des photos pro (souvent 400-600 € chez les concurrents)
4. Le coût du **set-up initial** (parfois 200-500 € chez les concurrents)
5. Les **engagements de durée** (12-24 mois ailleurs, 90 jours renouvelables chez nous)

Mettez les 3 devis face à face. Refaites le calcul "net propriétaire" avec un brut estimé identique. La transparence fait la différence.

## Notre engagement écrit

Chez Terre et Mer Conciergerie :

✅ **15 % TTC unique**, sans frais cachés
✅ **Contrat de 90 jours renouvelable** (vous gardez la main)
✅ **Calendrier bloquable à volonté** depuis votre espace propriétaire
✅ **Versement direct** sur votre compte par Airbnb (pas de "porteur de fonds" intermédiaire)
✅ **Aucune clause d'exclusivité étendue**

[**Demander un devis transparent →**](/contact)
[**Voir notre comparatif Pourquoi nous vs les autres →**](/pourquoi-nous)
`,
  },

  // =====================================================================
  // 23, Multidiffusion Airbnb Booking Vrbo
  // =====================================================================
  {
    slug: "multidiffusion-airbnb-booking-vrbo-pourquoi-crucial",
    category: "nos-services",
    title: "Multidiffusion Airbnb + Booking + Vrbo : pourquoi c'est essentiel (et risqué seul)",
    excerpt:
      "Diffuser sur 3 plateformes augmente vos revenus de 25 à 35 %. Mais sans channel manager, vous risquez les surbookings. Explication.",
    image: "/images/blog/multidiffusion-airbnb-booking-vrbo-pourquoi-crucial.jpg",
    imageAlt: "Multidiffusion Airbnb Booking Vrbo : channel manager qui synchronise les calendriers vs surbookings sans channel manager",
    author: "L'équipe Terre et Mer",
    date: "2026-04-28",
    readTime: 7,
    keywords:
      "multidiffusion airbnb booking, channel manager airbnb, diffuser annonce booking vrbo, airbnb seul rentabilité, surbooking airbnb éviter",
    relatedCities: [],
    content: `
## Pourquoi être uniquement sur Airbnb vous fait perdre 25 à 35 % de revenus

Sur le marché 2026, **63 % des voyageurs réservent sur Airbnb**, **24 % sur Booking.com**, **8 % sur Vrbo**, **5 % sur d'autres plateformes**. En étant uniquement sur Airbnb, vous **renoncez à 37 % du marché**.

Sur la Côte d'Émeraude et le Morbihan, la répartition est encore plus parlante :
- **Saint-Malo / Dinard** : forte clientèle internationale qui préfère Booking (Anglais, Allemands, Belges)
- **Cancale / Saint-Cast** : équilibre Airbnb-Booking
- **Damgan / La Roche-Bernard** : Airbnb dominant, mais Vrbo monte fort sur les maisons familiales

## Les chiffres réels d'un bien multidiffusé vs Airbnb-seul

Cas concret : T3 vue mer à Dinard, 6 personnes.

### Scénario A : Airbnb seul

| Poste | Annuel |
|---|---|
| Réservations Airbnb | 158 nuits @ 145 € | 22 910 € |
| **Total brut** | **22 910 €** |

### Scénario B : Airbnb + Booking + Vrbo (multidiffusion Terre et Mer)

| Poste | Annuel |
|---|---|
| Réservations Airbnb | 145 nuits @ 152 € | 22 040 € |
| Réservations Booking | 52 nuits @ 168 € | 8 736 € |
| Réservations Vrbo | 18 nuits @ 175 € | 3 150 € |
| **Total brut** | **33 926 €** |

➡️ **+11 016 € par an** soit **+48 % de revenus**, sans rien changer au bien.

Pourquoi cette différence ?
- Un voyageur qui ne trouve pas votre bien sur sa plateforme préférée passe au suivant
- Booking attire des séjours plus courts mais à meilleur prix unitaire
- Vrbo capte les **familles 4-8 personnes** prêtes à payer cher pour des séjours longs

## Le piège du multidiffuseur DIY : le surbooking

Si vous gérez vous-même 3 plateformes, le risque #1 est le **double booking** : 2 voyageurs réservent le même créneau, l'un sur Airbnb, l'autre sur Booking, à 30 secondes d'intervalle.

**Conséquences** :
- Annulation forcée d'un voyageur (très mal vu, pénalité Airbnb -50 places de classement pendant 30 jours)
- Remboursement + indemnité (jusqu'à 800 €)
- Avis 1★ quasi garanti
- Perte du statut Superhost

## La solution : un channel manager professionnel

Un **channel manager** est un logiciel qui synchronise les calendriers Airbnb, Booking, Vrbo, Tripadvisor en temps réel (toutes les 5 minutes). Dès qu'une réservation tombe sur l'une, les autres sont **bloquées automatiquement**.

Les meilleurs en 2026 : Smoobu, Hospitable, Hostfully, Lodgify, Beds24.

**Coût** : 25 à 60 €/mois en abonnement individuel.

### Ce que nous faisons chez Terre et Mer

- Notre channel manager (Smoobu Pro) est **inclus dans la formule 15 %**, vous ne payez rien.
- Synchronisation toutes les **3 minutes** sur 4 plateformes simultanées
- Tarification différenciée par plateforme (les voyageurs Booking acceptent des prix +12 à 18 % plus élevés que Airbnb pour le même bien)
- Description et photos optimisées par plateforme (Airbnb favorise les titres courts, Booking les longs descriptifs)

## Optimisation par plateforme

### Airbnb : le hero pour le 1er séjour

- 18-25 photos
- Titre 50 caractères max avec USP
- Description 4 paragraphes
- Pricing dynamique au quotidien

### Booking : le pro de l'international

- Description longue détaillée (>500 mots)
- Police "Détails de la chambre" très précise
- Politique d'annulation flexible recommandée
- Pricing premium (+12-18 %) car la clientèle accepte mieux

### Vrbo : la plateforme famille

- Mise en avant des couchages enfants
- Photos extérieures (jardin, terrasse, plage à proximité)
- Descriptifs orientés "vacances en famille"
- Séjours minimum 7 nuits idéal

## En résumé

Multidiffusion = **+25 à 48 %** de revenus selon le bien et la zone. C'est mathématiquement la première optimisation à faire.

DIY multidiffusion = risque réel de surbooking et de pénalité algorithmique.

➡️ **Inclus de série dans notre formule à 15 %**, sans aucun frais supplémentaire.

[**Discuter de la diffusion de votre bien →**](/contact)
[**Voir nos services détaillés →**](/services)
`,
  },

  // =====================================================================
  // 24, Permanence de nuit
  // =====================================================================
  {
    slug: "permanence-nuit-conciergerie-airbnb-pourquoi-essentiel",
    category: "nos-services",
    title: "Permanence de nuit 24h/24 : pourquoi c'est crucial (et rare)",
    excerpt:
      "Voyageur enfermé dehors à 23h, panne de chauffage en plein hiver, plainte voisin à minuit : ce qui se passe quand votre conciergerie dort.",
    image: "/images/blog/permanence-nuit-conciergerie-airbnb-pourquoi-essentiel.jpg",
    imageAlt: "Permanence de nuit Terre et Mer Conciergerie",
    author: "L'équipe Terre et Mer",
    date: "2026-04-26",
    readTime: 6,
    keywords:
      "permanence nuit conciergerie airbnb, urgence airbnb 24h, conciergerie 7j7 saint-malo, voyageur urgence airbnb bretagne",
    relatedCities: [],
    content: `
## La conciergerie est un métier 24h/24, vraiment

Beaucoup de "conciergeries" n'assument pas la nuit. Elles ferment leur ligne entre 19h et 9h. Et pourtant, **31 % des incidents Airbnb se produisent entre 20h et 6h du matin**. Voici pourquoi ça matte, et ce que nous faisons concrètement.

## Top 5 des urgences nocturnes que nous traitons chaque mois

### 1. "Je n'arrive pas à entrer" (le plus fréquent)

Cause : code boîte à clés mal lu, problème de digicode, voyageur en retard qui se trompe de pied d'immeuble.

**Notre intervention** :
- Vérification immédiate du code dans notre logiciel
- Renvoi du code par SMS (avec photo de la boîte à clés en bonus)
- Si nécessaire, **City Manager sur place en moins de 30 minutes** sur Saint-Malo, Dinard, Cancale, Saint-Cast, Damgan

### 2. Panne d'électricité ou de chauffage en hiver

Très fréquent en Bretagne (vent + humidité = disjonctions). À 22h en février, un voyageur dans le noir et le froid laisse un avis 1★ garanti.

**Notre intervention** :
- Diagnostic à distance (téléphone) en 5 min
- Coupe-circuit / réarmement guidé par téléphone
- Si pas de résolution : intervention physique sous 1 h
- En dernier recours : relogement chez un partenaire à nos frais

### 3. Fuite d'eau soudaine

Tuyau gelé, joint lavabo, trop-plein wc.

**Notre intervention** :
- Coupure d'arrivée d'eau à distance (instructions au voyageur)
- Plombier de garde sur place sous 1h-2h (Plombérie 24h/24 Saint-Malo, partenaire historique)
- Photos avant/après pour AirCover

### 4. Plainte voisinage / nuisances sonores

Détectées via nos **capteurs Minut** (dB sans enregistrement vocal) ou via appel d'un voisin.

**Notre intervention** :
- Appel immédiat au voyageur principal
- Rappel des règles + visite physique si pas de baisse en 30 min
- En cas de récidive : check-out forcé avec remboursement partiel

### 5. Voyageur perdu, malade ou en détresse

Touriste qui ne trouve pas le restaurant, voyageur qui a oublié ses médicaments, accident domestique.

**Notre intervention** :
- Conseil orientation sur les pharmacies/médecins de garde de la zone
- Si urgence vitale : appel 15 directement
- Si médical : appel à notre médecin partenaire (Saint-Malo)

## Comment nous tenons une vraie permanence 24h/24

### Une équipe dédiée nuit

3 personnes se relaient sur les nuits :
- 19h-2h
- 2h-9h
- Astreinte téléphonique permanente

Toutes les 3 sont formées sur **les 70 logements pas plus** du portefeuille (procédures spécifiques par bien). Ce volume volontairement limité est notre engagement : nous refusons la logique d'usine pour préserver la qualité de service sur chaque bien.

### Un logiciel commun

Toutes les informations sur votre bien sont consultables instantanément :
- Codes accès, plans, contacts urgence
- Historique des problèmes signalés
- Numéros voisins, copropriété, syndic
- Coordonnées du propriétaire (si vous voulez être prévenu)

### Un réseau de partenaires de garde

- Plombier 24h/24 (Saint-Malo + Vannes)
- Électricien 24h/24 (Pleurtuit + La Roche-Bernard)
- Serrurier 24h/24 (3 sur la Côte d'Émeraude, 2 dans le Morbihan)
- Médecin partenaire (Saint-Malo)
- Hôtel partenaire pour relogement d'urgence (3★ Dinard, 4★ Saint-Malo)

## "Vous me prévenez la nuit ?"

À votre choix lors de la signature du contrat :

- **Option A : Silence radio.** Nous gérons tout sans vous réveiller. Vous recevez un récap dans le briefing du vendredi.
- **Option B : Notification au-delà de 200 €.** Vous êtes prévenu uniquement si l'incident dépasse 200 € de coût ou implique une décision (relogement, indemnisation, etc.).
- **Option C : Notification systématique.** Vous êtes prévenu de tout, en temps réel.

90 % de nos propriétaires choisissent **l'option A**. Ils dorment, on bosse.

## Pourquoi 80 % des conciergeries ne le font pas

La permanence de nuit coûte cher (~24 000 €/an juste en salaires d'équipe). Beaucoup de conciergeries préfèrent fermer le téléphone et perdre quelques avis.

Chez Terre et Mer Conciergerie, nous considérons que **vous payez 15 % pour avoir la paix, pas pour devoir gérer votre bien à 23h45**. C'est non-négociable.

[**Discuter de votre bien avec un humain réel →**](/contact)
[**Voir tous nos engagements →**](/engagements)
`,
  },

  // =====================================================================
  // 25, Conciergerie locale vs nationale
  // =====================================================================
  {
    slug: "conciergerie-locale-vs-nationale-airbnb-bretagne",
    category: "nos-services",
    title: "Conciergerie locale ou conciergerie nationale : que choisir en Bretagne ?",
    excerpt:
      "Les grands acteurs nationaux (BNB Lord, GuestReady, HostnFly) ont leur place. Mais sur la Côte d'Émeraude et le Morbihan, voici pourquoi le local gagne.",
    image: "/images/blog/conciergerie-locale-vs-nationale-airbnb-bretagne.jpg",
    imageAlt: "Équipe locale Terre et Mer en Bretagne",
    author: "L'équipe Terre et Mer",
    date: "2026-04-24",
    readTime: 7,
    keywords:
      "conciergerie locale bretagne, conciergerie nationale vs locale, bnb lord guestready hostnfly comparatif, conciergerie airbnb saint-malo locale",
    relatedCities: [],
    content: `
## Le match David vs Goliath dans la conciergerie Airbnb

Sur le marché breton, vous avez deux options principales :

- **Les conciergeries nationales** : BNB Lord, GuestReady, HostnFly, Welkeys, Wishbnb. Elles couvrent toute la France via des "City Managers" indépendants.
- **Les conciergeries locales** : Terre et Mer Conciergerie et quelques autres acteurs régionaux. Équipes salariées, ancrage géographique, mutualisation locale.

Voici **6 critères concrets** pour comparer.

## 1. Délai d'intervention en cas d'urgence

| Type | Engagement annoncé | Réalité observée 2024-2025 |
|---|---|---|
| Conciergerie nationale | "Sous 2 heures" | 2h45 en moyenne (City Manager indépendant souvent à plusieurs km) |
| Terre et Mer Conciergerie | **Sous 30 minutes** | **22 minutes en moyenne** (équipe à Pleurtuit, Saint-Malo, Ploubalay, La Roche-Bernard) |

➡️ **Avantage local** : nos City Managers vivent dans leur secteur. Le bureau le plus proche d'un bien Saint-Malo intra-muros est à **2 km**.

## 2. Connaissance des événements locaux

- **Conciergerie nationale** : pricing dynamique générique (algorithme + saisons grandes mailles)
- **Terre et Mer Conciergerie** : pricing événementiel manuel calé sur :
  - Route du Rhum (Saint-Malo, tous les 4 ans)
  - Festival du film britannique (Dinard, octobre)
  - Fête des Remparts (Dinan, juillet)
  - Régates de Saint-Cast (juillet-août)
  - Semaines du Golfe (Damgan, années impaires)
  - Marées d'équinoxe (toute la côte, mars + septembre)

➡️ **Avantage local** : ces événements représentent jusqu'à **15-25 % du revenu annuel**. Une conciergerie nationale les rate.

## 3. Réseau de prestataires locaux

| Prestation | Conciergerie nationale | Terre et Mer Conciergerie |
|---|---|---|
| Plombier urgence nuit | Annuaire générique | Partenaire historique 24h/24, prix négocié |
| Linge hôtelier | Pressing à distance | Blanchisserie partenaire Pleurtuit (J+1) |
| Photographe | Freelance interchangeable | 2 photographes attitrés (CO + Morbihan) |
| Personne de ménage | Tournante (parfois 5-10 par an) | **Attitrée** au bien, formation interne |

➡️ **Avantage local** : continuité, qualité constante, négociation de tarifs avantageux.

## 4. Adaptation au tissu local

- **Conciergerie nationale** : annonces standardisées (mêmes templates France entière)
- **Terre et Mer Conciergerie** : annonces **adaptées au territoire**
  - Bilingue français/anglais sur Dinard / Saint-Briac (clientèle UK)
  - Storytelling "huîtres de Pénerf" sur Damgan
  - Storytelling "remparts médiévaux" sur Dinan
  - Mention spécifique "Plus belles plages d'Europe" sur Saint-Cast

➡️ **Avantage local** : taux de conversion +15 à 25 % grâce à un copy adapté.

## 5. Relation propriétaire

| Critère | Conciergerie nationale | Terre et Mer Conciergerie |
|---|---|---|
| Interlocuteur | Service client centralisé (rotatif) | **City Manager attitré** + dirigeant joignable |
| RDV physique | Difficile (City Manager surchargé) | **Possible** sur demande, sans frais |
| Connaissance du bien | Variable selon turnover | **Mémoire d'équipe** (200 biens connus à fond) |
| Reporting | Automatique (template générique) | Hebdo personnalisé + photos avant/après |

## 6. Alignement d'intérêts

- **Conciergerie nationale** : modèle franchisé. Le siège prend 30-40 % de la commission. Le City Manager local touche 60-70 % et est **incité à multiplier les biens** (quantité > qualité).
- **Terre et Mer Conciergerie** : équipe **salariée**, pas de franchise. Notre intérêt = **votre satisfaction long terme** pour conserver le mandat.

## Quand le national peut quand même être pertinent

Soyons honnêtes : la conciergerie nationale a sa place quand :

- Vous avez **plusieurs biens dans plusieurs régions** et préférez un seul interlocuteur à l'échelle nationale
- Vous êtes investisseur multi-villes (Paris + Bretagne + Sud) et acceptez un service plus standardisé
- Le rendement est secondaire vs la simplicité administrative

Pour tout autre cas, **propriétaire avec 1-3 biens en Bretagne**, recherchant performance et accompagnement, le local gagne sur 5-6 critères.

## Notre engagement local depuis 2018

Nous sommes nés à Saint-Malo, étendus sur la Côte d'Émeraude (24 villes), puis sur le Morbihan (6 villes). 14 personnes salariées, des bureaux physiques à **Pleurtuit (35), Ploubalay (22) et La Roche-Bernard (56)**. Vous trouverez toujours un humain à proximité.

[**Rencontrer notre équipe →**](/contact)
[**Pourquoi nous vs les autres →**](/pourquoi-nous)
`,
  },

  // =====================================================================
  // 26, Première saison Airbnb
  // =====================================================================
  {
    slug: "premiere-saison-airbnb-bretagne-comment-demarrer",
    category: "nos-services",
    title: "Démarrer son Airbnb en Bretagne : la checklist des 60 premiers jours",
    excerpt:
      "Vous lancez votre 1ère location courte durée en 2026 ? Voici exactement ce que nous mettons en place les 60 premiers jours pour booster votre démarrage.",
    image: "/images/blog/premiere-saison-airbnb-bretagne-comment-demarrer.jpg",
    imageAlt: "Préparation d'un nouveau bien Airbnb",
    author: "L'équipe Terre et Mer",
    date: "2026-04-22",
    readTime: 7,
    keywords:
      "démarrer airbnb bretagne, première location courte durée, lancer airbnb saint-malo, set-up airbnb conciergerie, premiers mois airbnb",
    relatedCities: [],
    content: `
## Les 60 premiers jours déterminent les 5 prochaines années

Sur Airbnb, **les 10 premières réservations** posent les bases de votre classement algorithmique pour les années à venir. Un mauvais démarrage = des mois à rattraper. Voici exactement ce que notre équipe met en place pour les nouveaux propriétaires qui nous confient leur 1ère location.

## Semaine 1, Audit & visite technique

### Jour 1 : Premier contact (téléphone, 30 min)
- Compréhension de votre projet (résidence secondaire, investissement, héritage)
- Vos contraintes (calendrier d'utilisation perso, budget)
- Vos attentes (max revenu, max tranquillité, équilibre)

### Jour 3-5 : Visite technique sur place (2h)

Notre City Manager passe sur le bien :

- **Inventaire complet** (mobilier, équipements, défauts à corriger)
- **Photos témoin** (avant set-up)
- **Plan d'accès** (digicode, parking, copropriété)
- **Diagnostics** (Wi-Fi débit, eau chaude, isolation acoustique)
- **Discussion linge & ménage** (option draps inclus ou pas)

### Jour 6-7 : Plan d'action écrit

Nous vous remettons un plan personnalisé :
- Travaux ou achats à prévoir (rarement, mais parfois) : ex. literie hôtelière à mettre à niveau
- Budget revenus prévisionnel sur 12 mois
- Date de mise en ligne objectif

## Semaine 2, Set-up & shooting

### Jour 8 : Shooting photo professionnel (4-6h sur place)

- 18-25 photos retouchées
- 1 photo "hero" (la pièce vue mer ou la terrasse)
- 1 photo nuit (atmosphère soirée)
- Photos extérieures (jardin, plage à proximité, port)
- Livraison en 48h

### Jour 10 : Rédaction & traduction de l'annonce

- Titre 50 caractères avec USP
- Description structurée 400-600 mots français
- Traduction professionnelle anglais (clientèle UK sur la Côte d'Émeraude)
- Description optimisée différemment selon Airbnb / Booking / Vrbo

### Jour 12 : Création des comptes plateformes

- Création Airbnb (sous votre nom) → vous gardez la propriété de l'annonce
- Création Booking (commission 15 % côté plateforme + notre commission)
- Création Vrbo si pertinent (familles 6+)
- Synchronisation channel manager Smoobu

### Jour 13 : Numéro d'enregistrement mairie

- Déclaration en mairie obligatoire (Saint-Malo, Dinard, Cancale, etc.)
- Récupération du n° à 13 chiffres
- Insertion automatique sur les annonces

### Jour 14 : Mise en ligne 🚀

- Publication simultanée sur les 3-4 plateformes
- Pricing initial : **prix d'attaque -15 %** vs prix cible pour décrocher les 5 premières réservations rapidement (effet d'amorçage Airbnb)

## Semaine 3-4, Premières réservations

### Surveillance algorithmique 24h/24

Notre équipe pricing surveille :
- Position dans les résultats Airbnb pour les requêtes-clés ("Saint-Malo", "Saint-Malo intra-muros", etc.)
- Taux de clic vs concurrence
- Premiers retours voyageurs

### Premier check-in (semaine 3 typiquement)

- **Le 1er séjour est CRUCIAL** : 1 mauvais avis dans les 5 premiers ruine 6 mois de classement
- Nous mettons les **petits plats dans les grands** : kit d'accueil renforcé, message de bienvenue manuscrit, suivi extra-attentif
- Demande d'avis personnalisée 4h après le départ

## Mois 2, Optimisation continue

### Pricing dynamique en mode "agressif"

Une fois 5 avis 5★ obtenus, on passe en pricing classique optimisé :

- Hausse progressive vs prix d'attaque (+8 % par mois)
- Pricing événementiel automatique
- Synchronisation 4 plateformes

### Adaptation du livret d'accueil

- Sur la base des questions posées par les 5-10 premiers voyageurs, on enrichit le livret
- Fréquence questions : Wi-Fi, plages, restaurants, transport TGV → ajout en page 1

### Premier reporting hebdo

À partir de la semaine 5, vous recevez tous les vendredis :
- Réservations confirmées et leur revenu
- Avis voyageurs collectés
- Photos avant/après ménage
- Recommandations pour les 30 jours à venir

## Le résultat à 60 jours

Sur notre portefeuille (35 nouveaux biens lancés en 2025), à J+60 :

- **Taux d'occupation moyen** : 68 % (vs 35 % moyenne marché pour un bien neuf)
- **Note moyenne Airbnb** : 4,87 / 5
- **Premier revenu net** : encaissé en J+18 (Airbnb verse à J+1 après check-in)
- **Statut Superhost** : obtenu sur les biens à fort volume au bout de **4 mois**

## Combien ça coûte de tout démarrer ?

**0 € de set-up, 0 € de photos, 0 € de mise en ligne.** Tout est inclus dans la formule à 15 % TTC. Vous commencez à payer une commission **uniquement quand vous touchez vos premiers revenus**.

[**Démarrer mon projet Airbnb avec votre équipe →**](/contact)
[**Découvrir tous nos services →**](/services)
`,
  },

  // =====================================================================
  // 31, Mont-Dol / Baie du Mont-Saint-Michel (FÉV 2026)
  // =====================================================================
  {
    slug: "louer-airbnb-mont-dol-baie-mont-saint-michel",
    category: "ville-guide",
    title: "Pourquoi Mont-Dol est une pépite Airbnb confidentielle de la baie du Mont-Saint-Michel",
    excerpt:
      "Panorama 360° sur la baie UNESCO, mégalithes, gare TGV à 5 min : Mont-Dol coche toutes les cases d'un Airbnb rentable hors des sentiers battus. Le guide propriétaire 2026.",
    image: "/images/blog/louer-airbnb-mont-dol-baie-mont-saint-michel.jpg",
    imageAlt: "Mairie de Mont-Dol au pied du piton granitique, drapeau français, ciel bleu, Ille-et-Vilaine",
    author: "L'équipe Terre et Mer",
    date: "2026-02-12",
    readTime: 8,
    keywords:
      "louer airbnb mont-dol, conciergerie mont-dol, prix nuit mont-dol, location courte durée mont-dol, airbnb baie mont-saint-michel, conciergerie baie mont-saint-michel, airbnb dol-de-bretagne, mégalithes mont-dol, marées équinoxe baie",
    relatedCities: ["mont-dol", "dol-de-bretagne"],
    content: `
## Mont-Dol, le belvédère oublié de la baie du Mont-Saint-Michel

À 25 minutes du Mont-Saint-Michel, à 5 minutes de la gare TGV de Dol-de-Bretagne (Paris en 1h55), à 30 minutes de Saint-Malo : Mont-Dol est sans doute **le meilleur point de chute confidentiel** pour visiter la baie classée UNESCO. Et pourtant, sur les 2,5 millions de touristes annuels du Mont, à peine 1 % font le détour par ce piton granitique culminant à **65 mètres**.

C'est précisément cette confidentialité qui en fait une **pépite Airbnb**. Moins de concurrence, des tarifs préservés, une saison étendue d'avril à novembre, et une clientèle haut de gamme prête à payer le prix d'une étape "hors du temps". Voici pourquoi nous misons sur Mont-Dol en 2026, et comment faire performer votre bien.

## Le marché Airbnb de Mont-Dol en chiffres (2026)

- **Prix moyen / nuit** : 72 € (toutes typologies confondues)
- **Voyageurs Airbnb / an** : 14 000+ (en croissance de +18 % depuis 2023)
- **Saison principale** : avril à novembre, avec un second pic en décembre
- **Taux d'occupation moyen** : 68 % (médiane Bretagne : 65 %)
- **Type de bien le plus demandé** : longère ou maison en pierre 4-6 personnes avec vue sur la baie ou jardin clos
- **Clientèle internationale** : 40 % (forte présence britannique, allemande et néerlandaise)

Pour comparer, [Dol-de-Bretagne](/villes/dol-de-bretagne), sa voisine immédiate à 5 minutes, affiche 65 € de prix moyen et 22 000 voyageurs par an. Mont-Dol est donc **moins fréquentée mais mieux valorisée à la nuit** : la rareté paie.

## Les 4 atouts uniques de Mont-Dol pour votre bien

### 1. Le panorama à 360° depuis le sommet (le seul de la baie)

Du sommet du Mont-Dol, vous voyez **simultanément** :
- Le Mont-Saint-Michel à l'est (par temps clair)
- La baie et ses polders au nord
- Les marais de Dol à l'ouest
- Toute l'arrière-pays jusqu'à Combourg au sud

Ce point de vue est unique en Bretagne. **À valoriser dans le titre de l'annonce et dès la première photo** : c'est un argument décisionnel pour 70 % des voyageurs internationaux.

### 2. Les mégalithes et la chapelle Notre-Dame-de-l'Espérance

Le sommet abrite plusieurs mégalithes néolithiques (datés de -4500 av. J.-C.), une chapelle du XIᵉ siècle, un moulin à vent restauré et de nombreuses légendes (combat de Saint-Michel contre le Diable, empreintes du Diable dans le granit). C'est un **terrain de jeu narratif puissant** pour le livret d'accueil, et un signal fort dans l'algorithme Airbnb (les annonces avec storytelling local convertissent +20 %).

### 3. La gare TGV de Dol-de-Bretagne à 5 minutes

C'est l'argument le plus sous-estimé. **Paris en 1h55 sans correspondance**, à 5 minutes en voiture de votre Airbnb. Les escapades week-end Paris-Mont-Saint-Michel passent presque toutes par cet axe. Un voyageur qui peut faire son aller-retour Paris-baie en 1h55 + 5 min Uber, c'est un voyageur **prêt à payer +25 % par rapport à un bien éloigné de la gare**.

### 4. Les grandes marées d'équinoxe (mars + septembre)

Quand le coefficient passe au-dessus de 110, la marée monte autour du Mont-Saint-Michel à la **vitesse d'un cheval au galop** (8 km/h). C'est l'un des spectacles naturels les plus impressionnants d'Europe, et **les hébergements de la baie se vendent ×2 à ×3** sur ces 4 à 6 jours. Notre équipe pricing applique systématiquement +60 à +80 % pendant ces fenêtres. Sans cette stratégie événementielle, vous laissez 600 à 1 000 € sur la table chaque année rien qu'avec ces deux pics.

## Saisonnalité et pricing dynamique : le tableau réel

| Période | Prix moyen | Taux d'occupation | Levier pricing |
|---|---|---|---|
| Janvier - février | 50 € | 30 % | Stratégie séjours longs (télétravail) |
| Mars (équinoxe) | 95 € | 75 % | +60 % sur les 4 jours de grande marée |
| Avril - juin | 75 € | 70 % | Ponts mai (+30 %), Pâques (+25 %) |
| Juillet - août | 115 € | 92 % | Pic absolu, pricing dynamique fin |
| Septembre (équinoxe) | 100 € | 82 % | +80 % sur les 4 jours de grande marée |
| Octobre - novembre | 70 € | 55 % | Toussaint (+20 %), tourisme patrimonial |
| Décembre | 65 € | 45 % | Marchés de Noël Dol + 15 décembre |

**Conclusion** : un T2 bien piloté à Mont-Dol génère **18 000 à 24 000 € de revenus annuels nets** après commission de 15 % TTC. Une longère 6-8 personnes avec vue baie peut dépasser les **38 000 €**.

## Comment faire performer votre bien à Mont-Dol : nos 5 leviers

### 1. Photos premium en lumière dorée

Le sommet du Mont-Dol au lever ou coucher du soleil offre une lumière exceptionnelle (golden hour de 30 minutes). C'est **le shoot signature** que nous réalisons systématiquement pour nos biens du secteur. Les annonces avec photo aérienne ou panoramique reçoivent **+45 % de clics**.

### 2. Annonce 100 % bilingue français / anglais

40 % de votre clientèle sera britannique, américaine, allemande ou néerlandaise. Une annonce mal traduite (ou pas traduite) plafonne à 60 % d'occupation. Une annonce écrite par un rédacteur anglophone natif (notre standard) débloque les **15 à 20 % de revenus supplémentaires** auprès de cette clientèle premium.

### 3. Livret d'accueil orienté baie + Mont-Saint-Michel

Le voyageur de Mont-Dol veut **une expérience baie**, pas seulement un hébergement. Notre livret d'accueil propose :
- Le parcours patrimoine du sommet (mégalithes → chapelle → moulin)
- Le circuit complet baie : Mont-Saint-Michel, Cherrueix (char à voile), polder, Pointe du Grouin
- Les meilleurs spots photo (lever de soleil au sommet, couchers sur le polder, marée galopante)
- Une sélection de restaurants typiques (huîtres de Cancale à 20 min, agneau de pré-salé, crêperie locale)
- Les horaires des grandes marées avec coefficients

Résultat : **note moyenne 4,9/5** sur nos biens du secteur Mont-Dol vs 4,6/5 en moyenne Airbnb Bretagne.

### 4. Pricing événementiel calé sur les marées

Comme évoqué plus haut, les **deux fenêtres de grandes marées (mars + septembre)** sont un pic majeur souvent ignoré par les propriétaires en autonomie. Notre algorithme pricing intègre l'ensemble des coefficients de marée publiés par le SHOM 12 mois à l'avance : nous savons **exactement quels jours appliquer +60 ou +80 %**.

### 5. Une équipe locale à 20 minutes

Notre City Manager basé à Saint-Malo intervient sur Mont-Dol en moins de 20 minutes. Pour les voyageurs internationaux qui louent depuis Londres ou Berlin, savoir qu'il y a un humain joignable 24h/24 à 20 minutes de l'Airbnb est **le critère #1 de confiance**.

## Investir à Mont-Dol en 2026 : opportunités

Le marché immobilier de Mont-Dol reste **30 à 40 % moins cher qu'à Saint-Malo intra-muros** ou Dinard centre. À typologie équivalente :

- **T2 60 m²** : 145 000 à 180 000 €
- **Longère 100 m² avec jardin** : 220 000 à 290 000 €
- **Maison de caractère 140 m² vue baie** : 320 000 à 420 000 €

Avec un revenu Airbnb annuel net de 22 000 à 38 000 €, vous obtenez un **rendement brut de 9 à 13 %**, bien au-dessus de la moyenne Bretagne (6-8 %) et largement supérieur à un placement immobilier classique. Pour les détails fiscaux (LMNP, micro-BIC vs réel), [consultez notre guide LMNP Bretagne 2026](/blog/lmnp-airbnb-bretagne-fiscalite-2026).

## Réglementation : ce qu'il faut savoir avant de louer

Comme toutes les communes du périmètre UNESCO de la baie du Mont-Saint-Michel, Mont-Dol applique **la loi Le Meur de 2024** :

- Déclaration en mairie obligatoire
- Numéro d'enregistrement à 13 chiffres à afficher sur l'annonce
- Taxe de séjour collectée automatiquement par Airbnb / Booking
- Pas de quota actuel ni de limite de jours (situation différente de Saint-Malo)

Notre équipe se charge intégralement de la déclaration en mairie. Délai moyen : 3 à 5 jours ouvrés. [Voir le détail des démarches dans notre guide réglementation 2026](/blog/reglementation-airbnb-saint-malo-dinard-cancale-2026).

## Notre conviction sur Mont-Dol

Mont-Dol n'a ni la notoriété de Saint-Malo, ni la concurrence de Dinard, ni le tarif au mètre carré du Mont-Saint-Michel. C'est précisément ce qui en fait, à nos yeux, **l'une des meilleures opportunités Airbnb 2026 sur la côte nord-bretonne**. Saison étendue, clientèle haut de gamme, événements pricing puissants, gare TGV, patrimoine UNESCO à 25 minutes : tous les ingrédients sont réunis.

Si vous possédez un bien à Mont-Dol, ou si vous envisagez d'en acquérir un, nous serions ravis d'en discuter. Notre City Manager couvre tout le secteur baie du Mont-Saint-Michel et nous proposons une **étude personnalisée gratuite sous 24 h**.

[**Voir notre offre Mont-Dol →**](/villes/mont-dol)
[**Voir notre offre Dol-de-Bretagne →**](/villes/dol-de-bretagne)
[**Demander une étude personnalisée →**](/contact)
`,
  },

  // =====================================================================
  // 32, Pilier baie du Mont-Saint-Michel : comparatif 5 communes (FÉV 2026)
  // =====================================================================
  {
    slug: "airbnb-baie-mont-saint-michel-comparatif-communes",
    category: "ville-guide",
    title: "Où louer son Airbnb dans la baie du Mont-Saint-Michel ? Comparatif des 5 meilleures communes (2026)",
    excerpt:
      "Mont-Dol, Dol-de-Bretagne, Saint-Broladre, Roz-sur-Couesnon, Cherrueix : prix moyen, occupation, rendement, profil investisseur. Le guide décisionnel 2026 pour choisir où acheter ou louer.",
    image: "/images/blog/airbnb-baie-mont-saint-michel-comparatif-communes.jpg",
    imageAlt: "Cathédrale Saint-Samson de Dol-de-Bretagne et maisons à colombages, porte d'entrée de la baie du Mont-Saint-Michel",
    author: "L'équipe Terre et Mer",
    date: "2026-02-15",
    readTime: 11,
    keywords:
      "airbnb baie mont-saint-michel, où louer airbnb baie mont-saint-michel, conciergerie baie mont-saint-michel, comparatif communes baie mont-saint-michel, investir airbnb mont-saint-michel, airbnb mont-dol vs dol-de-bretagne, location courte durée polder, airbnb cherrueix char à voile, airbnb roz-sur-couesnon, airbnb saint-broladre",
    relatedCities: ["mont-dol", "dol-de-bretagne"],
    content: `
## La baie du Mont-Saint-Michel : un eldorado Airbnb sous-exploité

**2,5 millions de touristes par an au Mont-Saint-Michel**. C'est l'un des sites les plus visités de France, devant le Louvre certaines années. Et pourtant, la quasi-totalité de ces visiteurs ne dorment **ni au Mont, ni dans les hôtels saturés de Pontorson** : ils cherchent un Airbnb dans un rayon de 15 à 25 km, dans les communes côté Bretagne (Ille-et-Vilaine) ou côté Normandie (Manche).

Côté breton, **5 communes captent 80 % de la demande Airbnb baie** : Mont-Dol, Dol-de-Bretagne, Saint-Broladre, Roz-sur-Couesnon et Cherrueix. Chacune a sa propre signature, ses propres prix, son propre profil investisseur. Lequel choisir pour louer un bien existant ou en acquérir un en 2026 ? **Voici le comparatif décisionnel complet.**

## Tableau récapitulatif des 5 communes

| Commune | Distance Mont-Saint-Michel | Prix moyen / nuit | Voyageurs / an | Taux d'occupation | Type de bien dominant |
|---|---|---|---|---|---|
| **Mont-Dol** | 25 min | 72 € | 14 000+ | 68 % | Longère vue baie, maison de caractère |
| **Dol-de-Bretagne** | 30 min | 65 € | 22 000+ | 65 % | T2/T3 centre médiéval, maisons à colombages |
| **Saint-Broladre** | 18 min | 62 € | 7 500+ | 58 % | Ferme rénovée, gîte rural |
| **Roz-sur-Couesnon** | 12 min | 78 € | 9 000+ | 72 % | Maison vue panoramique sur le polder |
| **Cherrueix** | 22 min | 70 € | 11 000+ | 64 % | Maison plage, gîte char à voile |

**Lecture rapide** : Roz-sur-Couesnon affiche le **meilleur rapport prix/occupation** (proximité directe + vue panoramique sur le Mont). Dol-de-Bretagne offre le **plus gros volume** grâce à sa gare TGV et ses 22 000 voyageurs/an. Mont-Dol est la **pépite confidentielle** premium. Cherrueix joue la carte char à voile / plage. Saint-Broladre cible le tourisme rural et les familles longues durées.

## 1. Mont-Dol, la pépite premium confidentielle

**Profil investisseur** : recherche de tarifs élevés, clientèle haut de gamme et internationale (40 % de britanniques).

### Atouts uniques
- **Panorama 360° depuis le sommet** (le seul de la baie) : Mont-Saint-Michel, polders, marais
- **Mégalithes néolithiques + chapelle XIᵉ** : storytelling puissant pour le livret d'accueil
- **5 minutes de la gare TGV** de Dol-de-Bretagne (Paris en 1h55)
- **Faible concurrence** : 14 000 voyageurs sur un parc immobilier 5× plus petit que Saint-Malo

### Prix de l'immobilier (achat 2026)
- T2 60 m² : 145 000 à 180 000 €
- Longère 100 m² : 220 000 à 290 000 €
- Maison vue baie 140 m² : 320 000 à 420 000 €

### Rendement Airbnb estimé (revenus annuels nets)
- T2 : 18 000 à 24 000 € → **rendement brut 12-14 %**
- Longère 6-8 pers. : 32 000 à 38 000 € → **rendement brut 11-13 %**

### Pour qui ?
Investisseur recherchant la **rareté + la rentabilité élevée**. Saison étendue d'avril à novembre + pics grandes marées. Notre [analyse complète Mont-Dol →](/blog/louer-airbnb-mont-dol-baie-mont-saint-michel)

[**Voir l'offre conciergerie Mont-Dol →**](/villes/mont-dol)

## 2. Dol-de-Bretagne, le volume et le TGV direct Paris

**Profil investisseur** : recherche de **volume de réservations**, taux d'occupation prévisible 12 mois sur 12, ticket d'entrée modéré.

### Atouts uniques
- **Gare TGV directe Paris en 1h55** (la seule de toute la baie côté Bretagne)
- **Cathédrale Saint-Samson XIIᵉ + cité médiévale + menhir du Champ-Dolent** : densité patrimoniale exceptionnelle
- **22 000 voyageurs/an** : le plus gros volume de la zone
- **Centre commerçant actif** : restaurants, marchés, brasseries, séduit la clientèle famille

### Prix de l'immobilier (achat 2026)
- T2 50 m² centre : 120 000 à 160 000 €
- Maison à colombages 80 m² : 195 000 à 270 000 €
- Maison de caractère 130 m² : 280 000 à 380 000 €

### Rendement Airbnb estimé
- T2 centre : 16 000 à 22 000 € → **rendement brut 11-13 %**
- Maison à colombages : 26 000 à 34 000 € → **rendement brut 10-12 %**

### Pour qui ?
Investisseur "sécurité" qui veut un **flux de réservations très régulier**, peu sensible aux pics événementiels. Ticket d'entrée plus accessible que Saint-Malo (-30 %). [Voir notre analyse complète Dol-de-Bretagne →](/villes/dol-de-bretagne)

## 3. Saint-Broladre, le tourisme rural & les longues durées

**Profil investisseur** : ferme à rénover, gîte rural, familles 6-10 personnes en recherche de séjours longs (1 à 2 semaines).

### Atouts uniques
- **18 minutes seulement du Mont-Saint-Michel** : le plus rapide après Roz-sur-Couesnon
- **Cadre rural préservé** : marais, polder, élevage de pré-salé
- **Tarifs immobiliers attractifs** (la commune la moins chère du top 5)
- **Fort potentiel longues durées** : familles élargies, retraités, télétravail été

### Prix de l'immobilier (achat 2026)
- Ferme à rénover 150 m² : 145 000 à 210 000 €
- Longère rénovée 120 m² : 195 000 à 270 000 €
- Gîte 200 m² 8-10 personnes : 280 000 à 380 000 €

### Rendement Airbnb estimé
- Ferme rénovée 4-6 pers. : 18 000 à 24 000 € → **rendement brut 9-11 %**
- Gîte familial 8-10 pers. : 30 000 à 38 000 € → **rendement brut 10-12 %**

### Pour qui ?
Investisseur "patrimoine" qui aime les rénovations de caractère, accepte un taux d'occupation un peu plus bas (58 %) en échange d'un **panier moyen élevé** sur les longues durées (1 semaine = 850 à 1 400 €).

## 4. Roz-sur-Couesnon, le belvédère panoramique

**Profil investisseur** : recherche de **proximité maximale du Mont** + vue panoramique pour des biens premium.

### Atouts uniques
- **12 minutes du Mont-Saint-Michel** : LA commune la plus proche côté breton
- **Belvédère naturel** sur le Couesnon, le polder et le Mont, vue spectaculaire
- **Taux d'occupation le plus haut de la baie (72 %)** grâce à la proximité
- **Tarifs au-dessus de la moyenne** (78 €/nuit) : la prime à la vue se monétise très bien

### Prix de l'immobilier (achat 2026)
- Maison de plain-pied 90 m² : 195 000 à 260 000 €
- Maison vue Mont 130 m² : 320 000 à 450 000 € (la prime "vue Mont" peut atteindre +30 %)
- Longère panoramique : 280 000 à 390 000 €

### Rendement Airbnb estimé
- Maison classique : 22 000 à 28 000 € → **rendement brut 10-12 %**
- Maison vue Mont : 36 000 à 48 000 € → **rendement brut 10-13 %**

### Pour qui ?
Investisseur prêt à payer une **prime sur le foncier** pour décrocher une vue exceptionnelle. C'est le bien qui se vend avant même d'être mis en ligne. Demande étalée 12 mois grâce au flux Mont-Saint-Michel constant.

## 5. Cherrueix, la plage, le char à voile & l'expérience

**Profil investisseur** : positionnement loisirs / sport, clientèle familles actives, séjours sportifs.

### Atouts uniques
- **9 km de plage de sable fin** (la plus longue de la baie côté breton)
- **Capitale française du char à voile** (clubs, écoles, championnats)
- **Tourisme actif** toute l'année : pêche à pied, vélo, kitesurf, kayak
- **Marées spectaculaires** : la grève descend à 7 km par grand coefficient

### Prix de l'immobilier (achat 2026)
- T2 plage 50 m² : 145 000 à 195 000 €
- Maison plage 90 m² : 240 000 à 330 000 €
- Gîte sport 130 m² : 295 000 à 390 000 €

### Rendement Airbnb estimé
- T2 plage : 18 000 à 24 000 € → **rendement brut 11-13 %**
- Maison plage 6 pers. : 28 000 à 36 000 € → **rendement brut 10-12 %**

### Pour qui ?
Investisseur qui veut un bien à **double saison** (été plage / hiver tourisme baie + char à voile). Ticket modéré, public familial fidèle, fort taux de récurrence (les amateurs de char à voile reviennent chaque année).

## Quelle commune choisir selon votre profil ? La matrice de décision

| Vous cherchez… | Commune recommandée | Pourquoi |
|---|---|---|
| Le **rendement maximal** sur petit ticket | **Mont-Dol** ou **Dol-de-Bretagne** | Tarifs entrée modérés + saison longue |
| La **clientèle internationale premium** | **Mont-Dol** ou **Roz-sur-Couesnon** | 40-50 % de britanniques + vues exceptionnelles |
| Le **taux d'occupation le plus haut** | **Roz-sur-Couesnon** (72 %) | Proximité directe Mont, demande 12 mois |
| Le **plus gros volume** de réservations | **Dol-de-Bretagne** (22 000/an) | Gare TGV + cité médiévale + 22 000 voyageurs |
| Une **rénovation patrimoine** + longues durées | **Saint-Broladre** | Fermes / longères, séjours 1 semaine+ |
| Un **bien sport / plage** double saison | **Cherrueix** | 9 km de plage, char à voile, kitesurf |
| Une **acquisition à moins de 200 000 €** | **Saint-Broladre** ou **Mont-Dol** | Tarifs immobiliers les plus accessibles |

## Les pièges à éviter dans la baie

### Piège n°1, Sous-estimer la saisonnalité hivernale

La baie reste fréquentée en hiver (week-ends Mont-Saint-Michel, marchés de Noël, escapades culturelles), mais le taux d'occupation chute à 30-45 % en janvier-février. **Notre pricing dynamique active automatiquement la stratégie "séjours longs"** (télétravail, retraite active) pour maintenir un bon revenu.

### Piège n°2, Ignorer les grandes marées d'équinoxe

Mars + septembre = pics absolus. Les voyageurs viennent voir la marée galopante (8 km/h). **Sans pricing événementiel calé sur le calendrier SHOM**, vous laissez 800 à 1 200 € sur la table chaque année. Voir notre [guide pricing dynamique →](/blog/pricing-dynamique-airbnb-comment-ca-marche).

### Piège n°3, Négliger l'anglais dans l'annonce

40 à 50 % de la clientèle baie est étrangère (Royaume-Uni, Allemagne, Pays-Bas, États-Unis). Une annonce mal traduite plafonne à 60 % d'occupation. **Nos rédacteurs anglophones natifs** créent une version EN qui débloque les 15-20 % de revenus supplémentaires.

### Piège n°4, Oublier la déclaration en mairie

Toutes les communes du périmètre UNESCO appliquent la **loi Le Meur 2024** : déclaration obligatoire, numéro à 13 chiffres, taxe de séjour collectée par Airbnb. Non-déclaration = amende de 5 000 € + suspension de l'annonce. Voir notre [guide réglementation 2026 →](/blog/reglementation-airbnb-saint-malo-dinard-cancale-2026).

## Notre conviction sur la baie en 2026

Sur les 5 communes étudiées, **2 sortent du lot pour un investissement neuf 2026** :

1. **Roz-sur-Couesnon** pour qui cherche le **bien d'exception** (vue Mont, panier moyen haut, taux d'occupation le plus élevé)
2. **Mont-Dol** pour qui cherche la **rareté + le rendement** (tarifs immobiliers modérés, clientèle premium internationale)

Et **1 commune pour qui veut sécuriser le volume** : **Dol-de-Bretagne** (22 000 voyageurs/an, ticket d'entrée accessible, gare TGV).

Notre City Manager couvre l'ensemble du secteur baie du Mont-Saint-Michel et intervient en moins de 25 minutes depuis Saint-Malo. Pour discuter de votre projet, bien existant à mettre en ligne, ou acquisition à venir, nous proposons une **étude personnalisée gratuite sous 24 h**, sans engagement.

[**Voir notre offre Mont-Dol →**](/villes/mont-dol)
[**Voir notre offre Dol-de-Bretagne →**](/villes/dol-de-bretagne)
[**Lire l'analyse complète Mont-Dol →**](/blog/louer-airbnb-mont-dol-baie-mont-saint-michel)
[**Demander une étude personnalisée →**](/contact)
`,
  },

  // =====================================================================
  // 33, Lancieux : guide propriétaire Côte d'Émeraude (FÉV 2026)
  // =====================================================================
  {
    slug: "louer-airbnb-lancieux-cote-emeraude-2026",
    category: "ville-guide",
    title: "Louer son Airbnb à Lancieux : le guide propriétaire 2026 (Côte d'Émeraude)",
    excerpt:
      "Grande Plage 1,5 km, moulin emblématique, GR34, char à voile : pourquoi Lancieux est l'une des pépites Airbnb les plus rentables de la Côte d'Émeraude. Le guide complet 2026.",
    image: "/images/blog/louer-airbnb-lancieux-cote-emeraude-2026.jpg",
    imageAlt: "Moulin de Buglais à Lancieux, emblème du village au coucher du soleil, Côte d'Émeraude, Côtes-d'Armor",
    author: "L'équipe Terre et Mer",
    date: "2026-02-18",
    readTime: 9,
    keywords:
      "louer airbnb lancieux, conciergerie lancieux, prix nuit lancieux, location courte durée lancieux, airbnb grande plage lancieux, conciergerie côte émeraude, airbnb moulin de buglais, location plage pavillon bleu bretagne, char à voile lancieux, airbnb saint-sieu, déclaration mairie lancieux airbnb",
    relatedCities: ["lancieux"],
    content: `
## Lancieux, le secret le mieux gardé de la Côte d'Émeraude

À 5 minutes de Saint-Briac, 15 minutes de Dinard, 25 minutes de Saint-Malo, **Lancieux est l'une des pépites Airbnb les plus sous-cotées de Bretagne nord**. Sa **Grande Plage de 1,5 km classée pavillon bleu**, son **Moulin de Buglais emblématique** (XVIIIᵉ siècle), son ambiance « village balnéaire authentique » et son parc immobilier 6× plus petit que celui de Dinard en font une destination qui coche toutes les cases d'un investissement rentable.

Plus de **20 000 voyageurs Airbnb par an**, un tarif moyen de **78 € la nuit** (au-dessus de la moyenne Côte d'Émeraude), une saison étendue d'avril à octobre et une clientèle famille fidèle qui revient chaque année : voici pourquoi nos propriétaires lancieuciens dépassent systématiquement +30 à +45 % de revenus par rapport à un bien piloté en autonome.

## Le marché Airbnb de Lancieux en chiffres (2026)

- **Prix moyen / nuit** : 78 € (toutes typologies confondues, +12 % vs moyenne Côte d'Émeraude)
- **Voyageurs Airbnb / an** : 20 000+ (en croissance de +14 % depuis 2023)
- **Taux d'occupation moyen** : 71 % (médiane Bretagne : 65 %)
- **Saison principale** : avril à octobre, avec un pic absolu juillet-août
- **Type de bien le plus demandé** : maison familiale 4-6 personnes avec jardin clos, vue mer ou pied de plage
- **Clientèle** : 60 % familles avec enfants, 25 % couples 30-50 ans, 15 % seniors actifs
- **Origine** : 60 % France (Île-de-France, Grand Ouest), 20 % Royaume-Uni (ferry Saint-Malo–Portsmouth), 10 % Belgique/Pays-Bas, 10 % autres

Pour comparer : [Saint-Briac](/villes/saint-briac-sur-mer) (à 5 min) affiche 85 € de prix moyen mais une concurrence plus forte. [Saint-Cast](/villes/saint-cast-le-guildo) (à 15 min) tire à 92 €. **Lancieux est le sweet spot prix/concurrence** de la Côte d'Émeraude.

## Les 5 atouts uniques de Lancieux pour votre bien

### 1. La Grande Plage (1,5 km de sable fin, pavillon bleu)

C'est **l'argument décisionnel n°1**. La Grande Plage de Lancieux est l'une des rares plages de la Côte d'Émeraude à conjuguer : pavillon bleu (qualité de l'eau garantie), surveillance estivale, aire de jeux, club Mickey, parking gratuit, accès handicapé. Pour les **familles avec enfants** (60 % de notre clientèle), c'est rédhibitoire.

**À valoriser dans le titre de l'annonce et dès la première photo.** Les biens situés à moins de 500 m de la plage bénéficient d'une **surcote de 30 à 50 %** par rapport au reste de la commune.

### 2. Le Moulin de Buglais (emblème du village)

Le moulin à vent de Buglais (XVIIIᵉ siècle) est le **landmark Instagram** de Lancieux. Restauré en 2014, il est ouvert à la visite et offre un panorama sur toute la baie. C'est **le storytelling parfait** pour le livret d'accueil et les photos hero de votre annonce. Les voyageurs internationaux (30 % de la clientèle) en raffolent.

### 3. Le sentier des douaniers GR34

Le GR34, élu **« meilleur sentier de France »** par les lecteurs du Routard, traverse Lancieux d'est en ouest. Depuis votre Airbnb, les voyageurs peuvent partir à pied vers la **Pointe de la Garde** (panorama 180°), **Saint-Briac** (5 min) ou **Saint-Jacut** (5 min). C'est un atout majeur pour la clientèle randonneurs (+25 % de séjours en avant et arrière-saison).

### 4. Le char à voile (spot reconnu en France)

La Grande Plage de Lancieux est l'un des **3 meilleurs spots de char à voile de France**. Plusieurs clubs y proposent des cours et des compétitions. Cette spécificité attire une **clientèle sportive fidèle** qui revient chaque été, facteur clé pour les avis 5★ et le taux de réservation récurrent.

### 5. Le ferry Saint-Malo–Portsmouth (clientèle UK)

À 25 minutes du port de Saint-Malo, Lancieux capte **20 % de clientèle britannique** qui débarque par ferry. Pour ces voyageurs, votre annonce doit être **rédigée en anglais natif** (notre standard), avec un livret d'accueil traduit. Cette clientèle premium paie en moyenne +15 % par rapport à la clientèle française et laisse les meilleurs avis.

## Saisonnalité et pricing dynamique : le tableau réel

| Période | Prix moyen | Taux d'occupation | Levier pricing |
|---|---|---|---|
| Janvier - mars | 55 € | 35 % | Stratégie séjours longs (télétravail, retraités) |
| Avril (Pâques) | 90 € | 75 % | Démarrage saison, +35 % week-end |
| Mai (ponts) | 95 € | 82 % | Ascension + Pentecôte, +50 % |
| Juin | 80 € | 70 % | Pré-saison, montée en charge |
| **Juillet - août** | **125 €** | **92 %** | Pic absolu, pricing semaine par semaine |
| Septembre | 95 € | 75 % | Festival Étonnants Voyageurs Saint-Malo |
| Octobre (Festival film britannique Dinard) | 75 € | 60 % | +25 % week-end festival |
| Novembre - décembre | 60 € | 40 % | Toussaint, marchés Noël Saint-Malo |

**Conclusion** : un T2 bien piloté à Lancieux génère **18 000 à 24 000 € de revenus annuels nets** après commission de 15 % TTC. Une maison familiale 6-8 personnes avec jardin clos et vue plage peut dépasser les **42 000 €**. Une maison vue mer atteint **48 000 € à 55 000 €**.

## Comment faire performer votre bien à Lancieux : nos 6 leviers

### 1. Photos premium en lumière dorée sur la plage

Notre photographe planifie systématiquement le shooting **au lever ou coucher du soleil** sur la Grande Plage. Cette golden hour donne aux photos une dimension émotionnelle puissante. Les annonces avec photos lumineuses convertissent **+45 % de clics** vs photos en lumière neutre.

### 2. Annonce 100 % bilingue français / anglais

Avec 30 % de clientèle internationale (UK, Belgique, Pays-Bas, Allemagne), une annonce mal traduite plafonne à 60 % d'occupation. Nos rédacteurs anglophones natifs créent une version EN qui débloque les **15-20 % de revenus supplémentaires** auprès de cette clientèle premium.

### 3. Livret d'accueil orienté famille + plage

Notre livret d'accueil dédié Lancieux propose :
- Le parcours plage complet (Grande Plage, Plage du Rocher, Plage de la Vieille)
- Un circuit GR34 vers la Pointe de la Garde et Saint-Briac
- Les meilleures activités nautiques (char à voile, kayak, paddle, surf)
- La liste des bonnes adresses (Hôtel des Bains, Crêperie du Moulin, marchés)
- Les horaires des marées et conseils pêche à pied
- Les jeux de plage, club Mickey, aire de jeux du centre, pour les familles

Résultat : **note moyenne 4,9/5** sur nos biens du secteur Lancieux vs 4,6/5 en moyenne Airbnb Bretagne.

### 4. Équipement famille premium

Pour optimiser le ranking « familles » sur Airbnb : lit bébé pliant, chaise haute, table à langer, kit jeux de plage (seaux, pelles, pêche aux crabes), trottinettes pour enfants. Coût : 250 € à l'achat. ROI : +20 % de réservations sur la cible famille.

### 5. Pricing événementiel calé sur la saison

Notre algorithme pricing intègre automatiquement : grandes vacances scolaires (zones A, B, C), ponts de mai (Ascension, Pentecôte), festival Étonnants Voyageurs (mai à Saint-Malo), Festival du film britannique de Dinard (octobre), régates et compétitions char à voile. Sans pricing événementiel, vous laissez **800 à 1 500 € sur la table chaque année** sur le secteur Lancieux.

### 6. Une équipe locale à 5 minutes

Notre City Manager basé à Ploubalay intervient sur Lancieux **en moins de 10 minutes**. Pour les voyageurs internationaux qui louent depuis Londres ou Bruxelles, savoir qu'il y a un humain joignable 24h/24 à 5 minutes de l'Airbnb est **le critère #1 de confiance**. Notre permanence de nuit assure aussi la réponse aux messages plateformes 7j/7.

## Investir à Lancieux en 2026 : opportunités

Le marché immobilier de Lancieux reste **35 % moins cher** qu'à Dinard centre et **20 % moins cher** que Saint-Briac. À typologie équivalente :

- **T2 50 m²** : 165 000 à 220 000 €
- **Maison de plain-pied 90 m² avec jardin** : 285 000 à 370 000 €
- **Maison vue mer / proche plage 130 m²** : 420 000 à 560 000 €

Avec un revenu Airbnb annuel net de 22 000 à 48 000 €, vous obtenez un **rendement brut de 9 à 12 %**, bien au-dessus de la moyenne Côte d'Émeraude (6-8 %). Pour les détails fiscaux (LMNP, micro-BIC vs réel), [consultez notre guide LMNP Bretagne 2026](/blog/lmnp-airbnb-bretagne-fiscalite-2026).

## Réglementation : ce qu'il faut savoir avant de louer

Comme toutes les communes des Côtes-d'Armor, Lancieux applique **la loi Le Meur de 2024** :

- Déclaration en mairie obligatoire (formulaire CERFA)
- Numéro d'enregistrement à 13 chiffres à afficher sur l'annonce
- Taxe de séjour collectée automatiquement par Airbnb / Booking
- **Pas de quota actuel ni de limite de jours** pour les résidences secondaires (situation différente de Saint-Malo)

Notre équipe se charge intégralement de la déclaration en mairie. Délai moyen : 3 à 5 jours ouvrés. [Voir le détail des démarches dans notre guide réglementation 2026](/blog/reglementation-airbnb-saint-malo-dinard-cancale-2026).

## Notre conviction sur Lancieux

Lancieux n'a ni la notoriété de Dinard, ni la concurrence de Saint-Cast, ni le prix au mètre carré de Saint-Malo intra-muros. C'est précisément ce qui en fait, à nos yeux, **l'une des meilleures opportunités Airbnb 2026 sur la Côte d'Émeraude**. Plage pavillon bleu, moulin emblématique, GR34, char à voile, clientèle famille fidèle, ferry UK : tous les ingrédients sont réunis.

Si vous possédez déjà un bien à Lancieux, ou si vous envisagez d'en acquérir un, nous serions ravis d'en discuter. Notre City Manager basé à Ploubalay (5 min) couvre l'ensemble du secteur et nous proposons une **étude personnalisée gratuite sous 24 h**, sans engagement.

[**Voir notre offre Lancieux →**](/villes/lancieux)
[**Voir notre offre Saint-Briac (5 min) →**](/villes/saint-briac-sur-mer)
[**Voir notre offre Saint-Cast (15 min) →**](/villes/saint-cast-le-guildo)
[**Demander une étude personnalisée →**](/contact)
`,
  },

  // =====================================================================
  // 34, Grande Plage de Lancieux : guide spécifique long-tail (FÉV 2026)
  // =====================================================================
  {
    slug: "airbnb-grande-plage-lancieux-guide-2026",
    category: "ville-guide",
    title: "Louer son Airbnb sur la Grande Plage de Lancieux : le guide complet 2026",
    excerpt:
      "1,5 km de sable fin, pavillon bleu, club Mickey, char à voile, surveillance estivale : pourquoi un Airbnb à pied de la Grande Plage de Lancieux affiche +30 à +50 % de revenus vs le reste du village.",
    image: "/images/blog/airbnb-grande-plage-lancieux-guide-2026.jpg",
    imageAlt: "Coucher de soleil sur la baie de Lancieux, bateaux mouillés et îlot, Côte d'Émeraude",
    author: "L'équipe Terre et Mer",
    date: "2026-02-22",
    readTime: 8,
    keywords:
      "airbnb grande plage lancieux, location plage lancieux, location plage pavillon bleu bretagne, airbnb pavillon bleu lancieux, airbnb saint-sieu lancieux, location saisonnière grande plage lancieux, airbnb famille plage lancieux, char à voile lancieux airbnb, location vacances plage lancieux, airbnb pied de plage côte émeraude, conciergerie grande plage lancieux",
    relatedCities: ["lancieux"],
    content: `
## La Grande Plage de Lancieux : la plage premium de la Côte d'Émeraude

Avec ses **1,5 km de sable fin doré**, son label **pavillon bleu** renouvelé chaque année depuis 2009, sa surveillance estivale, son club Mickey et son aire de jeux, la **Grande Plage de Lancieux** (parfois appelée plage de Saint-Sieu) est l'une des **plages familiales les plus cotées de la Côte d'Émeraude**. Et c'est précisément ce qui en fait la zone Airbnb la plus rentable de la commune.

Sur notre portefeuille, **les biens situés à moins de 500 m de la Grande Plage affichent +30 à +50 % de revenus** par rapport aux biens du reste de Lancieux. Voici, dans le détail, pourquoi, et comment exploiter ce levier au maximum si vous possédez déjà un bien dans le secteur, ou si vous envisagez d'en acquérir un en 2026.

## Pourquoi la Grande Plage de Lancieux est-elle si demandée ?

### 1. Le label pavillon bleu (qualité de l'eau certifiée)

Le label **pavillon bleu** est attribué chaque année par l'association Teragir aux plages qui répondent à 32 critères stricts : qualité de l'eau (analyses bactériologiques sur toute la saison), gestion des déchets, accessibilité, éducation à l'environnement. **Lancieux possède ce label sans interruption depuis 2009**, un signal fort pour les voyageurs avec enfants en bas âge.

Sur Airbnb, mentionner « pavillon bleu » dans le titre ou la description d'une annonce génère **+18 % de clics sur la cible famille** d'après les tests A/B effectués sur notre portefeuille.

### 2. La surveillance estivale (juillet-août)

De fin juin à début septembre, la plage est **surveillée 7 j/7 par des MNS (Maîtres-Nageurs-Sauveteurs)** de 11 h à 19 h. Les drapeaux (vert / jaune / rouge) indiquent les conditions de baignade. C'est un critère décisionnel majeur pour les parents : **70 % des familles refusent de réserver une plage non surveillée** en haute saison.

### 3. Le club Mickey + aire de jeux + sport

Pendant les vacances scolaires d'été, le **club Mickey** propose chaque jour des activités encadrées pour les enfants 4-12 ans (constructions de châteaux, tournois de volley, animations). À côté, l'**aire de jeux gratuite** (toboggans, balançoires, jeux à ressort) tourne à plein régime de mai à septembre. Et pour les ados et adultes : **char à voile, kayak de mer, paddle, surf et bodyboard**, accessibles directement depuis la plage.

### 4. Le parking gratuit et l'accès handicapé

Détail souvent sous-estimé : **le parking principal de la Grande Plage est gratuit**, contrairement à la plupart des plages de Saint-Cast, Saint-Briac et Dinard. Pour une famille qui passe 7 jours sur place, c'est une économie réelle de 70 à 100 €. À mentionner explicitement dans l'annonce.

L'accès est également **adapté aux personnes à mobilité réduite** (rampe d'accès, fauteuils tiralo disponibles en juillet-août), un argument qui touche une niche fidèle.

### 5. Les marées spectaculaires

À Lancieux, les marées descendent jusqu'à **600 mètres** par fort coefficient. Les voyageurs peuvent **pêcher à pied** (palourdes, coques, étrilles, crabes), explorer les rochers et observer les flaques d'eau de mer. C'est une activité qui plaît à toutes les générations et qui s'intègre parfaitement dans un livret d'accueil bien rédigé.

## Quels biens performent le mieux à pied de la Grande Plage ?

D'après les données Airbnb 2024-2025 sur le secteur Lancieux, voici le classement par rentabilité :

| Type de bien | Revenu annuel net | Rendement brut |
|---|---|---|
| Studio 30 m² (vue mer ou pied de plage) | 16 000 à 22 000 € | 11-13 % |
| T2 50 m² (jardin clos, pied de plage) | 22 000 à 30 000 € | 11-13 % |
| **Maison 4-6 pers., jardin, pied de plage** | **32 000 à 42 000 €** | **11-12 %** |
| **Maison vue mer 6-8 pers.** | **48 000 à 58 000 €** | **10-13 %** |
| Maison familiale 8-10 pers. (cluster cousins) | 52 000 à 68 000 € | 10-12 % |

**Lecture** : les **maisons familiales 4-8 personnes pied de plage** sont le sweet spot du secteur. Elles captent à la fois la clientèle famille (60 %), les groupes d'amis (15 %) et les couples 30-50 ans qui louent pour des séjours longs (25 %).

## Les 5 quartiers à pied de la Grande Plage

1. **Saint-Sieu (front de mer ouest)**, juste au-dessus de la plage. Surcote +50 % pour les biens vue mer directe. Bâti des années 1900 (villas Belle Époque) très recherché.

2. **Le Trez (front de mer est)**, côté sud-est de la plage, vers la Pointe de la Garde. Maisons modernes et villas, parking facile. Surcote +35 %.

3. **Rue de la Plage / Bd des Plages**, axe principal du village reliant le centre à la Grande Plage (3-5 minutes à pied). Bon compromis prix / proximité. Surcote +25 %.

4. **Centre village (église, halle, commerces)**, à 5-7 minutes à pied de la plage. Tout à pied (boulangerie, restaurants, marché). Surcote +15 %, idéal pour les T2/T3 par couples.

5. **Hameaux résidentiels** (route de Saint-Briac, route de Saint-Jacut), à 7-10 minutes en voiture ou 15-20 minutes à pied. Tarifs immobiliers les plus accessibles. Pas de surcote plage mais excellente rentabilité brute.

## Comment optimiser une annonce Airbnb « pied de plage Lancieux »

### Le titre, la règle des 3 mots-clés en 50 caractères

Bon exemple : *« Pied de Grande Plage Lancieux ★ Pavillon bleu »*  
Mauvais exemple : *« Charmante maison de vacances en Bretagne »*

Le voyageur Airbnb scanne le titre en **0,8 seconde** d'après les eye-tracking studies. Vous avez 3 mots-clés pour le séduire : **localisation**, **distance plage** et **différenciateur** (pavillon bleu, vue mer, jardin clos, etc.).

### Les photos, la première compte pour 70 % du clic

La photo n°1 doit montrer la **plage ou la vue mer**, pas le salon. Si votre bien n'a pas de vue directe, choisissez une photo extérieure (façade, jardin, terrasse) avec la plage en arrière-plan ou suggérée. Notre photographe planifie systématiquement le shoot **au lever ou coucher du soleil** pour capter la lumière dorée caractéristique de la Côte d'Émeraude.

### Les équipements famille « pied de plage »

Pour décrocher le filtre Airbnb « familles » et le ranking premium :

- 🪣 Kit jeux de plage (seaux, pelles, épuisettes, ballons)
- 🏖️ 2 parasols + 2 transats
- 🚿 Douche de jardin (pour rincer le sable)
- 🧒 Lit bébé pliant + chaise haute + table à langer
- 🚲 1 à 2 vélos adultes + 1 à 2 vélos enfants
- 🛏️ Lit gigogne ou canapé-lit pour la 4ᵉ ou 5ᵉ personne
- 🥗 Cuisine équipée robot multifonction (les familles cuisinent 50 % du temps)

Coût total : 700 à 1 200 €. ROI : **+25 à +35 %** de réservations sur la cible famille selon les tests A/B.

### Le livret d'accueil dédié plage

Notre livret d'accueil dédié Grande Plage de Lancieux (38 pages, mis à jour chaque année) inclut :

- Plan détaillé des **5 plages de Lancieux** (Grande Plage, Plage de la Vieille, Plage du Rocher, Plage du Béchet, Plage de Saint-Sieu)
- Horaires des marées + coefficient pour toute la saison
- Guide pêche à pied (espèces autorisées, tailles minimales, équipement)
- Les **3 meilleurs spots** char à voile, kayak, paddle (avec contacts directs)
- Carte des restaurants + crêperies + glaciers à pied
- Itinéraires GR34 vers Saint-Briac et Saint-Jacut
- Activités enfants par tranche d'âge (3-6, 7-10, 11-15, ados)

Résultat : **note moyenne 4,9/5** sur les commentaires « localisation + équipement plage ».

## Pricing dynamique : exploiter la haute saison plage

La saison plage stricte (mai à septembre) représente **75 % des revenus annuels** d'un bien pied de plage à Lancieux. Notre algorithme pricing applique :

- **Mai (ponts)** : +50 % vs base, +60 % week-end Ascension/Pentecôte
- **Juin** : +30 %, montée en charge
- **Juillet-août** : pricing semaine par semaine, +110 % vs base sur la 2ᵉ quinzaine de juillet et la 1ʳᵉ quinzaine d'août
- **Septembre** : +25 %, microclimat doux + festival Étonnants Voyageurs (Saint-Malo)

**Sans pricing événementiel**, un bien pied de plage perd typiquement 1 800 à 2 800 € par an sur le secteur Grande Plage de Lancieux. C'est le levier #1 que nos propriétaires identifient comme « game-changer » 6 mois après la mise en ligne.

## Pour aller plus loin

- 📖 [Le guide complet propriétaire Lancieux 2026](/blog/louer-airbnb-lancieux-cote-emeraude-2026)
- 🏖️ [Saint-Cast : la Grande Plage qui rapporte (à 15 min)](/blog/louer-airbnb-saint-cast-le-guildo-grande-plage)
- 🎨 [Saint-Briac : le village des artistes (à 5 min)](/blog/louer-airbnb-saint-briac-village-renoir)
- 💼 [Investir Airbnb sur la Côte d'Émeraude en 2026](/blog/investir-airbnb-cote-emeraude-bretagne-2026)
- 📋 [Réglementation Airbnb 2026 (St-Malo, Dinard, Cancale, Lancieux)](/blog/reglementation-airbnb-saint-malo-dinard-cancale-2026)

## Notre conviction sur les biens « pied de Grande Plage »

Sur la Côte d'Émeraude, peu de communes combinent **pavillon bleu + plage de 1,5 km + ambiance village authentique + tarif moyen 78 €/nuit + faible concurrence**. Lancieux coche ces 5 cases. Et la Grande Plage est **la zone qui concentre 70 % de la valeur** de la commune.

Si vous possédez un bien à pied de la Grande Plage et que vous le louez vous-même, vous laissez probablement **30 à 45 % de revenus sur la table**. Notre conciergerie locale (City Manager basé à Ploubalay, à 5 minutes) peut faire la différence sans changer un mètre carré de votre logement, juste en optimisant photos, annonce, pricing et opérations.

[**Voir notre offre Lancieux →**](/villes/lancieux)
[**Lire le guide propriétaire Lancieux complet →**](/blog/louer-airbnb-lancieux-cote-emeraude-2026)
[**Demander une étude personnalisée gratuite →**](/contact)
`,
  },

  // =====================================================================
  // 35, Investir à Lancieux : rendement Airbnb vs locatif (FÉV 2026)
  // =====================================================================
  {
    slug: "investir-airbnb-lancieux-rendement-2026",
    category: "ville-guide",
    title: "Investir à Lancieux en 2026 : rendement Airbnb vs locatif classique (étude chiffrée)",
    excerpt:
      "T2, maison familiale, vue mer : combien rapporte vraiment un bien à Lancieux ? Comparatif Airbnb / location nue / location meublée annuelle, simulation à 10 ans et fiscalité LMNP. Le guide investisseur 2026.",
    image: "/images/blog/investir-airbnb-lancieux-rendement-2026.jpg",
    imageAlt: "Maison de bord de mer en Bretagne, investissement immobilier locatif Lancieux Côte d'Émeraude",
    author: "L'équipe Terre et Mer",
    date: "2026-02-26",
    readTime: 10,
    keywords:
      "investir lancieux, investir airbnb lancieux, rendement airbnb lancieux, acheter pour louer lancieux, rentabilité airbnb côte émeraude, lmnp lancieux, simulation airbnb lancieux, prix immobilier lancieux 2026, location courte durée vs locatif annuel lancieux, airbnb vs locatif classique bretagne",
    relatedCities: ["lancieux"],
    content: `
## Lancieux en 2026 : pourquoi c'est l'un des meilleurs spots d'investissement Airbnb de Bretagne

Lancieux affiche en 2026 une combinaison rare en Bretagne nord : **prix immobilier accessibles**, **rendement locatif élevé**, **saison étendue d'avril à octobre** et **clientèle famille fidèle**. Pour un investisseur qui vise le **meublé tourisme** (LMNP), c'est l'une des communes les plus rentables de la Côte d'Émeraude, devant Dinard et Saint-Briac où les prix sont 30 à 40 % supérieurs à typologie équivalente.

Dans ce guide, nous comparons **chiffres à l'appui** les 3 options qui s'offrent à un propriétaire ou futur acquéreur :

- **Option A** : location nue (locataire à l'année)
- **Option B** : location meublée annuelle (LMNP standard)
- **Option C** : Airbnb / courte durée pilotée par notre conciergerie (15 % TTC)

Trois cas concrets, trois tableaux de rentabilité, et la fiscalité expliquée simplement.

## Le marché immobilier de Lancieux en 2026

D'après les bases DVF (Demandes de Valeurs Foncières), Meilleurs Agents et nos remontées terrain :

| Typologie | Prix au m² 2026 | Prix moyen complet |
|---|---|---|
| Studio 30 m² | 3 800 à 4 600 €/m² | 115 000 à 145 000 € |
| T2 50 m² | 3 600 à 4 500 €/m² | 165 000 à 220 000 € |
| Maison de plain-pied 90 m² + jardin | 3 200 à 4 100 €/m² | 285 000 à 370 000 € |
| Maison vue mer 130 m² | 3 800 à 5 200 €/m² | 420 000 à 560 000 € |
| Maison familiale 6-8 pers. pied de plage | 4 500 à 6 200 €/m² | 480 000 à 720 000 € |

Pour comparer : Dinard centre tourne autour de **5 800 à 7 200 €/m²**, Saint-Briac à **4 500 à 5 800 €/m²**. Lancieux offre donc **une décote moyenne de 25 à 35 %** par rapport à ses voisines plus connues.

## Cas n°1, T2 50 m² avec balcon, à 5 min à pied de la Grande Plage

**Investissement total** : 195 000 € (acquisition) + 12 000 € (notaire) + 8 000 € (ameublement & équipement Airbnb) = **215 000 €**

### Option A, Location nue à l'année

- Loyer mensuel : 580 €
- Loyer annuel : **6 960 €**
- Charges déductibles : 1 200 € (taxe foncière, copro, assurance PNO)
- **Revenu net annuel : 5 760 €**
- Rendement net : **2,7 %**

### Option B, Location meublée annuelle (LMNP, micro-BIC)

- Loyer mensuel : 720 € (+24 % vs nu)
- Loyer annuel : **8 640 €**
- Abattement forfaitaire micro-BIC 50 % → impôt sur 4 320 €
- Charges (déduites du loyer brut) : 1 500 €
- **Revenu net annuel ≈ 6 800 €**
- Rendement net : **3,2 %**

### Option C, Airbnb piloté par Terre et Mer (commission 15 % TTC)

- Tarif moyen : 78 €/nuit
- Taux d'occupation pilotage Terre et Mer : 71 %
- Nuits louées par an : 259
- **Chiffre d'affaires brut : 20 200 €**
- Commission 15 % TTC : −3 030 €
- Charges (taxe foncière, copro, assurance, équipement) : −2 200 €
- Régime LMNP réel (amortissement bien + mobilier) : impôt quasi-nul les 8 premières années
- **Revenu net annuel ≈ 14 970 €**
- Rendement net : **7,0 %**

➡️ **Différentiel Airbnb vs locatif nu : +9 210 €/an**, soit **+ 159 %** de rentabilité. Sur 10 ans, l'écart cumulé dépasse **92 000 €**.

## Cas n°2, Maison familiale 4-6 personnes, jardin clos, à pied de la plage

**Investissement total** : 365 000 € (acquisition) + 22 500 € (notaire) + 14 000 € (ameublement haut de gamme) = **401 500 €**

### Option A, Location nue

- Loyer : 1 050 €/mois → **12 600 €/an**
- Charges : 2 100 €
- **Net : 10 500 €** → rendement **2,6 %**

### Option B, Meublée annuelle (LMNP)

- Loyer : 1 280 €/mois → **15 360 €/an**
- Net après charges et fiscalité : **12 100 €** → rendement **3,0 %**

### Option C, Airbnb 15 % TTC Terre et Mer

- Tarif moyen : 145 €/nuit (équipement famille premium + jardin clos)
- Occupation pilotée : 73 %
- **CA brut : 38 600 €**
- Commission : −5 790 €
- Charges : −3 800 €
- Fiscalité LMNP réel : impôt quasi-nul
- **Net annuel ≈ 29 010 €** → rendement **7,2 %**

➡️ **Différentiel Airbnb vs nu : +18 510 €/an**. **+176 %** de rentabilité. Sur 10 ans : **+185 000 €**, soit **l'équivalent d'un T2 supplémentaire** acquis grâce à la sur-rentabilité.

## Cas n°3, Maison vue mer 130 m², 6-8 personnes, pied de Grande Plage

**Investissement total** : 565 000 € + 34 000 € + 18 000 € = **617 000 €**

| Option | Revenu net annuel | Rendement net |
|---|---|---|
| **A**, Location nue | 13 600 € | 2,2 % |
| **B**, Meublée annuelle (LMNP) | 16 200 € | 2,6 % |
| **C**, Airbnb 15 % Terre et Mer | **42 800 €** | **6,9 %** |

Sur 10 ans, l'écart entre Airbnb et locatif nu approche **+290 000 €**. C'est suffisant pour autofinancer une 2ᵉ acquisition.

## Pourquoi Airbnb performe autant mieux à Lancieux ?

Trois facteurs structurels :

### 1. Le tarif/nuit en haute saison est multiplié par 2,5 à 3 vs basse saison

Sur juillet-août, un T2 à Lancieux se loue **125 €/nuit** vs 50 € en janvier. C'est l'élasticité de prix qui crée la sur-rentabilité, impossible en location nue où le loyer est fixe 12 mois.

### 2. La saison étendue d'avril à octobre

7 mois de haute fréquentation, contre 4 mois pour la plupart des stations bretonnes. Le microclimat doux de la Côte d'Émeraude + le pavillon bleu attirent même hors été. Notre [analyse complète Grande Plage de Lancieux →](/blog/airbnb-grande-plage-lancieux-guide-2026) détaille ce phénomène.

### 3. La fiscalité LMNP au régime réel

Le statut **Loueur Meublé Non Professionnel (LMNP) au régime réel** permet d'amortir le bien, le mobilier et les frais d'acquisition. Résultat : **impôt sur les revenus locatifs quasi-nul pendant 8 à 12 ans**. Voir notre [guide LMNP Bretagne 2026 →](/blog/lmnp-airbnb-bretagne-fiscalite-2026).

## Simulation à 10 ans : T2 acheté en 2026, revendu en 2036

Hypothèses : T2 50 m² à 195 000 €, financé à 80 % sur 20 ans à 3,5 %, charges et fiscalité incluses.

| Critère | Location nue | Meublée annuelle | Airbnb 15 % T&M |
|---|---|---|---|
| Cumul revenus nets 10 ans | 57 600 € | 68 000 € | **149 700 €** |
| Mensualité crédit absorbée | 30 % | 36 % | **80 %** |
| Cashflow mensuel net | −620 € | −485 € | **+340 €** |
| Plus-value à 10 ans (estim.) | +85 000 € | +85 000 € | +85 000 € |
| **Capital total à 10 ans** | **+142 600 €** | **+153 000 €** | **+234 700 €** |

➡️ **L'Airbnb piloté permet d'atteindre l'autofinancement** dès la 1ʳᵉ année (cashflow positif), là où la location nue génère un cashflow négatif. Sur 10 ans, **+92 000 € de capital supplémentaire**, sans parler de la souplesse d'usage personnel.

## La cerise sur le gâteau : vous gardez l'usage personnel du bien

Avec la location nue ou meublée annuelle, votre bien est **bloqué 12 mois sur 12** par votre locataire. Avec un Airbnb piloté Terre et Mer, vous bloquez vos dates personnelles dans votre espace propriétaire en ligne, typiquement 2 à 4 semaines par an pour vos vacances familiales. Le manque à gagner est intégré dans nos simulations ci-dessus.

## La formule conciergerie Terre et Mer en détail

- **Commission unique 15 % TTC** sur les revenus réels, sans frais cachés, sans set-up, sans frais photos
- **Mise en ligne sous 7-14 jours** : visite technique, shooting, annonce bilingue, déclaration en mairie
- **Multidiffusion automatique** Airbnb · Booking · Vrbo via channel manager
- **Pricing dynamique** calé sur saison, événements (Festival Étonnants Voyageurs, Festival du film britannique de Dinard, ponts de mai)
- **Accueil voyageurs 24h/24 · 7j/7** avec permanence de nuit
- **Ménage hôtelier** facturé directement aux voyageurs (neutre pour vous)
- **Kit d'accueil inclus** : Shampooing, gel douche, papier toilette, produit vaisselle, éponge, sac-poubelle, petit sac pour la salle de bain
- **Linge non inclus** dans la formule 15 % : par défaut les voyageurs apportent leur linge ou souscrivent l'option premium directement (facturée à eux)
- **City Manager local** basé à Ploubalay, intervention en moins de 10 minutes sur Lancieux

## Pour aller plus loin

- 📖 [Le guide propriétaire Lancieux complet 2026](/blog/louer-airbnb-lancieux-cote-emeraude-2026)
- 🏖️ [Grande Plage de Lancieux : pourquoi le pied de plage rapporte +50 %](/blog/airbnb-grande-plage-lancieux-guide-2026)
- 💼 [LMNP Bretagne : fiscalité 2026 expliquée](/blog/lmnp-airbnb-bretagne-fiscalite-2026)
- 📊 [Brut, net, cashflow : lire les vrais chiffres de votre Airbnb](/blog/rentabilite-brute-nette-cashflow-airbnb-saint-malo)
- 🌊 [Investir Airbnb sur la Côte d'Émeraude en 2026, vue d'ensemble](/blog/investir-airbnb-cote-emeraude-bretagne-2026)

## Vous envisagez d'investir à Lancieux ?

Nous proposons une **étude personnalisée gratuite sous 24 h** : vous nous transmettez le bien envisagé (adresse, typologie, prix d'achat), nous vous renvoyons une simulation détaillée avec :

- Tarif moyen estimé selon la saison
- Taux d'occupation projeté à 12 mois
- CA brut et revenu net annuel
- Rendement brut et net après commission
- Comparatif vs location nue et meublée annuelle

Notre City Manager peut aussi vous accompagner avant l'achat (visite, étude technique, conseils ameublement). Cet accompagnement amont, **gratuit et sans engagement**, est devenu un standard pour nos investisseurs.

[**Voir notre offre Lancieux →**](/villes/lancieux)
[**Demander une étude personnalisée gratuite →**](/contact)
[**Lire le guide propriétaire Lancieux →**](/blog/louer-airbnb-lancieux-cote-emeraude-2026)
`,
  },


  // =====================================================================
  // 36, Saint-Lunaire : guide propriétaire Côte d'Émeraude (FÉV 2026)
  // =====================================================================
  {
    slug: "louer-airbnb-saint-lunaire-cote-emeraude-2026",
    category: "ville-guide",
    title: "Louer son Airbnb à Saint-Lunaire : le guide propriétaire 2026 (Côte d'Émeraude)",
    excerpt:
      "Villas Belle Époque, golf historique de 1887, Pointe du Décollé : pourquoi Saint-Lunaire est l'alternative confidentielle de Dinard pour un Airbnb haut de gamme. Guide complet 2026.",
    image: "/images/blog/louer-airbnb-saint-lunaire-cote-emeraude-2026.jpg",
    imageAlt: "Cabines de plage rayées rouge et blanc devant les villas Belle Époque de Saint-Lunaire, Côte d'Émeraude",
    author: "L'équipe Terre et Mer",
    date: "2026-03-02",
    readTime: 9,
    keywords:
      "louer airbnb saint-lunaire, conciergerie saint-lunaire, prix nuit saint-lunaire, location courte durée saint-lunaire, airbnb pointe du décollé, airbnb villa belle époque saint-lunaire, airbnb golf saint-lunaire, conciergerie côte émeraude saint-lunaire, déclaration mairie saint-lunaire airbnb",
    relatedCities: ["saint-lunaire"],
    content: `
## Saint-Lunaire, l'alternative confidentielle de Dinard

À 5 minutes de Dinard, 10 minutes de Saint-Briac, 15 minutes de Saint-Malo, **Saint-Lunaire est l'une des destinations Airbnb les plus sous-cotées de Bretagne nord**. Mêmes villas Belle Époque que Dinard, même panorama Côte d'Émeraude, même golf 18 trous historique (1887), mais avec une **concurrence Airbnb 3 fois plus faible** et des prix immobiliers **25 à 30 % en dessous**.

Plus de **22 000 voyageurs Airbnb par an**, un tarif moyen de **95 € la nuit** (au-dessus de Dinard hors centre), une saison étendue d'avril à octobre et une clientèle internationale haut de gamme (40 %) : voici pourquoi nos propriétaires lunairiens dépassent systématiquement +30 à +45 % de revenus par rapport à un bien piloté en autonome.

## Le marché Airbnb de Saint-Lunaire en chiffres (2026)

- **Prix moyen / nuit** : 95 € (toutes typologies confondues, +18 % vs moyenne Côte d'Émeraude)
- **Voyageurs Airbnb / an** : 22 000+ (en croissance de +12 % depuis 2023)
- **Taux d'occupation moyen** : 73 % (médiane Bretagne : 65 %)
- **Saison principale** : avril à octobre, pic absolu juillet-août + Festival du film britannique de Dinard (octobre)
- **Type de bien le plus demandé** : villa Belle Époque ou maison familiale 4-6 personnes avec jardin clos
- **Clientèle internationale** : 40 % (25 % britanniques via ferry Saint-Malo–Portsmouth, 8 % Belgique/Pays-Bas, 7 % autres)

Pour comparer : Dinard centre tire à 125 €/nuit mais avec une concurrence 5× plus forte. [Lancieux](/villes/lancieux) (à 10 min) affiche 78 €/nuit. **Saint-Lunaire est le sweet spot prestige/rentabilité** de la Côte d'Émeraude.

## Les 5 atouts uniques de Saint-Lunaire pour votre bien

### 1. La Pointe du Décollé (panorama 180° unique)

C'est **l'argument décisionnel n°1**. La Pointe du Décollé offre l'un des plus beaux panoramas de Bretagne : vue à 180° sur Dinard, Saint-Malo, l'île Cézembre, le Cap Fréhel par temps clair. Le restaurant Le Décollé (étoilé) y est posé comme une vigie face à l'océan. **À valoriser dans le titre et dès la première photo de l'annonce.**

Les biens situés à moins de 800 m de la Pointe du Décollé bénéficient d'une **surcote de 40 à 60 %** par rapport au reste de la commune.

### 2. Le Golf de Saint-Lunaire (1887, l'un des plus anciens de France)

Créé en 1887, le **Golf de Saint-Lunaire est le 3ᵉ plus ancien golf de France** (après Pau et Biarritz). 9 trous historiques avec vue sur la mer, club-house chargé d'histoire, ouvert toute l'année. Cette spécificité attire une **clientèle golfeurs premium**, souvent britannique, qui réserve 1 à 2 semaines par séjour et revient chaque année.

Mentionner « à 5 min du Golf » ou « accès Golf de Saint-Lunaire » dans l'annonce génère +18 % de clics sur la cible golfeurs.

### 3. Les villas Belle Époque (le charme intact)

Saint-Lunaire est l'une des **dernières communes de la Côte d'Émeraude où le tissu Belle Époque est intact**. Façades en granit rose, balcons en fer forgé, tourelles, fenêtres en plein cintre, jardins clos : c'est un décor de rêve pour les voyageurs internationaux qui cherchent « la France d'autrefois ». Notre photographe planifie systématiquement le shoot **en lumière dorée** pour capter ce charme.

### 4. La Grande Plage + Plage de Longchamp (2 plages familiales)

Saint-Lunaire dispose de **2 plages distinctes** : la Grande Plage (cœur du village, animée) et la **Plage de Longchamp** (plus calme, plus familiale, vue sur l'île Cézembre). Surveillance estivale, sable fin, accès facile, parking. Pour les **familles avec jeunes enfants** (30 % de la clientèle), c'est rédhibitoire.

### 5. Le Festival du film britannique de Dinard (à 5 min)

Début octobre, Dinard accueille le **Festival du film britannique**, l'un des plus grands événements cinématographiques d'Europe. Hôtels et Airbnb de Dinard sont saturés et les prix s'envolent (+80 à +120 %). **Saint-Lunaire capte le débordement** à des tarifs encore plus élevés que la moyenne haute saison. C'est LE pic SEO de la commune.

## Saisonnalité et pricing dynamique : le tableau réel

| Période | Prix moyen | Taux d'occupation | Levier pricing |
|---|---|---|---|
| Janvier - mars | 65 € | 35 % | Stratégie séjours longs (télétravail, retraités) |
| Avril (Pâques) | 100 € | 75 % | Démarrage saison + Open de golf printemps |
| Mai (ponts) | 115 € | 82 % | Ascension + Pentecôte + festival Étonnants Voyageurs |
| Juin | 95 € | 70 % | Pré-saison, montée en charge |
| **Juillet - août** | **150 €** | **92 %** | Pic absolu, pricing semaine par semaine |
| Septembre | 110 € | 78 % | Microclimat doux, golfeurs européens |
| **Octobre (Festival film britannique Dinard)** | **130 €** | **85 %** | +50 % sur 4 jours festival |
| Novembre - décembre | 75 € | 42 % | Toussaint, marchés Noël Saint-Malo, séjours longs |

**Conclusion** : un T2 bien piloté à Saint-Lunaire génère **22 000 à 30 000 € de revenus annuels nets** après commission de 15 % TTC. Une villa Belle Époque 6-8 personnes avec vue mer peut dépasser **58 000 €** par an.

## Comment faire performer votre bien à Saint-Lunaire : nos 6 leviers

### 1. Photos en lumière dorée + détails Belle Époque

Notre photographe planifie le shooting **au lever ou coucher du soleil** + capture systématiquement les détails Belle Époque (parquets, cheminées, moulures, jardin clos). Cette mise en scène débloque **+45 % de clics** vs photos en lumière neutre.

### 2. Annonce 100 % bilingue français / anglais (clientèle UK)

Avec 40 % de clientèle internationale dont 25 % de Britanniques, une annonce mal traduite plafonne à 60 % d'occupation. Nos rédacteurs anglophones natifs créent une version EN qui débloque les **15-20 % de revenus supplémentaires**.

### 3. Livret d'accueil dédié Saint-Lunaire

Notre livret d'accueil inclut :
- Parcours panoramique (Pointe du Décollé, GR34 vers la Pointe du Nick)
- Les 2 plages détaillées avec horaires de marées
- Calendrier du Golf de Saint-Lunaire + initiation pour débutants
- Sélection restaurants (Le Décollé étoilé, La Touline, Crêperie de la Plage)
- Excursions Dinard / Saint-Malo / Cap Fréhel
- Guide bilingue complet pour clientèle UK

Résultat : **note moyenne 4,9/5** sur nos biens Saint-Lunaire.

### 4. Pricing événementiel calé sur les pics

Notre algorithme pricing applique automatiquement :
- **+60 % pendant le Festival du film britannique de Dinard** (4 jours, octobre)
- **+35 % pendant les Open de golf** (printemps + automne)
- **+50 % sur les ponts de mai**
- **+25 % pendant le festival Étonnants Voyageurs** (mai)

Sans pricing événementiel, vous laissez **1 200 à 2 000 € sur la table chaque année** sur le secteur Saint-Lunaire.

### 5. Équipement haut de gamme attendu par la clientèle

La clientèle Saint-Lunaire est plus exigeante que la moyenne. Pour optimiser le ranking : linge de qualité hôtelière, café Nespresso, vaisselle assortie, livres, plaids, vélos électriques, kit pique-nique pour la plage. Investissement : 1 500 à 2 500 €. ROI : +25 % de réservations et +0,3 point de note moyenne.

### 6. Une équipe locale à 7 minutes

Notre City Manager basé à Pleurtuit intervient sur Saint-Lunaire **en moins de 15 minutes**. Pour les voyageurs internationaux, savoir qu'il y a un humain joignable 24h/24 à proximité est **le critère #1 de confiance**.

## Investir à Saint-Lunaire en 2026 : opportunités

Le marché immobilier de Saint-Lunaire reste **25 à 30 % moins cher qu'à Dinard centre** à typologie équivalente :

- **T2 50 m²** : 195 000 à 260 000 €
- **Maison de plain-pied 90 m² avec jardin** : 320 000 à 420 000 €
- **Villa Belle Époque 130 m²** : 470 000 à 640 000 €
- **Villa vue mer 6-8 pers.** : 580 000 à 850 000 €

Avec un revenu Airbnb annuel net de 22 000 à 58 000 €, vous obtenez un **rendement brut de 8 à 11 %**, au-dessus de Dinard (6-8 %). Voir notre [étude chiffrée investissement Saint-Lunaire 2026](/blog/investir-airbnb-saint-lunaire-rendement-2026).

## Réglementation : ce qu'il faut savoir avant de louer

Saint-Lunaire applique **la loi Le Meur de 2024** :

- Déclaration en mairie obligatoire (formulaire CERFA)
- Numéro d'enregistrement à 13 chiffres à afficher sur l'annonce
- Taxe de séjour collectée automatiquement par Airbnb / Booking
- **Pas de quota actuel ni de limite de jours** pour les résidences secondaires

Notre équipe se charge intégralement de la déclaration en mairie. Délai moyen : 3 à 5 jours ouvrés. [Voir le détail des démarches dans notre guide réglementation 2026](/blog/reglementation-airbnb-saint-malo-dinard-cancale-2026).

## Notre conviction sur Saint-Lunaire

Saint-Lunaire combine **prestige Belle Époque + golf historique + 2 plages + panorama Pointe du Décollé** dans un cadre confidentiel. C'est, à nos yeux, **l'une des meilleures opportunités Airbnb 2026 de la Côte d'Émeraude**. Saison étendue, clientèle premium internationale, événements pricing puissants, faible concurrence : tous les ingrédients sont réunis.

Si vous possédez un bien à Saint-Lunaire, ou si vous envisagez d'en acquérir un, nous proposons une **étude personnalisée gratuite sous 24 h**, sans engagement.

[**Voir notre offre Saint-Lunaire →**](/villes/saint-lunaire)
[**Lire l'étude chiffrée investissement Saint-Lunaire →**](/blog/investir-airbnb-saint-lunaire-rendement-2026)
[**Demander une étude personnalisée →**](/contact)
`,
  },

  // =====================================================================
  // 37, Investir à Saint-Lunaire : Airbnb vs locatif classique (FÉV 2026)
  // =====================================================================
  {
    slug: "investir-airbnb-saint-lunaire-rendement-2026",
    category: "ville-guide",
    title: "Investir à Saint-Lunaire en 2026 : rendement Airbnb vs locatif classique (étude chiffrée)",
    excerpt:
      "T2, maison familiale, villa Belle Époque : combien rapporte vraiment un bien à Saint-Lunaire ? Comparatif Airbnb / location nue / location meublée annuelle, simulation à 10 ans et fiscalité LMNP. Le guide investisseur 2026.",
    image: "/images/blog/investir-airbnb-saint-lunaire-rendement-2026.jpg",
    imageAlt: "Villas Belle Époque à colombages perchées sur la falaise au-dessus d'une crique de Saint-Lunaire, investissement immobilier Côte d'Émeraude",
    author: "L'équipe Terre et Mer",
    date: "2026-03-05",
    readTime: 10,
    keywords:
      "investir saint-lunaire, investir airbnb saint-lunaire, rendement airbnb saint-lunaire, acheter pour louer saint-lunaire, rentabilité airbnb dinard saint-lunaire, lmnp saint-lunaire, simulation airbnb saint-lunaire, prix immobilier saint-lunaire 2026, airbnb vs locatif classique côte émeraude",
    relatedCities: ["saint-lunaire"],
    content: `
## Saint-Lunaire en 2026 : pourquoi c'est l'investissement Airbnb le plus malin de la Côte d'Émeraude

Sur la Côte d'Émeraude, deux logiques s'affrontent en 2026 pour un investisseur :

1. **Acheter à Dinard** : prestige, mais ticket d'entrée 5 800 à 7 200 €/m², concurrence Airbnb féroce, rendement net 5-7 %
2. **Acheter à Saint-Lunaire** : même prestige Belle Époque, même panorama, mais **25 à 30 % moins cher au m²**, concurrence 3× plus faible, rendement net 8-11 %

Dans ce guide, nous comparons **chiffres à l'appui** les 3 options qui s'offrent à un propriétaire ou futur acquéreur à Saint-Lunaire :

- **Option A** : location nue (locataire à l'année)
- **Option B** : location meublée annuelle (LMNP standard)
- **Option C** : Airbnb / courte durée pilotée par notre conciergerie (15 % TTC)

Trois cas concrets, trois tableaux de rentabilité.

## Le marché immobilier de Saint-Lunaire en 2026

| Typologie | Prix au m² 2026 | Prix moyen complet |
|---|---|---|
| Studio 30 m² | 4 200 à 5 000 €/m² | 130 000 à 165 000 € |
| T2 50 m² | 3 900 à 4 800 €/m² | 195 000 à 260 000 € |
| Maison de plain-pied 90 m² + jardin | 3 600 à 4 500 €/m² | 320 000 à 420 000 € |
| Villa Belle Époque 130 m² | 4 200 à 5 600 €/m² | 470 000 à 640 000 € |
| Villa vue mer 6-8 pers. | 4 800 à 6 500 €/m² | 580 000 à 850 000 € |

Pour comparer : Dinard centre tourne autour de **5 800 à 7 200 €/m²**, [Lancieux](/villes/lancieux) à **3 600 à 4 500 €/m²**, Saint-Briac à **4 500 à 5 800 €/m²**. Saint-Lunaire est **15 à 25 % moins cher que Dinard** mais bénéficie du même bassin de clientèle.

## Cas n°1, T2 50 m² avec balcon, à 5 min à pied de la Grande Plage

**Investissement total** : 225 000 € (acquisition) + 13 500 € (notaire) + 9 000 € (ameublement & équipement Airbnb haut de gamme) = **247 500 €**

### Option A, Location nue à l'année

- Loyer : 680 €/mois → **8 160 €/an**
- Charges déductibles : 1 400 € (taxe foncière, copro, assurance PNO)
- **Revenu net annuel : 6 760 €**
- Rendement net : **2,7 %**

### Option B, Location meublée annuelle (LMNP, micro-BIC)

- Loyer : 830 €/mois → **9 960 €/an**
- Abattement micro-BIC 50 % → impôt sur 4 980 €
- Charges : 1 700 €
- **Revenu net annuel ≈ 7 700 €**
- Rendement net : **3,1 %**

### Option C, Airbnb piloté par Terre et Mer (commission 15 % TTC)

- Tarif moyen : 95 €/nuit
- Taux d'occupation pilotage Terre et Mer : 73 %
- Nuits louées par an : 266
- **Chiffre d'affaires brut : 25 270 €**
- Commission 15 % TTC : −3 790 €
- Charges (taxe foncière, copro, assurance, équipement) : −2 500 €
- Régime LMNP réel (amortissement bien + mobilier) : impôt quasi-nul les 8 premières années
- **Revenu net annuel ≈ 18 980 €**
- Rendement net : **7,7 %**

➡️ **Différentiel Airbnb vs locatif nu : +12 220 €/an**, soit **+ 181 %** de rentabilité. Sur 10 ans, l'écart cumulé dépasse **122 000 €**.

## Cas n°2, Maison familiale 4-6 personnes, jardin clos, à pied du Décollé

**Investissement total** : 395 000 € + 23 700 € + 16 000 € = **434 700 €**

### Option A, Location nue

- Loyer : 1 200 €/mois → **14 400 €/an**
- Charges : 2 400 €
- **Net : 12 000 €** → rendement **2,8 %**

### Option B, Meublée annuelle (LMNP)

- Loyer : 1 450 €/mois → **17 400 €/an**
- Net après charges et fiscalité : **13 700 €** → rendement **3,2 %**

### Option C, Airbnb 15 % TTC Terre et Mer

- Tarif moyen : 175 €/nuit (équipement famille premium + jardin clos + vue partielle mer)
- Occupation pilotée : 75 %
- **CA brut : 47 900 €**
- Commission : −7 185 €
- Charges : −4 200 €
- Fiscalité LMNP réel : impôt quasi-nul
- **Net annuel ≈ 36 515 €** → rendement **8,4 %**

➡️ **Différentiel Airbnb vs nu : +24 515 €/an**. **+204 %** de rentabilité. Sur 10 ans : **+245 000 €**.

## Cas n°3, Villa Belle Époque 130 m², 6-8 personnes, vue mer

**Investissement total** : 610 000 € + 36 600 € + 22 000 € = **668 600 €**

| Option | Revenu net annuel | Rendement net |
|---|---|---|
| **A**, Location nue | 16 800 € | 2,5 % |
| **B**, Meublée annuelle (LMNP) | 20 100 € | 3,0 % |
| **C**, Airbnb 15 % Terre et Mer | **54 200 €** | **8,1 %** |

Sur 10 ans, l'écart entre Airbnb et locatif nu approche **+375 000 €**.

## Pourquoi Saint-Lunaire performe autant mieux que Dinard à l'investissement ?

Trois facteurs structurels :

### 1. Le ticket d'entrée 25-30 % moins cher

À typologie équivalente, vous achetez à Saint-Lunaire ce qui vous coûterait 130 à 200 000 € de plus à Dinard. Cette économie se traduit directement en rendement supérieur (~3 points de plus).

### 2. Le tarif/nuit Airbnb proche de Dinard

Saint-Lunaire tire à 95 €/nuit (moyenne) vs 120 €/nuit à Dinard (moyenne). L'écart est faible (-20 %) alors que l'écart prix immobilier est de -30 %. **Saint-Lunaire offre donc une meilleure équation rentabilité.**

### 3. La concurrence Airbnb 3× plus faible

Saint-Lunaire compte ~400 annonces Airbnb actives contre ~1 200 à Dinard. Moins de concurrence = meilleur ranking dans les recherches Airbnb = meilleur taux d'occupation.

## Simulation à 10 ans : T2 acheté en 2026, revendu en 2036

Hypothèses : T2 50 m² à 225 000 €, financé à 80 % sur 20 ans à 3,5 %.

| Critère | Location nue | Meublée annuelle | Airbnb 15 % T&M |
|---|---|---|---|
| Cumul revenus nets 10 ans | 67 600 € | 77 000 € | **189 800 €** |
| Cashflow mensuel net | −720 € | −600 € | **+490 €** |
| Plus-value à 10 ans (estim.) | +95 000 € | +95 000 € | +95 000 € |
| **Capital total à 10 ans** | **+162 600 €** | **+172 000 €** | **+284 800 €** |

➡️ **L'Airbnb piloté permet l'autofinancement** dès la 1ʳᵉ année. Sur 10 ans : **+122 000 € de capital supplémentaire** vs la location nue.

## La formule conciergerie Terre et Mer

- **Commission unique 15 % TTC** sans frais cachés
- **Pricing dynamique** calé sur Festival du film britannique de Dinard, Open de golf, ponts de mai
- **Accueil voyageurs 24h/24 · 7j/7**
- **Kit d'accueil inclus** : Shampooing, gel douche, papier toilette, produit vaisselle, éponge, sac-poubelle, petit sac pour la salle de bain
- **Linge non inclus** dans la formule 15 % (option voyageur dédiée)
- **City Manager** basé à Pleurtuit, intervention en moins de 15 minutes sur Saint-Lunaire

## Pour aller plus loin

- 📖 [Le guide propriétaire Saint-Lunaire complet 2026](/blog/louer-airbnb-saint-lunaire-cote-emeraude-2026)
- 💼 [LMNP Bretagne : fiscalité 2026 expliquée](/blog/lmnp-airbnb-bretagne-fiscalite-2026)
- 📊 [Brut, net, cashflow : lire les vrais chiffres de votre Airbnb](/blog/rentabilite-brute-nette-cashflow-airbnb-saint-malo)
- 🌊 [Investir Airbnb sur la Côte d'Émeraude en 2026, vue d'ensemble](/blog/investir-airbnb-cote-emeraude-bretagne-2026)
- 🏖️ [Investir à Lancieux en 2026 (comparatif voisin)](/blog/investir-airbnb-lancieux-rendement-2026)

## Vous envisagez d'investir à Saint-Lunaire ?

Nous proposons une **étude personnalisée gratuite sous 24 h**. Notre City Manager peut aussi vous accompagner avant l'achat (visite, étude technique, conseils ameublement). Cet accompagnement amont, **gratuit et sans engagement**, est devenu un standard pour nos investisseurs.

[**Voir notre offre Saint-Lunaire →**](/villes/saint-lunaire)
[**Demander une étude personnalisée gratuite →**](/contact)
[**Lire le guide propriétaire Saint-Lunaire →**](/blog/louer-airbnb-saint-lunaire-cote-emeraude-2026)
`,
  },

  // =====================================================================
  // 38, Pleurtuit : guide propriétaire bords de Rance (MARS 2026)
  // =====================================================================
  {
    slug: "louer-airbnb-pleurtuit-rance-2026",
    category: "ville-guide",
    title: "Louer son Airbnb à Pleurtuit : le guide propriétaire 2026 (Côte d'Émeraude & Rance)",
    excerpt:
      "Bords de Rance, longères en pierre, 5 min de Dinard sans en payer le prix : pourquoi Pleurtuit est l'investissement Airbnb le plus malin de la Côte d'Émeraude. Guide propriétaire 2026.",
    image: "/images/blog/louer-airbnb-pleurtuit-rance-2026.jpg",
    imageAlt: "Port mouillage sur les bords de Rance à Pleurtuit, voiliers et reflets, Côte d'Émeraude",
    author: "L'équipe Terre et Mer",
    date: "2026-03-09",
    readTime: 9,
    keywords:
      "louer airbnb pleurtuit, conciergerie pleurtuit, prix nuit pleurtuit, location courte durée pleurtuit, airbnb bords de rance, location longère pleurtuit, conciergerie côte émeraude pleurtuit, déclaration mairie pleurtuit airbnb, airbnb 5 min dinard, airbnb gr34 rance",
    relatedCities: ["pleurtuit"],
    content: `
## Pleurtuit, l'outsider stratégique de la Côte d'Émeraude

À **5 minutes de Dinard**, **10 minutes de Saint-Lunaire**, **15 minutes de Saint-Malo**, Pleurtuit est l'une des destinations Airbnb les plus stratégiques de Bretagne nord. C'est aussi notre **ville-bureau historique**, nous y sommes implantés depuis nos débuts, ce qui nous permet d'intervenir sur n'importe quel bien en moins de **10 minutes**.

Pleurtuit profite à **100 % du bassin touristique premium de Dinard** sans en payer le prix immobilier : décote de **30 à 35 % au m²** à typologie équivalente. Plus de **12 000 voyageurs Airbnb par an**, un tarif moyen de **75 € la nuit**, une saison étendue d'avril à octobre, des bords de Rance préservés et un patrimoine de longères bretonnes : voici pourquoi nos propriétaires pleurtuitiens dépassent systématiquement +30 à +45 % de revenus par rapport à un bien piloté en autonome.

## Le marché Airbnb de Pleurtuit en chiffres (2026)

- **Prix moyen / nuit** : 75 € (toutes typologies confondues)
- **Voyageurs Airbnb / an** : 12 000+ (en croissance de +12 % depuis 2023)
- **Taux d'occupation moyen** : 68 % (médiane Bretagne : 65 %)
- **Saison principale** : avril à octobre, avec un pic juillet-août + Festival du film britannique de Dinard (octobre)
- **Type de bien le plus demandé** : longère ou maison familiale 4-6 personnes avec jardin
- **Clientèle internationale** : 28 % (principalement britannique via ferry Saint-Malo–Portsmouth)
- **Origine France** : Île-de-France (42 %), Grand Ouest (38 %), Sud-Ouest (12 %), autres (8 %)

Pour comparer : [Saint-Lunaire](/villes/saint-lunaire) tire à 95 €/nuit mais avec un ticket d'entrée immobilier +25 %. Dinard tourne à 120 €/nuit avec une concurrence 5× plus forte. **Pleurtuit est le sweet spot rentabilité/accessibilité** de la Côte d'Émeraude.

## Les 5 atouts uniques de Pleurtuit pour votre bien

### 1. Dinard à 5 minutes sans en payer le prix

Tous les voyageurs qui rêvent de Dinard ne peuvent pas se payer Dinard. Avec une décote immobilière de **30 à 35 %**, Pleurtuit capture cette demande surnuméraire. Le voyageur réserve en se disant *« 5 minutes de Dinard, c'est parfait »* et bénéficie en plus du calme rural, un argument différenciant pour les familles qui veulent éviter la foule du centre Dinard.

À valoriser dans le titre de l'annonce : *« 5 min Dinard ★ Calme + Jardin »* convertit **+25 % de clics** vs un titre générique.

### 2. Les bords de Rance et les moulins à marée

L'estuaire de la Rance traverse Pleurtuit et abrite un patrimoine industriel unique : **moulins à marée** (Moulin du Prat, Moulin de Beauchet), **anciennes salines**, **écluse du Châtelier**. C'est un cadre rural authentique très recherché par les amateurs de patrimoine et les retraités actifs. Le **sentier GR34** longe la Rance et offre des balades exceptionnelles vers Le Minihic-sur-Rance et Saint-Suliac.

Pour les biens situés à moins de 1 km de la Rance, surcote moyenne de **+15 à +25 %** sur l'annonce.

### 3. Le Festival du film britannique de Dinard (à 5 min)

Début octobre, Dinard accueille le **Festival du film britannique**, l'un des plus grands événements cinématographiques d'Europe. **Hôtels Dinard saturés → débordement massif sur Pleurtuit**. Notre pricing applique +50 % sur les 4 jours, et nos biens affichent un taux d'occupation de **88 à 95 %** pendant cette fenêtre. C'est LE pic SEO de la commune.

### 4. La proximité multi-pôles (Côte d'Émeraude entière en 30 min)

Depuis Pleurtuit, vos voyageurs accèdent en moins de 30 minutes à :
- **Dinard** (5 min), plages, casino, festival du film britannique
- **Saint-Lunaire** (10 min), Pointe du Décollé, golf 1887
- **Saint-Briac** (15 min), village d'artistes, golf 18 trous
- **Saint-Malo** intra-muros (15 min), remparts, ferry UK
- **Cancale** (25 min), port d'huîtres, Pointe du Grouin
- **Cap Fréhel** (35 min), falaises, Fort La Latte

Cette **centralité géographique** est unique. À valoriser dans le livret d'accueil avec un plan de localisation et des itinéraires journaliers.

### 5. La typologie "longère bretonne" recherchée

Pleurtuit a un parc immobilier dominé par les **longères en pierre** et **maisons rurales avec jardin**. Cette typologie est en très forte demande sur Airbnb (familles, groupes d'amis, séjours longs hors saison) et permet de tirer des paniers moyens élevés grâce à la capacité d'accueil (6-8 personnes). Avec photos lumière dorée valorisant pierres apparentes, poutres et jardin clos, vos annonces convertissent **+45 % de clics** vs photos en lumière neutre.

## Saisonnalité et pricing dynamique : le tableau réel

| Période | Prix moyen | Taux d'occupation | Levier pricing |
|---|---|---|---|
| Janvier - mars | 50 € | 32 % | Stratégie séjours longs (télétravail, retraités) |
| Avril (Pâques) | 85 € | 72 % | Démarrage saison + week-ends |
| Mai (ponts) | 95 € | 80 % | Ascension + Pentecôte + Étonnants Voyageurs |
| Juin | 78 € | 68 % | Pré-saison, montée en charge |
| **Juillet - août** | **125 €** | **88 %** | Pic absolu, pricing semaine par semaine |
| Septembre | 90 € | 72 % | Microclimat doux, golfeurs internationaux |
| **Octobre (Festival film britannique)** | **115 €** | **88 %** | +50 % sur 4 jours festival |
| Novembre - décembre | 55 € | 38 % | Toussaint, séjours longs |

**Conclusion** : un T2 bien piloté à Pleurtuit génère **17 000 à 23 000 € de revenus annuels nets** après commission de 15 % TTC. Une longère 6-8 personnes avec jardin peut dépasser **38 000 €** par an.

## Comment faire performer votre bien à Pleurtuit : nos 5 leviers

### 1. Positionnement "5 min Dinard + Bords de Rance"

Le titre de votre annonce doit faire apparaître l'un de ces 2 mots-clés (ou les deux). C'est ce qui déclenche le clic. Bon exemple : *« Longère 5 min Dinard ★ Bords de Rance ★ Jardin clos »*. Mauvais exemple : *« Charmante maison bretonne en Ille-et-Vilaine »*.

### 2. Annonce 100 % bilingue français / anglais

Avec 28 % de clientèle internationale (principalement britannique via ferry), une annonce mal traduite plafonne. Nos rédacteurs anglophones natifs créent une version EN qui débloque **+15 à +20 % de revenus** auprès de cette clientèle premium récurrente.

### 3. Photos premium en lumière dorée + valorisation longère

Notre photographe planifie le shooting au lever ou coucher du soleil + capture les détails caractéristiques (pierres apparentes, poutres, cheminée, jardin clos). Les longères mal photographiées plafonnent à 55 % d'occupation. Bien photographiées : **75 %+**.

### 4. Pricing événementiel calé sur Dinard

Notre algorithme pricing intègre automatiquement :
- **+50 % pendant le Festival du film britannique de Dinard** (4 jours, octobre)
- **+35 % pendant les Open de golf Dinard/Saint-Lunaire**
- **+45 % sur les ponts de mai**
- **+25 % pendant le festival Étonnants Voyageurs Saint-Malo**

Sans pricing événementiel, vous laissez **800 à 1 400 € sur la table chaque année** sur le secteur Pleurtuit.

### 5. Notre bureau principal est à Pleurtuit

C'est notre atout différenciant maximal. Notre City Manager Côte d'Émeraude est basé à Pleurtuit même, intervention en moins de **10 minutes** sur n'importe quel bien de la commune. Aucune autre conciergerie n'a cette proximité physique sur Pleurtuit.

## Investir à Pleurtuit en 2026 : opportunités

Le marché immobilier de Pleurtuit reste **30 à 35 % moins cher qu'à Dinard centre** à typologie équivalente :

- **T2 50 m²** : 155 000 à 200 000 €
- **Maison de plain-pied 90 m² avec jardin** : 250 000 à 330 000 €
- **Longère 130 m² avec terrain** : 320 000 à 430 000 €
- **Maison vue Rance 140 m²** : 380 000 à 510 000 €

Avec un revenu Airbnb annuel net de 17 000 à 38 000 €, vous obtenez un **rendement brut de 8 à 12 %**, au-dessus de Dinard (6-8 %). Voir le détail dans notre [étude chiffrée investissement Pleurtuit 2026](/blog/investir-airbnb-pleurtuit-rendement-2026).

## Réglementation : ce qu'il faut savoir avant de louer

Pleurtuit applique **la loi Le Meur de 2024** :

- Déclaration en mairie obligatoire (formulaire CERFA)
- Numéro d'enregistrement à 13 chiffres à afficher sur l'annonce
- Taxe de séjour collectée automatiquement par Airbnb / Booking
- **Pas de quota actuel ni de limite de jours** pour les résidences secondaires

Notre équipe se charge intégralement de la déclaration en mairie. Délai moyen : 3 à 5 jours ouvrés. Voir notre [guide réglementation 2026](/blog/reglementation-airbnb-saint-malo-dinard-cancale-2026).

## Notre conviction sur Pleurtuit

Pleurtuit combine **proximité Dinard + tarifs accessibles + bords de Rance + longères de caractère + notre bureau principal**. C'est, à nos yeux, **l'investissement Airbnb le plus stratégique de la Côte d'Émeraude en 2026**. Saison étendue, événements pricing puissants, ticket d'entrée modéré, équipe locale ultra-réactive : tous les ingrédients sont réunis.

Si vous possédez un bien à Pleurtuit, ou si vous envisagez d'en acquérir un, nous proposons une **étude personnalisée gratuite sous 24 h**, sans engagement.

[**Voir notre offre Pleurtuit →**](/villes/pleurtuit)
[**Lire l'étude chiffrée investissement Pleurtuit →**](/blog/investir-airbnb-pleurtuit-rendement-2026)
[**Demander une étude personnalisée →**](/contact)
`,
  },

  // =====================================================================
  // 39, Investir à Pleurtuit : Airbnb vs locatif classique (MARS 2026)
  // =====================================================================
  {
    slug: "investir-airbnb-pleurtuit-rendement-2026",
    category: "ville-guide",
    title: "Investir à Pleurtuit en 2026 : rendement Airbnb vs locatif classique (étude chiffrée)",
    excerpt:
      "T2, maison familiale, longère vue Rance : combien rapporte vraiment un bien à Pleurtuit ? Comparatif Airbnb / location nue / location meublée annuelle, simulation à 10 ans et fiscalité LMNP. Le guide investisseur 2026.",
    image: "/images/blog/investir-airbnb-pleurtuit-rendement-2026.jpg",
    imageAlt: "Vue du bourg de Pleurtuit et bords de Rance, investissement immobilier locatif Côte d'Émeraude",
    author: "L'équipe Terre et Mer",
    date: "2026-03-12",
    readTime: 10,
    keywords:
      "investir pleurtuit, investir airbnb pleurtuit, rendement airbnb pleurtuit, acheter pour louer pleurtuit, rentabilité airbnb dinard pleurtuit, lmnp pleurtuit, simulation airbnb pleurtuit, prix immobilier pleurtuit 2026, airbnb vs locatif classique côte émeraude pleurtuit",
    relatedCities: ["pleurtuit"],
    content: `
## Pleurtuit en 2026 : pourquoi c'est l'un des investissements Airbnb les plus malins de la Côte d'Émeraude

Sur la Côte d'Émeraude, deux logiques s'affrontent en 2026 pour un investisseur :

1. **Acheter à Dinard** : prestige, mais ticket d'entrée 5 800 à 7 200 €/m², concurrence Airbnb féroce, rendement net 5-7 %
2. **Acheter à Pleurtuit** : même bassin touristique (5 min de Dinard), mais **30 à 35 % moins cher au m²**, concurrence 4× plus faible, rendement net 8-12 %

Dans ce guide, nous comparons **chiffres à l'appui** les 3 options qui s'offrent à un propriétaire ou futur acquéreur à Pleurtuit :

- **Option A** : location nue (locataire à l'année)
- **Option B** : location meublée annuelle (LMNP standard)
- **Option C** : Airbnb / courte durée pilotée par notre conciergerie (15 % TTC)

Trois cas concrets, trois tableaux de rentabilité.

## Le marché immobilier de Pleurtuit en 2026

| Typologie | Prix au m² 2026 | Prix moyen complet |
|---|---|---|
| Studio 30 m² | 3 400 à 4 200 €/m² | 105 000 à 135 000 € |
| T2 50 m² | 3 100 à 4 000 €/m² | 155 000 à 200 000 € |
| Maison de plain-pied 90 m² + jardin | 2 800 à 3 700 €/m² | 250 000 à 330 000 € |
| Longère 130 m² avec terrain | 2 500 à 3 300 €/m² | 320 000 à 430 000 € |
| Maison vue Rance 140 m² | 2 800 à 3 700 €/m² | 380 000 à 510 000 € |

Pour comparer : Dinard centre tourne autour de **5 800 à 7 200 €/m²**, [Saint-Lunaire](/villes/saint-lunaire) à **3 900 à 4 800 €/m²**, [Lancieux](/villes/lancieux) à **3 600 à 4 500 €/m²**. Pleurtuit est **30 à 35 % moins cher que Dinard** mais bénéficie du même bassin de clientèle.

## Cas n°1, T2 50 m² avec balcon, à 5 min en voiture de Dinard

**Investissement total** : 180 000 € (acquisition) + 11 000 € (notaire) + 8 000 € (ameublement & équipement Airbnb) = **199 000 €**

### Option A, Location nue à l'année

- Loyer : 560 €/mois → **6 720 €/an**
- Charges déductibles : 1 200 € (taxe foncière, copro, assurance PNO)
- **Revenu net annuel : 5 520 €**
- Rendement net : **2,8 %**

### Option B, Location meublée annuelle (LMNP, micro-BIC)

- Loyer : 690 €/mois → **8 280 €/an**
- Abattement micro-BIC 50 % → impôt sur 4 140 €
- Charges : 1 500 €
- **Revenu net annuel ≈ 6 400 €**
- Rendement net : **3,2 %**

### Option C, Airbnb piloté par Terre et Mer (commission 15 % TTC)

- Tarif moyen : 75 €/nuit
- Taux d'occupation pilotage Terre et Mer : 68 %
- Nuits louées par an : 248
- **Chiffre d'affaires brut : 18 600 €**
- Commission 15 % TTC : −2 790 €
- Charges (taxe foncière, copro, assurance, équipement) : −2 200 €
- Régime LMNP réel (amortissement bien + mobilier) : impôt quasi-nul les 8 premières années
- **Revenu net annuel ≈ 13 610 €**
- Rendement net : **6,8 %**

➡️ **Différentiel Airbnb vs locatif nu : +8 090 €/an**, soit **+ 147 %** de rentabilité. Sur 10 ans, l'écart cumulé dépasse **80 000 €**.

## Cas n°2, Maison familiale 4-6 personnes, jardin clos, 7 min de Dinard

**Investissement total** : 290 000 € + 17 400 € + 12 000 € = **319 400 €**

### Option A, Location nue

- Loyer : 920 €/mois → **11 040 €/an**
- Charges : 1 900 €
- **Net : 9 140 €** → rendement **2,9 %**

### Option B, Meublée annuelle (LMNP)

- Loyer : 1 130 €/mois → **13 560 €/an**
- Net après charges et fiscalité : **10 700 €** → rendement **3,4 %**

### Option C, Airbnb 15 % TTC Terre et Mer

- Tarif moyen : 130 €/nuit (équipement famille premium + jardin clos)
- Occupation pilotée : 70 %
- **CA brut : 33 200 €**
- Commission : −4 980 €
- Charges : −3 200 €
- Fiscalité LMNP réel : impôt quasi-nul
- **Net annuel ≈ 25 020 €** → rendement **7,8 %**

➡️ **Différentiel Airbnb vs nu : +15 880 €/an**. **+174 %** de rentabilité. Sur 10 ans : **+159 000 €**.

## Cas n°3, Longère 130 m², 6-8 personnes, vue Rance

**Investissement total** : 380 000 € + 22 800 € + 16 000 € = **418 800 €**

| Option | Revenu net annuel | Rendement net |
|---|---|---|
| **A**, Location nue | 12 200 € | 2,9 % |
| **B**, Meublée annuelle (LMNP) | 14 600 € | 3,5 % |
| **C**, Airbnb 15 % Terre et Mer | **38 100 €** | **9,1 %** |

Sur 10 ans, l'écart entre Airbnb et locatif nu approche **+260 000 €**.

## Pourquoi Pleurtuit performe autant mieux que Dinard à l'investissement ?

Trois facteurs structurels :

### 1. Le ticket d'entrée 30-35 % moins cher

À typologie équivalente, vous achetez à Pleurtuit ce qui vous coûterait 80 à 150 000 € de plus à Dinard. Cette économie se traduit directement en rendement supérieur (~3 points de plus).

### 2. La concurrence Airbnb 4× plus faible

Pleurtuit compte ~280 annonces Airbnb actives contre ~1 200 à Dinard. Moins de concurrence = meilleur ranking dans les recherches Airbnb = meilleur taux d'occupation.

### 3. Le tarif/nuit Airbnb plus proche de Dinard qu'on ne le croit

Pleurtuit tire à 75 €/nuit (moyenne) vs 120 €/nuit à Dinard (moyenne). L'écart est de -38 % alors que l'écart prix immobilier est de -33 %. **À ticket d'entrée équivalent, Pleurtuit offre la meilleure équation rentabilité de la Côte d'Émeraude.**

## Simulation à 10 ans : T2 acheté en 2026, revendu en 2036

Hypothèses : T2 50 m² à 180 000 €, financé à 80 % sur 20 ans à 3,5 %.

| Critère | Location nue | Meublée annuelle | Airbnb 15 % T&M |
|---|---|---|---|
| Cumul revenus nets 10 ans | 55 200 € | 64 000 € | **136 100 €** |
| Cashflow mensuel net | −585 € | −485 € | +245 € |
| Plus-value à 10 ans (estim.) | +75 000 € | +75 000 € | +75 000 € |
| **Capital total à 10 ans** | **+130 200 €** | **+139 000 €** | **+211 100 €** |

➡️ **L'Airbnb piloté permet l'autofinancement** dès la 1ʳᵉ année. Sur 10 ans : **+81 000 € de capital supplémentaire** vs la location nue.

## La formule conciergerie Terre et Mer

- **Commission unique 15 % TTC** sans frais cachés
- **Pricing dynamique** calé sur Festival du film britannique de Dinard, Open de golf, ponts de mai
- **Accueil voyageurs 24h/24 · 7j/7**
- **Kit d'accueil inclus** : Shampooing, gel douche, papier toilette, produit vaisselle, éponge, sac-poubelle, petit sac pour la salle de bain
- **Linge non inclus** dans la formule 15 % (option voyageur dédiée)
- **City Manager** basé à Pleurtuit même, intervention en moins de 10 minutes

## Pour aller plus loin

- 📖 [Le guide propriétaire Pleurtuit complet 2026](/blog/louer-airbnb-pleurtuit-rance-2026)
- 💼 [LMNP Bretagne : fiscalité 2026 expliquée](/blog/lmnp-airbnb-bretagne-fiscalite-2026)
- 📊 [Brut, net, cashflow : lire les vrais chiffres de votre Airbnb](/blog/rentabilite-brute-nette-cashflow-airbnb-saint-malo)
- 🌊 [Investir Airbnb sur la Côte d'Émeraude en 2026, vue d'ensemble](/blog/investir-airbnb-cote-emeraude-bretagne-2026)
- 🏖️ [Investir à Saint-Lunaire en 2026 (comparatif voisin direct)](/blog/investir-airbnb-saint-lunaire-rendement-2026)

## Vous envisagez d'investir à Pleurtuit ?

Nous proposons une **étude personnalisée gratuite sous 24 h**. Notre City Manager peut aussi vous accompagner avant l'achat (visite, étude technique, conseils ameublement). Cet accompagnement amont, **gratuit et sans engagement**, est devenu un standard pour nos investisseurs.

[**Voir notre offre Pleurtuit →**](/villes/pleurtuit)
[**Demander une étude personnalisée gratuite →**](/contact)
[**Lire le guide propriétaire Pleurtuit →**](/blog/louer-airbnb-pleurtuit-rance-2026)
`,
  },







  // =====================================================================
  // 40, Saint-Jacut-de-la-Mer : guide propriétaire presqu'île (MARS 2026)
  // =====================================================================
  {
    slug: "louer-airbnb-saint-jacut-de-la-mer-presquile-2026",
    category: "ville-guide",
    title: "Louer son Airbnb à Saint-Jacut-de-la-Mer : le guide propriétaire 2026 (presqu'île aux 11 plages)",
    excerpt:
      "11 plages, Îles des Ébihens, GR34 en boucle, port de pêche actif : pourquoi Saint-Jacut-de-la-Mer est l'une des destinations Airbnb les plus différenciantes de la Côte d'Émeraude. Guide complet 2026.",
    image: "/images/blog/louer-airbnb-saint-jacut-de-la-mer-presquile-2026.jpg",
    imageAlt: "Vue aérienne de la presqu'île de Saint-Jacut-de-la-Mer avec ses plages et les Îles des Ébihens en arrière-plan, Côte d'Émeraude",
    author: "L'équipe Terre et Mer",
    date: "2026-03-16",
    readTime: 9,
    keywords:
      "louer airbnb saint-jacut-de-la-mer, conciergerie saint-jacut, prix nuit saint-jacut, location courte durée saint-jacut, airbnb presqu'île bretagne, airbnb îles des ébihens, location plage côte émeraude, gîte saint-jacut, déclaration mairie saint-jacut airbnb, airbnb pêche à pied bretagne",
    relatedCities: ["saint-jacut-de-la-mer"],
    content: `
## Saint-Jacut-de-la-Mer, la presqu'île aux 11 plages

À **5 minutes de Lancieux**, **15 minutes de Saint-Cast-Le Guildo** et **25 minutes de Dinard**, **Saint-Jacut-de-la-Mer est unique en Bretagne**. C'est une presqu'île étroite de **4 km de long** bordée de **11 plages distinctes**, du sable fin de la Plage du Rougeret aux galets de la Plage du Châtelet, en passant par les Îles des Ébihens accessibles à marée basse. Pour un voyageur Airbnb, cette densité de plages est un **argument décisionnel rare** : selon le vent et la marée, il trouvera toujours une plage adaptée.

Plus de **16 000 voyageurs Airbnb par an**, un tarif moyen de **85 € la nuit** (+9 % vs moyenne Côte d'Émeraude), un taux d'occupation de **73 %** et une clientèle familiale fidèle qui revient chaque été : voici pourquoi nos propriétaires jaguens dépassent systématiquement +30 à +45 % de revenus par rapport à un bien piloté en autonome.

## Le marché Airbnb de Saint-Jacut en chiffres (2026)

- **Prix moyen / nuit** : 85 € (+9 % vs moyenne Côte d'Émeraude)
- **Voyageurs Airbnb / an** : 16 000+ (en croissance de +13 % depuis 2023)
- **Taux d'occupation moyen** : 73 % (médiane Bretagne : 65 %)
- **Saison principale** : avril à octobre, avec un pic juillet-août + week-ends de grandes marées (Ébihens)
- **Type de bien le plus demandé** : maison familiale 4-6 personnes proche d'une plage, idéalement vue mer
- **Clientèle internationale** : 30 % (18 % britanniques via ferry Saint-Malo, 7 % Belgique/Pays-Bas, 5 % autres)
- **Clientèle dominante** : 55 % familles avec enfants, 25 % couples 40-65 ans, 12 % seniors actifs, 8 % groupes amis sport

Pour comparer : [Lancieux](/villes/lancieux) (à 5 min) tire à 78 €/nuit avec une concurrence similaire. Saint-Cast tire à 92 €/nuit avec un parc 3× plus grand. **Saint-Jacut est le bon rapport "rareté + saison étendue + clientèle famille fidèle"** de la Côte d'Émeraude.

## Les 5 atouts uniques de Saint-Jacut pour votre bien

### 1. Les 11 plages (et leur diversité)

C'est **l'argument décisionnel n°1** et il est unique en France. Saint-Jacut compte 11 plages distinctes sur seulement 4 km de presqu'île :

- **Plage du Rougeret** : familiale, sable fin, surveillée été, vue Ébihens
- **Plage de la Pissotte** : calme, idéale jeunes enfants
- **Plage de la Houle** : au pied du port, animée
- **Plage de la Banche-aux-Moines** : accès Ébihens à marée basse
- **Plage du Châtelet** : pêche à pied (palourdes, étrilles)
- **Plage des Quatre Vaulx** : kitesurf et planche à voile
- **Plage de la Manchette** : galets, ambiance bord du monde

Cette diversité **permet de trouver la bonne plage selon le vent et la marée** chaque jour. C'est un argument que **moins de 5 % des hôtes locaux exploitent dans leur annonce**. Le mentionner explicitement (avec un mini-tableau dans la description) génère **+30 % de clics**.

### 2. Les Îles des Ébihens (accessibles à pied à marée basse)

Face à Saint-Jacut, les **Îles des Ébihens** sont un petit archipel privé accessible à pied à marée basse, environ 2 à 3 heures avant et après la basse mer. C'est une **expérience inoubliable** pour les voyageurs : traversée à pied dans la baie, exploration des criques, tour Vauban, retour avant que la marée ne remonte.

À valoriser absolument dans le livret d'accueil avec un **guide marées personnalisé** : horaires précis, coefficient minimal recommandé, conseils de sécurité. Sur les week-ends de **grandes marées** (mars-avril + septembre-octobre), notre pricing applique automatiquement **+35 %** car la demande explose.

### 3. Le GR34 en boucle complète (~10 km)

Saint-Jacut est l'une des **rares communes où le GR34 fait une boucle complète** autour de la presqu'île. ~10 km de sentier des douaniers, accessible à tous, avec des panoramas spectaculaires sur les Ébihens, la baie d'Arguenon, Saint-Cast et le Cap Fréhel par temps clair.

Cette boucle attire les **randonneurs et marcheurs** (~25 % de la clientèle hors saison). C'est un argument fort pour étendre la saison d'avril à novembre.

### 4. Le port de pêche actif + criée

Saint-Jacut conserve un **port de pêche en activité**, chose rare sur la Côte d'Émeraude où la plupart des ports sont devenus uniquement de plaisance. Tous les matins, vous pouvez voir les pêcheurs rentrer leur prise. La **criée** est ouverte au public et permet d'acheter directement le poisson du jour. Cette authenticité plaît particulièrement à la clientèle internationale et urbaine.

### 5. Une concurrence Airbnb 4× plus faible qu'à Saint-Malo

Saint-Jacut compte ~280 annonces Airbnb actives contre ~1 100 à Saint-Malo et ~750 à Cancale. **Moins de concurrence = meilleur ranking dans les recherches Airbnb = meilleur taux d'occupation**. C'est l'argument structurel le plus solide pour votre rentabilité long terme.

## Saisonnalité et pricing dynamique : le tableau réel

| Période | Prix moyen | Taux d'occupation | Levier pricing |
|---|---|---|---|
| Janvier - mars | 55 € | 32 % | Stratégie séjours longs (télétravail) |
| Avril (Pâques) | 95 € | 73 % | Démarrage saison + grandes marées Ébihens |
| Mai (ponts) | 105 € | 82 % | Ascension + Pentecôte +50 % |
| Juin | 90 € | 72 % | Pré-saison, randonneurs GR34 |
| **Juillet - août** | **140 €** | **92 %** | Pic absolu, pricing semaine par semaine |
| Septembre | 100 € | 76 % | Microclimat doux + grandes marées Ébihens |
| Octobre | 80 € | 58 % | Toussaint + festival film britannique Dinard |
| Novembre - décembre | 60 € | 40 % | Séjours longs, marchés Noël Saint-Malo |

**Conclusion** : un T2 bien piloté à Saint-Jacut génère **18 000 à 26 000 € de revenus annuels nets** après commission de 15 % TTC. Une maison familiale 6-8 personnes proche plage peut dépasser **42 000 €** par an. Une maison vue Ébihens atteint **48 000 à 55 000 €**.

## Comment faire performer votre bien à Saint-Jacut : nos 6 leviers

### 1. Photos premium incluant la presqu'île + une plage

Notre photographe planifie le shoot **au lever ou coucher du soleil** + capture systématiquement une vue de la presqu'île ou des Ébihens. La photo n°1 doit raconter Saint-Jacut, pas juste votre intérieur.

### 2. Annonce 100 % bilingue français / anglais + mini-tableau des 11 plages

Avec 30 % de clientèle internationale, traduction anglaise native obligatoire. Ajoutez un **mini-tableau des 11 plages** dans la description (1 ligne par plage avec spécialité). **+30 % de clics** vs annonce générique.

### 3. Livret d'accueil "Guide des marées Ébihens"

Le **guide marées personnalisé** est notre signature à Saint-Jacut. PDF de 8 pages mis à jour 4× par an avec : horaires SHOM précis, coefficient minimal recommandé pour traverser, parcours sur l'île, consignes de sécurité, contacts urgences. Résultat : **note moyenne 4,9/5** sur nos biens du secteur.

### 4. Équipement famille + plage

Pour la clientèle dominante (55 % familles) : kit jeux de plage (épuisettes pêche à pied, seaux, pelles, ballons), lit bébé + chaise haute, parasols, douche jardin, vélos enfants + adultes pour le tour de la presqu'île. Coût : 800 à 1 200 €. ROI : +30 % de réservations famille.

### 5. Pricing événementiel calé sur les marées

Notre algorithme intègre **automatiquement le calendrier SHOM** des grandes marées + les vacances scolaires + les événements locaux (fête de la mer fin juillet). Sans pricing événementiel, vous laissez **1 000 à 1 600 € sur la table chaque année** sur le secteur Saint-Jacut.

### 6. Notre équipe locale à 12 minutes

Notre City Manager Côte d'Émeraude basé à Ploubalay intervient sur Saint-Jacut **en moins de 12 minutes**. Astreinte 24h/24 garantie y compris en pleine saison.

## Investir à Saint-Jacut en 2026 : opportunités

Le marché immobilier de Saint-Jacut reste **20 à 25 % moins cher qu'à Dinard centre** à typologie équivalente, et **comparable à Saint-Cast** :

- **T2 50 m²** : 175 000 à 235 000 €
- **Maison de plain-pied 90 m² avec jardin** : 295 000 à 390 000 €
- **Maison vue mer ou proche plage 130 m²** : 460 000 à 620 000 €
- **Maison vue Ébihens 140 m²** : 540 000 à 780 000 €

Avec un revenu Airbnb annuel net de 22 000 à 55 000 €, vous obtenez un **rendement brut de 8 à 12 %**, bien au-dessus de la moyenne Côte d'Émeraude. Voir notre [étude chiffrée investissement Saint-Jacut 2026](/blog/investir-airbnb-saint-jacut-de-la-mer-rendement-2026).

## Réglementation : ce qu'il faut savoir avant de louer

Saint-Jacut-de-la-Mer applique **la loi Le Meur de 2024** :

- Déclaration en mairie obligatoire (formulaire CERFA)
- Numéro d'enregistrement à 13 chiffres à afficher sur l'annonce
- Taxe de séjour collectée automatiquement par Airbnb / Booking
- **Pas de quota actuel ni de limite de jours** pour les résidences secondaires

Notre équipe se charge intégralement de la déclaration en mairie. Délai moyen : 3 à 5 jours ouvrés. [Voir le détail des démarches dans notre guide réglementation 2026](/blog/reglementation-airbnb-saint-malo-dinard-cancale-2026).

## Notre conviction sur Saint-Jacut-de-la-Mer

Saint-Jacut combine **presqu'île unique + 11 plages + Îles des Ébihens + port de pêche actif + GR34 en boucle + faible concurrence**. C'est, à nos yeux, **l'une des destinations Airbnb les plus différenciantes de la Côte d'Émeraude en 2026**. Clientèle famille fidèle, saison étendue d'avril à octobre, événements pricing puissants (marées), authenticité préservée : tous les ingrédients sont réunis.

Si vous possédez un bien à Saint-Jacut, ou si vous envisagez d'en acquérir un, nous proposons une **étude personnalisée gratuite sous 24 h**, sans engagement.

[**Voir notre offre Saint-Jacut →**](/villes/saint-jacut-de-la-mer)
[**Lire l'étude chiffrée investissement Saint-Jacut →**](/blog/investir-airbnb-saint-jacut-de-la-mer-rendement-2026)
[**Demander une étude personnalisée →**](/contact)
`,
  },

  // =====================================================================
  // 41, Investir à Saint-Jacut : Airbnb vs locatif classique (MARS 2026)
  // =====================================================================
  {
    slug: "investir-airbnb-saint-jacut-de-la-mer-rendement-2026",
    category: "ville-guide",
    title: "Investir à Saint-Jacut-de-la-Mer en 2026 : rendement Airbnb vs locatif classique (étude chiffrée)",
    excerpt:
      "T2, maison familiale, maison vue Ébihens : combien rapporte vraiment un bien sur la presqu'île ? Comparatif Airbnb / location nue / meublée annuelle, simulation à 10 ans et fiscalité LMNP. Le guide investisseur 2026.",
    image: "/images/blog/investir-airbnb-saint-jacut-de-la-mer-rendement-2026.jpg",
    imageAlt: "Maison de bord de mer en Bretagne face aux Îles des Ébihens, investissement immobilier locatif Saint-Jacut Côte d'Émeraude",
    author: "L'équipe Terre et Mer",
    date: "2026-03-19",
    readTime: 10,
    keywords:
      "investir saint-jacut, investir airbnb saint-jacut-de-la-mer, rendement airbnb saint-jacut, acheter pour louer saint-jacut, rentabilité airbnb presqu'île bretagne, lmnp saint-jacut, simulation airbnb saint-jacut, prix immobilier saint-jacut 2026, airbnb vs locatif classique presqu'île",
    relatedCities: ["saint-jacut-de-la-mer"],
    content: `
## Saint-Jacut-de-la-Mer en 2026 : pourquoi la presqu'île tient ses promesses d'investissement

Sur la Côte d'Émeraude, **Saint-Jacut-de-la-Mer occupe une place à part**. Sa configuration de presqu'île, ses 11 plages, ses Îles des Ébihens et son port de pêche actif en font une destination que les voyageurs ne confondent avec aucune autre. Pour un investisseur, cela se traduit par **un taux d'occupation supérieur de 8 points à la moyenne bretonne** et une **récurrence client** (familles qui reviennent chaque été) exceptionnellement élevée.

Dans ce guide, nous comparons **chiffres à l'appui** les 3 options qui s'offrent à un propriétaire ou futur acquéreur à Saint-Jacut :

- **Option A** : location nue (locataire à l'année)
- **Option B** : location meublée annuelle (LMNP standard)
- **Option C** : Airbnb / courte durée pilotée par notre conciergerie (15 % TTC)

Trois cas concrets, trois tableaux de rentabilité.

## Le marché immobilier de Saint-Jacut en 2026

| Typologie | Prix au m² 2026 | Prix moyen complet |
|---|---|---|
| Studio 30 m² | 3 800 à 4 800 €/m² | 118 000 à 150 000 € |
| T2 50 m² | 3 500 à 4 700 €/m² | 175 000 à 235 000 € |
| Maison de plain-pied 90 m² + jardin | 3 300 à 4 300 €/m² | 295 000 à 390 000 € |
| Maison vue mer / proche plage 130 m² | 3 800 à 5 100 €/m² | 460 000 à 620 000 € |
| Maison vue Ébihens 140 m² | 4 200 à 5 800 €/m² | 540 000 à 780 000 € |

Pour comparer : Dinard centre tourne autour de **5 800 à 7 200 €/m²**, [Lancieux](/villes/lancieux) à **3 600 à 4 500 €/m²**, Saint-Cast à **4 100 à 5 200 €/m²**. **Saint-Jacut est 20 à 25 % moins cher que Dinard** mais bénéficie d'un tarif/nuit supérieur grâce à la rareté de l'offre.

## Cas n°1, T2 50 m² avec balcon, à 5 min à pied d'une plage

**Investissement total** : 205 000 € (acquisition) + 12 300 € (notaire) + 8 500 € (ameublement & équipement Airbnb) = **225 800 €**

### Option A, Location nue à l'année

- Loyer : 620 €/mois → **7 440 €/an**
- Charges déductibles : 1 300 € (taxe foncière, copro, assurance PNO)
- **Revenu net annuel : 6 140 €**
- Rendement net : **2,7 %**

### Option B, Location meublée annuelle (LMNP, micro-BIC)

- Loyer : 760 €/mois → **9 120 €/an**
- Abattement micro-BIC 50 % → impôt sur 4 560 €
- Charges : 1 600 €
- **Revenu net annuel ≈ 7 100 €**
- Rendement net : **3,1 %**

### Option C, Airbnb piloté par Terre et Mer (commission 15 % TTC)

- Tarif moyen : 85 €/nuit
- Taux d'occupation pilotage Terre et Mer : 73 %
- Nuits louées par an : 266
- **Chiffre d'affaires brut : 22 610 €**
- Commission 15 % TTC : −3 390 €
- Charges (taxe foncière, copro, assurance, équipement) : −2 400 €
- Régime LMNP réel (amortissement bien + mobilier) : impôt quasi-nul les 8 premières années
- **Revenu net annuel ≈ 16 820 €**
- Rendement net : **7,4 %**

➡️ **Différentiel Airbnb vs locatif nu : +10 680 €/an**, soit **+ 174 %** de rentabilité. Sur 10 ans, l'écart cumulé dépasse **107 000 €**.

## Cas n°2, Maison familiale 4-6 personnes, jardin clos, à 7 min de la Plage du Rougeret

**Investissement total** : 345 000 € + 20 700 € + 14 000 € = **379 700 €**

### Option A, Location nue

- Loyer : 1 020 €/mois → **12 240 €/an**
- Charges : 2 100 €
- **Net : 10 140 €** → rendement **2,7 %**

### Option B, Meublée annuelle (LMNP)

- Loyer : 1 230 €/mois → **14 760 €/an**
- Net après charges et fiscalité : **11 600 €** → rendement **3,1 %**

### Option C, Airbnb 15 % TTC Terre et Mer

- Tarif moyen : 155 €/nuit (équipement famille premium + jardin clos + proximité plage)
- Occupation pilotée : 75 %
- **CA brut : 42 400 €**
- Commission : −6 360 €
- Charges : −3 800 €
- Fiscalité LMNP réel : impôt quasi-nul
- **Net annuel ≈ 32 240 €** → rendement **8,5 %**

➡️ **Différentiel Airbnb vs nu : +22 100 €/an**. **+218 %** de rentabilité. Sur 10 ans : **+221 000 €**.

## Cas n°3, Maison vue Ébihens 140 m², 6-8 personnes

**Investissement total** : 620 000 € + 37 200 € + 20 000 € = **677 200 €**

| Option | Revenu net annuel | Rendement net |
|---|---|---|
| **A**, Location nue | 15 800 € | 2,3 % |
| **B**, Meublée annuelle (LMNP) | 18 800 € | 2,8 % |
| **C**, Airbnb 15 % Terre et Mer | **48 700 €** | **7,2 %** |

Sur 10 ans, l'écart entre Airbnb et locatif nu approche **+329 000 €**. Et la **plus-value moyenne sur ce type de bien à Saint-Jacut est de +28 % en 10 ans** (vs +18 % pour la France entière).

## Pourquoi Saint-Jacut performe particulièrement bien à l'investissement ?

Trois facteurs structurels :

### 1. Le tarif/nuit élevé grâce à la rareté

Saint-Jacut tire à 85 €/nuit (moyenne), au-dessus de Lancieux (78 €) et au-dessus de la moyenne Côte d'Émeraude. La rareté de l'offre (~280 annonces actives) + la spécificité de la presqu'île maintient les prix.

### 2. Le taux d'occupation 73 % (vs 65 % en moyenne Bretagne)

La diversité des plages + les Ébihens + le GR34 + la fête de la mer fin juillet créent une **demande étalée d'avril à octobre**. Notre algorithme pricing exploite chaque pic.

### 3. La récurrence client exceptionnelle

Sur notre portefeuille, **48 % des familles Saint-Jacut reviennent dans le même bien** au moins une fois. C'est le taux le plus élevé du portefeuille Côte d'Émeraude. Cette récurrence sécurise le taux d'occupation hors saison.

## Simulation à 10 ans : T2 acheté en 2026, revendu en 2036

Hypothèses : T2 50 m² à 205 000 €, financé à 80 % sur 20 ans à 3,5 %.

| Critère | Location nue | Meublée annuelle | Airbnb 15 % T&M |
|---|---|---|---|
| Cumul revenus nets 10 ans | 61 400 € | 71 000 € | **168 200 €** |
| Cashflow mensuel net | −665 € | −555 € | **+360 €** |
| Plus-value à 10 ans (estim.) | +85 000 € | +85 000 € | +85 000 € |
| **Capital total à 10 ans** | **+146 400 €** | **+156 000 €** | **+253 200 €** |

➡️ **L'Airbnb piloté permet l'autofinancement** dès la 1ʳᵉ année. Sur 10 ans : **+107 000 € de capital supplémentaire** vs la location nue.

## La formule conciergerie Terre et Mer à Saint-Jacut

- **Commission unique 15 % TTC** sans frais cachés
- **Pricing dynamique** calé sur grandes marées Ébihens, vacances scolaires, fête de la mer, festival du film britannique Dinard
- **Accueil voyageurs 24h/24 · 7j/7**
- **Kit d'accueil inclus** : Shampooing, gel douche, papier toilette, produit vaisselle, éponge, sac-poubelle, petit sac pour la salle de bain
- **Linge non inclus** dans la formule 15 % (option voyageur dédiée)
- **Livret marées Ébihens dédié** mis à jour 4× par an
- **City Manager** basé à Ploubalay, intervention en moins de 12 minutes sur la presqu'île

## Pour aller plus loin

- 📖 [Le guide propriétaire Saint-Jacut complet 2026](/blog/louer-airbnb-saint-jacut-de-la-mer-presquile-2026)
- 💼 [LMNP Bretagne : fiscalité 2026 expliquée](/blog/lmnp-airbnb-bretagne-fiscalite-2026)
- 📊 [Brut, net, cashflow : lire les vrais chiffres de votre Airbnb](/blog/rentabilite-brute-nette-cashflow-airbnb-saint-malo)
- 🌊 [Investir Airbnb sur la Côte d'Émeraude en 2026, vue d'ensemble](/blog/investir-airbnb-cote-emeraude-bretagne-2026)
- 🏖️ [Investir à Lancieux en 2026 (voisin direct, comparatif)](/blog/investir-airbnb-lancieux-rendement-2026)

## Vous envisagez d'investir à Saint-Jacut-de-la-Mer ?

Nous proposons une **étude personnalisée gratuite sous 24 h**. Notre City Manager peut aussi vous accompagner avant l'achat (visite, étude technique, conseils ameublement). Cet accompagnement amont, **gratuit et sans engagement**, est devenu un standard pour nos investisseurs.

[**Voir notre offre Saint-Jacut-de-la-Mer →**](/villes/saint-jacut-de-la-mer)
[**Demander une étude personnalisée gratuite →**](/contact)
[**Lire le guide propriétaire Saint-Jacut →**](/blog/louer-airbnb-saint-jacut-de-la-mer-presquile-2026)
`,
  },


  // =====================================================================
  // 42, Mettre son logement en Airbnb à Dinard : guide propriétaire (MAI 2026)
  // =====================================================================
  {
    slug: "mettre-airbnb-dinard-guide-proprietaire-2026",
    category: "ville-guide",
    title: "Mettre son logement en Airbnb à Dinard : le guide complet 2026 (du studio à la villa Belle Époque)",
    excerpt:
      "Vous possédez un appartement, une maison ou une villa à Dinard et vous voulez la mettre en Airbnb ? Démarches, prix moyens par typologie, déclaration mairie, kit d'accueil et stratégie haut de gamme : le guide propriétaire complet 2026.",
    image: "/images/blog/mettre-airbnb-dinard-guide-proprietaire-2026.jpg",
    imageAlt: "Villa Belle Époque à Dinard avec vue sur la plage de l'Écluse et cabines de bain à rayures, mise en Airbnb d'un logement sur la Côte d'Émeraude",
    author: "L'équipe Terre et Mer",
    date: "2026-05-12",
    readTime: 11,
    keywords:
      "mettre son logement en airbnb dinard, mettre en airbnb dinard, louer airbnb dinard, conciergerie airbnb dinard, comment mettre son bien en location courte durée dinard, démarches airbnb dinard, déclaration mairie dinard airbnb, prix nuit airbnb dinard, villa belle époque airbnb, location saisonnière dinard 2026",
    relatedCities: ["dinard", "pleurtuit", "saint-lunaire", "saint-malo"],
    content: `
## Mettre son logement en Airbnb à Dinard : pourquoi 2026 est le bon moment

Si vous possédez un studio, un appartement, une maison ou une villa Belle Époque à **Dinard**, vous êtes assis sur l'un des **marchés Airbnb les plus rentables de France hors Paris/Côte d'Azur**. La station de la Côte d'Émeraude accueille **plus de 95 000 voyageurs Airbnb par an**, dont **40 % d'étrangers** (Britanniques via le ferry Saint-Malo–Portsmouth, Belges, Suisses, Allemands). Le tarif moyen y atteint **125 € la nuit en haute saison**, bien au-dessus de la moyenne bretonne (78 €).

Et pourtant, **6 propriétaires sur 10 que nous rencontrons à Dinard sous-exploitent leur bien** : annonce mal rédigée, photos amateures, pricing fixe à l'année, livret d'accueil inexistant, pas de version anglaise. Résultat : ils plafonnent à 14 000 € de revenus annuels là où ils pourraient encaisser 28 000 à 38 000 €.

Ce guide vous explique **étape par étape** comment mettre votre logement en Airbnb à Dinard en 2026, depuis la déclaration en mairie jusqu'au pricing événementiel calé sur le Festival du film britannique. À la fin, vous saurez si vous pouvez le faire seul, ou si confier votre bien à une conciergerie spécialisée Dinard a du sens pour vous.

> 💡 **Pas le temps de tout faire vous-même ?** Notre équipe locale, basée à **Pleurtuit (5 min de Dinard)**, prend en charge l'intégralité de la mise en location et le pilotage de votre bien pour **15 % TTC** (commission unique, sans frais cachés). [Demander une étude gratuite sous 24 h →](/contact)

## Le marché Airbnb de Dinard en 2026 : les vrais chiffres

| Indicateur | Valeur | Commentaire |
|---|---|---|
| Prix moyen / nuit | 125 € | +60 % vs moyenne bretonne |
| Taux d'occupation annuel | 72 % | Pic à 92 % juillet-août |
| Voyageurs Airbnb / an | 95 000+ | En croissance de +11 % depuis 2023 |
| Clientèle internationale | 40 % | 25 % UK, 8 % Belgique, 7 % autres |
| Saison principale | Avril à octobre | Plus mai (Festival film britannique) |
| Type de bien le plus demandé | T2 / T3 vue mer ou villa Belle Époque | Cabine de bain Plage de l'Écluse adore |

**À retenir** : un T2 bien piloté à Dinard tire **24 000 à 32 000 € de revenus annuels nets** après commission de 15 % TTC. Une villa Belle Époque vue mer 6-8 personnes peut atteindre **55 000 à 75 000 €** par an. C'est 2 à 3× ce que rapporterait le même bien en location longue durée.

## Étape 1, Vérifier que vous avez le droit de louer

Avant tout, vérifiez **3 points juridiques** essentiels :

### Résidence principale ou secondaire ?

- **Résidence principale** : vous habitez sur place plus de 8 mois par an. Vous pouvez louer en courte durée jusqu'à **120 jours par an maximum** (loi Le Meur 2024). Démarche simplifiée mais plafond contraignant.
- **Résidence secondaire** : vous y séjournez moins de 4 mois par an. **Pas de plafond annuel à Dinard** en 2026 (la ville n'a pas instauré de compensation ni de quota), mais vous devez obtenir un numéro d'enregistrement obligatoire.

### Copropriété : lisez votre règlement

Pour un appartement Dinard intra-Boulevard Wilson ou Saint-Énogat, **vérifiez votre règlement de copropriété**. Une clause d'habitation bourgeoise (~15 % des copropriétés dinardaises) interdit la location courte durée. À vérifier avant tout investissement.

### Bail à votre nom

Si vous êtes locataire (et non propriétaire), la sous-location est **interdite sans accord écrit du bailleur**. À Dinard, peu de bailleurs acceptent.

## Étape 2, Déclarer son meublé en mairie de Dinard

Depuis la **loi Le Meur du 19 novembre 2024**, **toute mise en Airbnb à Dinard est soumise à déclaration en mairie**, y compris pour les résidences principales (anciennement exemptées). La démarche est gratuite et se déroule en 4 étapes :

1. Remplir le **formulaire CERFA n° 14004*04** (téléchargeable sur service-public.fr)
2. Joindre une **pièce d'identité** + **justificatif de propriété** + **plan du logement**
3. Déposer le dossier à la **mairie de Dinard, 47 Boulevard Féart** (ou par recommandé AR)
4. Recevoir sous **3 à 5 jours ouvrés** votre **numéro d'enregistrement à 13 chiffres** (format : 22XXXX-X-XX-XXXXX)

Ce numéro doit obligatoirement figurer sur votre annonce Airbnb, Booking, Vrbo. Sans lui, votre annonce peut être suspendue automatiquement par les plateformes et vous risquez une amende administrative pouvant aller jusqu'à **15 000 € par bien**.

> 📋 **Détail complet** : voir notre [guide réglementation 2026, déclaration mairie Dinard](/blog/reglementation-airbnb-dinard-declaration-taxe-sejour-2026).

## Étape 3, Préparer son bien : la checklist Dinard

Dinard exige un **standard premium** : le voyageur dinardais paye plus cher qu'à Saint-Malo et s'attend à un service hôtelier. Voici la checklist minimale pour décrocher un classement 4★ Airbnb dès les 10 premières réservations :

### Indispensables (équipements légaux + 5★)

- **Literie hôtelière** : matelas premium (Bultex, Tediber), couette 250g, oreillers ergonomiques, drap-housse + drap plat + housse de couette + 2 taies × nombre de couchages
- **Cuisine équipée complète** : plaques induction, four + micro-ondes, lave-vaisselle, réfrigérateur avec congélateur, vaisselle pour le nombre de couchages +2, machine à café (idéalement Nespresso + filtre), bouilloire, grille-pain, robot de cuisine
- **Salle de bain hôtelière** : pommeau effet pluie, miroir lumineux, peignoirs (× couchages), serviettes (1 grande + 1 visage + 1 invité × personne), *option voyageur*, sèche-cheveux pro
- **Wi-Fi très haut débit** : fibre 200 Mb/s minimum, certifié par speedtest dans le livret (les voyageurs business + 7 % de la clientèle Dinard)
- **TV connectée + Netflix** : indispensable pour les soirées d'hiver et de mai-juin
- **Kit télétravail** (gros différenciant à Dinard) : bureau dédié, chaise ergonomique, écran 24" en supplément, multiprises USB

### Touches "Belle Époque" qui font la différence

- **Beaux livres** sur les villas de Dinard, le casino, le Festival du film britannique
- **Carafe à eau** + verres en cristal (ou imitation chic)
- **Plateau de petit déjeuner** complet pour le matin (couples romantiques adorent)
- **Pichet à thé** (la clientèle UK le cherche systématiquement)
- **Cartes postales rétro** signées Dinard (placées dans le livret d'accueil)
- **Plaid en laine** breton sur le canapé

### Le kit d'accueil Terre et Mer (inclus dans la commission 15 % TTC)

À chaque arrivée, nos équipes déposent : **shampooing, gel douche, papier toilette, produit vaisselle, éponge, sac-poubelle, petit sac pour la salle de bain**. Tout est en quantité suffisante pour le séjour complet, sans rupture. Cette régularité fait gagner **+0,3 point sur la note moyenne** (vs hôtes en autonomie qui oublient régulièrement le PQ).

> 🛏️ **Linge non inclus dans la formule 15 % TTC** : par défaut, les voyageurs apportent leur propre linge. Ils peuvent souscrire **notre option linge premium** directement auprès de nous (facturée à eux, neutre pour vous). Cette flexibilité plaît à la clientèle UK habituée aux self-catering.

## Étape 4, Photos professionnelles + annonce bilingue

C'est **l'étape la plus sous-estimée**. À Dinard, où la concurrence est forte (~640 annonces Airbnb actives), votre annonce a **3 secondes pour convertir un clic en sauvegarde**. Sans photos pro et titre optimisé, votre bien restera invisible.

### Photos professionnelles (4-6 h sur site)

Notre photographe Côte d'Émeraude :
- Shoote au **lever ou coucher du soleil** pour capter la lumière dorée Belle Époque
- Privilégie les **vues mer** (à Dinard, une vue Plage de l'Écluse vaut +35 % de prix moyen)
- Met en scène le **petit-déjeuner**, le **kit télétravail**, la **salle de bain hôtelière**
- Capture systématiquement une vue de Dinard depuis la propriété ou ses environs (Promenade du Clair de Lune, Pointe du Moulinet, Plage de Saint-Énogat)

La **photo n°1 doit raconter Dinard**, pas seulement votre intérieur. C'est la règle Airbnb la plus rentable.

### Annonce bilingue français/anglais (obligatoire à Dinard)

Avec **40 % de clientèle anglophone**, une traduction anglaise native est obligatoire (pas de Google Translate qui détruit votre crédibilité auprès des UK). Notre rédactrice EN-native met en avant :
- L'héritage britannique de Dinard (lancée par les Anglais au XIXᵉ siècle)
- Le Casino Barrière, le Yacht-Club, le golf international
- Les restaurants étoilés (Le Pourquoi Pas, Didier Méril, La Vallée)
- Le ferry Saint-Malo–Portsmouth (à 15 min)
- Le TGV Paris–Saint-Malo (2h45)

## Étape 5, Multidiffusion Airbnb + Booking + Vrbo

À Dinard, **diffuser uniquement sur Airbnb laisse 25 à 30 % de revenus sur la table**. Notre channel manager pousse votre annonce en simultané sur :

- **Airbnb** (60 % des réservations Dinard)
- **Booking** (28 %, surtout international, UK + Belgique + Allemagne)
- **Vrbo** (8 %, clientèle famille premium, séjours +5 nuits)
- **Plateformes spécialisées** (Le Bon Coin Vacances, Gîtes de France pour les biens labellisables)

Les calendriers se synchronisent en temps réel, aucun risque de double réservation.

## Étape 6, Pricing dynamique : le levier qui change tout

C'est l'écart de revenus le plus brutal entre un bien autogéré et un bien piloté. À Dinard, le pricing fixe à l'année coûte **typiquement 6 000 à 12 000 € par an** de revenus perdus.

Notre équipe pricing est partenaire **PriceLabs** (la plateforme de yield management Airbnb la plus utilisée au monde, +400 000 logements pilotés). PriceLabs scanne chaque jour la concurrence Côte d'Émeraude, l'historique de votre bien, la demande temps réel et la météo à 10 jours. Notre City Manager local **double cette couche algorithmique d'une supervision humaine** pour caler les pics ultra-locaux que la machine ne voit pas seule :
- **Festival du film britannique** (début octobre, 5 jours), pic absolu, prix ×3 à ×4
- **Régates de Dinard** (juillet-août), +35 % sur les biens vue mer
- **Grandes marées d'équinoxe** (mars + septembre), +50 % sur 6 jours
- **Salon du livre Dinard** (septembre), +25 % week-end
- **Pâques + ponts de mai**, +30 à +50 % vs janvier-février
- **Tournoi de tennis du Yacht-Club** (été), +20 % biens premium

Sans pricing événementiel, **vous laissez 6 000 à 12 000 € sur la table chaque année rien que sur ces fenêtres**.

## Étape 7, Check-in, ménage hôtelier et astreinte 24h/24

C'est la partie "opérationnelle" qui décourage 90 % des propriétaires qui pilotent eux-mêmes. À Dinard, cela représente :

- **Check-in** : 2 options. Soit physique avec démonstration des équipements et conseils locaux (recommandé pour les biens premium, justifie +20 € de tarif), soit **autonome via boîte à clés ou serrure connectée**. Voir notre [comparatif check-in autonome vs physique](/blog/check-in-autonome-vs-physique-airbnb-2026).
- **Ménage hôtelier** entre chaque séjour (3 à 6 h selon la taille), avec **changement complet de linge si option souscrite** et kit d'accueil. Facturé directement aux voyageurs (50 à 120 € selon la taille).
- **Astreinte 24h/24** : panne de chaudière à 22 h, voyageur enfermé dehors, problème Wi-Fi. Notre équipe à Pleurtuit (5 min) intervient en **moins de 15 minutes** en moyenne.

C'est pour ces opérations terrain qu'une conciergerie locale fait la différence vs une plateforme nationale.

## Faire seul ou confier à une conciergerie : la vraie question

Voici notre grille de décision honnête :

### Faites-le seul si

- Vous habitez à moins de 30 min de Dinard
- Vous êtes disponible 7j/7 pour les arrivées / urgences
- Vous êtes bilingue anglais et capable de rédiger une annonce premium
- Vous acceptez de plafonner à ~70 % du potentiel de revenus

### Confiez à une conciergerie locale si

- Vous habitez hors région (Paris, Île-de-France, étranger)
- Vous voulez +30 à +45 % de revenus vs autogéré
- Vous voulez 0 charge mentale (pas d'appels la nuit, pas de SAV voyageurs)
- Vous voulez exploiter à 100 % les événements Dinard (Festival, régates, marées)

> 🎯 **Notre conviction** : à Dinard, le ratio "coût conciergerie 15 % vs revenus supplémentaires générés" est **largement positif** pour ~85 % des propriétaires que nous rencontrons. Le pricing événementiel + l'annonce bilingue + le check-in physique premium génèrent typiquement **+8 000 à +18 000 € de revenus annuels**, soit 3 à 5× ce que coûte la commission.

## Notre équipe Côte d'Émeraude à votre service

Terre et Mer Conciergerie est **basée à Pleurtuit (35), à 5 minutes de Dinard**. Notre City Manager intervient sur l'ensemble de la commune en moins de 15 minutes : Plage de l'Écluse, Saint-Énogat, La Vicomté, Promenade du Clair de Lune, quartier de la gare. Équipe **salariée** (pas de franchisés), **annonce bilingue obligatoire**, **photos premium incluses**, **pricing événementiel calé sur les 30+ rendez-vous annuels** de la Côte d'Émeraude.

**Commission unique : 15 % TTC.** Pas de frais d'installation, pas de set-up, pas de frais photos. Vous payez uniquement 15 % de vos revenus réels. Le ménage est facturé aux voyageurs, jamais à vous.

[**Voir notre offre Dinard détaillée →**](/villes/dinard)
[**Simuler vos revenus Airbnb Dinard →**](/simulateur)
[**Demander une étude gratuite sous 24 h →**](/contact)

## Pour aller plus loin

- [Combien rapporte un Airbnb à Dinard ? Revenus 2026 par type de bien](/blog/combien-rapporte-airbnb-dinard-revenus-2026)
- [Réglementation Airbnb à Dinard : déclaration mairie, taxe de séjour, changement d'usage](/blog/reglementation-airbnb-dinard-declaration-taxe-sejour-2026)
- [Le guide propriétaire Dinard 2026](/blog/louer-airbnb-dinard-guide-proprietaire)
- [Investir sur la Côte d'Émeraude en 2026](/blog/investir-airbnb-cote-emeraude-bretagne-2026)
`,
  },

  // =====================================================================
  // 43, Combien rapporte un Airbnb à Dinard (MAI 2026)
  // =====================================================================
  {
    slug: "combien-rapporte-airbnb-dinard-revenus-2026",
    category: "rentabilite",
    title: "Combien rapporte un Airbnb à Dinard en 2026 ? Revenus réels par type de bien (étude chiffrée)",
    excerpt:
      "Studio, T2, T3, villa Belle Époque vue mer : combien encaisse réellement un propriétaire à Dinard ? Chiffres réels du portefeuille Terre et Mer, comparatif avec la location classique et simulation à 10 ans.",
    image: "/images/blog/combien-rapporte-airbnb-dinard-revenus-2026.jpg",
    imageAlt: "Vue panoramique sur la plage de l'Écluse et les villas Belle Époque de Dinard, calcul des revenus Airbnb par typologie de bien",
    author: "L'équipe Terre et Mer",
    date: "2026-05-13",
    readTime: 10,
    keywords:
      "combien rapporte airbnb dinard, revenus airbnb dinard 2026, rentabilité airbnb dinard, prix nuit airbnb dinard, taux occupation airbnb dinard, revenus annuels location courte durée dinard, simulation airbnb dinard, airbnb vs locatif classique dinard, rendement villa belle époque dinard",
    relatedCities: ["dinard", "saint-lunaire", "pleurtuit", "saint-malo"],
    content: `
## Combien rapporte un Airbnb à Dinard en 2026 ? La réponse courte

Sur notre portefeuille de biens à **Dinard**, les revenus annuels bruts varient de **18 000 € pour un studio bien situé** à **75 000 € pour une villa Belle Époque 6-8 personnes vue mer**. La médiane se situe autour de **28 000 € pour un T2 / T3** correctement piloté. Après commission de 15 % TTC et charges, le net propriétaire tourne entre **14 000 € et 60 000 €** selon la typologie.

Mais ces chiffres ne valent rien sans contexte. Le **vrai écart** se joue sur 3 leviers : la **typologie**, la **localisation précise** (intra-Boulevard Wilson vs périphérie), et le **mode de pilotage** (autogéré vs conciergerie professionnelle).

Cet article décortique **chiffres à l'appui** les revenus Airbnb à Dinard par type de bien, par quartier, et compare Airbnb vs location classique sur 10 ans. Tous les chiffres viennent du **portefeuille réel Terre et Mer** : 38 biens à Dinard, 5 ans de recul, données 2024-2025-2026.

## La structure du marché Airbnb dinardais en 2026

Avant les chiffres, posons le marché :

- **Annonces Airbnb actives à Dinard** : ~640 (vs 4 200 à Saint-Malo, 280 à Saint-Jacut)
- **Taux d'occupation moyen** : 72 % (médiane Bretagne : 65 %)
- **Saison principale** : 8 mois (avril à novembre)
- **Pic absolu** : Festival du film britannique début octobre (5 jours)
- **Clientèle** : 60 % France (Île-de-France majoritaire), 25 % UK, 8 % Belgique, 7 % autres
- **Profil dominant** : couples 35-65 ans en escapade culturelle/golf (52 %), familles haut de gamme (28 %), seniors actifs (15 %), business (5 %)

Cette structure **explique deux phénomènes** : (1) la saison étendue d'avril à novembre permet des revenus 30 à 40 % supérieurs à ceux d'une station bretonne plus saisonnière, (2) la clientèle haut de gamme accepte des tarifs +35 à +60 % vs Saint-Malo à typologie équivalente.

## Revenus réels par typologie de bien (chiffres 2025 portefeuille Terre et Mer)

### Studio 1-2 personnes (25-35 m²)

| Indicateur | Centre / Boulevard Féart | Saint-Énogat / La Vicomté | Périphérie / La Richardais |
|---|---|---|---|
| Prix moyen / nuit | 105 € | 95 € | 75 € |
| Taux d'occupation | 75 % | 70 % | 62 % |
| Nuits louées / an | 274 | 255 | 226 |
| **Revenus annuels bruts** | **28 770 €** | **24 225 €** | **16 950 €** |
| Commission 15 % TTC | -4 315 € | -3 634 € | -2 543 € |
| Charges (copropriété, électricité, internet, assurance PNO) | -3 800 € | -3 600 € | -3 200 € |
| **Net propriétaire / an** | **20 655 €** | **16 991 €** | **11 207 €** |

> 💡 Un studio à 110 000 € en périphérie peut tirer ~11 000 €/an net = **10 % de rendement brut**. C'est rare en France.

### T2 / Appartement 2 chambres (40-60 m²), 2 à 4 personnes

| Indicateur | Vue mer (Plage Écluse / Saint-Énogat) | Centre Belle Époque | Périphérie |
|---|---|---|---|
| Prix moyen / nuit | 175 € | 145 € | 105 € |
| Taux d'occupation | 78 % | 72 % | 64 % |
| Nuits louées / an | 285 | 263 | 234 |
| **Revenus annuels bruts** | **49 875 €** | **38 135 €** | **24 570 €** |
| Commission 15 % TTC | -7 481 € | -5 720 € | -3 686 € |
| Charges | -5 400 € | -5 100 € | -4 200 € |
| **Net propriétaire / an** | **36 994 €** | **27 315 €** | **16 684 €** |

### T3 / Maison 4-6 personnes (70-110 m²)

| Indicateur | Maison Belle Époque jardin | Appartement T3 centre | Maison périphérie |
|---|---|---|---|
| Prix moyen / nuit | 230 € | 180 € | 145 € |
| Taux d'occupation | 76 % | 70 % | 62 % |
| Nuits louées / an | 277 | 256 | 226 |
| **Revenus annuels bruts** | **63 710 €** | **46 080 €** | **32 770 €** |
| Commission 15 % TTC | -9 557 € | -6 912 € | -4 916 € |
| Charges | -7 200 € | -6 400 € | -5 800 € |
| **Net propriétaire / an** | **46 953 €** | **32 768 €** | **22 054 €** |

### Villa Belle Époque vue mer 6-10 personnes (150-280 m²)

| Indicateur | Boulevard Wilson / Plage Écluse | La Vicomté | Saint-Énogat |
|---|---|---|---|
| Prix moyen / nuit | 380 € | 320 € | 270 € |
| Taux d'occupation | 70 % | 68 % | 64 % |
| Nuits louées / an | 255 | 248 | 234 |
| **Revenus annuels bruts** | **96 900 €** | **79 360 €** | **63 180 €** |
| Commission 15 % TTC | -14 535 € | -11 904 € | -9 477 € |
| Charges | -12 500 € | -10 800 € | -9 200 € |
| **Net propriétaire / an** | **69 865 €** | **56 656 €** | **44 503 €** |

> 🏠 Une villa Belle Époque à 1,2 M€ peut tirer ~70 000 €/an net = **5,8 % de rendement brut**. Pour un actif tangible en bord de mer Côte d'Émeraude, c'est un rapport rare.

## Comparatif Airbnb vs location longue durée à Dinard

Prenons le cas concret d'un **T2 vue mer 50 m² à Saint-Énogat**, acheté 295 000 € :

| Stratégie | Revenus annuels bruts | Charges | Net / an | Sur 10 ans |
|---|---|---|---|---|
| Location nue (locataire à l'année) | 13 800 € (loyer ~1 150 €/mois) | -2 800 € | **11 000 €** | **110 000 €** |
| Location meublée annuelle (LMNP) | 16 200 € (loyer ~1 350 €/mois) | -3 100 € | **13 100 €** | **131 000 €** |
| **Airbnb piloté par Terre et Mer (15 % TTC)** | **38 135 €** | **-10 820 €** | **27 315 €** | **273 150 €** |

**Écart sur 10 ans : +163 000 € en faveur d'Airbnb piloté** vs location nue. Et ce, **sans contrainte de gestion** (notre équipe pilote tout) ni risque locatif (pas d'impayés, pas de procédure d'expulsion).

Bien sûr, ce calcul ne tient pas compte de la **fiscalité avantageuse LMNP** ni des **opportunités d'usage personnel** (3-4 semaines/an pour vous = bonus invisible).

## Les 4 leviers qui font passer un bien de 14 000 € à 28 000 € net

1. **Pricing événementiel piloté par PriceLabs + supervision humaine** (Festival film britannique, régates, grandes marées, ponts de mai) : +25 à +40 % de revenus. Notre équipe est partenaire **PriceLabs**, la plateforme de yield management n°1 du marché, couplée à une supervision quotidienne par notre City Manager Côte d'Émeraude pour caler les pics ultra-locaux.
2. **Annonce bilingue + photos pro** : +18 à +25 % de taux d'occupation (vs annonce générique)
3. **Multidiffusion Airbnb + Booking + Vrbo** : +12 à +18 % de réservations
4. **Standard hôtelier (kit accueil, ménage premium, astreinte 24/7)** : +0,4 point de note moyenne = ranking algorithmique boosté = +15 % de visibilité

Ces 4 leviers, **combinés**, expliquent l'écart de +30 à +45 % entre un bien autogéré et un bien piloté par une conciergerie professionnelle locale.

## Simulation à 10 ans : T3 Belle Époque centre Dinard

Cas : T3 60 m² centre Dinard, achat 410 000 € en 2026.

| Année | Revenus bruts | Revenus nets | Cumul net | Valorisation bien |
|---|---|---|---|---|
| 2026 | 38 135 € | 27 315 € | 27 315 € | 414 000 € |
| 2027 | 39 280 € | 28 138 € | 55 453 € | 422 000 € |
| 2028 | 40 458 € | 28 984 € | 84 437 € | 430 000 € |
| 2029 | 41 672 € | 29 856 € | 114 293 € | 439 000 € |
| 2030 | 42 922 € | 30 753 € | 145 046 € | 448 000 € |
| 2031 | 44 210 € | 31 676 € | 176 722 € | 457 000 € |
| 2032 | 45 536 € | 32 627 € | 209 349 € | 466 000 € |
| 2033 | 46 902 € | 33 605 € | 242 954 € | 475 000 € |
| 2034 | 48 309 € | 34 612 € | 277 566 € | 485 000 € |
| 2035 | 49 758 € | 35 650 € | 313 216 € | 495 000 € |

(Hypothèse : +3 %/an sur les revenus, +2 %/an sur la valeur bien, historique Côte d'Émeraude 2015-2025.)

**Bilan 10 ans : 313 000 € de cash net + 85 000 € de plus-value = ~398 000 € de gain total sur un bien à 410 000 €.** Soit un rendement global de ~97 % en 10 ans, sans levier bancaire.

## Quel rendement viser à Dinard ?

Voici nos repères 2026 (rendement brut = revenus annuels bruts / prix d'achat) :

- **Studio** : 7 à 10 % (excellent)
- **T2** : 8 à 12 % (très bon)
- **T3 / Maison** : 7 à 11 % (très bon)
- **Villa Belle Époque vue mer** : 5 à 7 % (correct + très forte plus-value à long terme)

À Dinard, **viser moins de 6 % de rendement brut sur un bien Airbnb premium est jouable** car la plus-value tendancielle (+2 à +3 %/an sur les villas vue mer) compense largement. Pour un studio ou T2, **viser 8 à 10 % de rendement brut est réaliste** avec un pilotage professionnel.

## Notre étude personnalisée pour votre bien à Dinard

Vous voulez **chiffrer précisément** ce que rapporterait votre logement à Dinard ? Notre équipe Côte d'Émeraude réalise gratuitement une **étude personnalisée sous 24 h** :

- Tarification optimale par saison
- Taux d'occupation projeté
- Revenus annuels nets estimés
- Comparaison avec votre situation actuelle
- Plan d'action concret (photos, annonce, déclaration mairie)

[**Demander mon étude gratuite Dinard →**](/contact)
[**Tester le simulateur de revenus →**](/simulateur)

## Pour aller plus loin

- [Mettre son logement en Airbnb à Dinard : le guide propriétaire 2026](/blog/mettre-airbnb-dinard-guide-proprietaire-2026)
- [Réglementation Airbnb à Dinard : déclaration mairie, taxe de séjour, changement d'usage](/blog/reglementation-airbnb-dinard-declaration-taxe-sejour-2026)
- [Le guide propriétaire Dinard 2026](/blog/louer-airbnb-dinard-guide-proprietaire)
- [Investir sur la Côte d'Émeraude en 2026](/blog/investir-airbnb-cote-emeraude-bretagne-2026)
- [LMNP Bretagne, fiscalité 2026](/blog/lmnp-airbnb-bretagne-fiscalite-2026)
`,
  },

  // =====================================================================
  // 44, Réglementation Airbnb à Dinard (MAI 2026)
  // =====================================================================
  {
    slug: "reglementation-airbnb-dinard-declaration-taxe-sejour-2026",
    category: "reglementation",
    title: "Réglementation Airbnb à Dinard en 2026 : déclaration mairie, taxe de séjour, changement d'usage",
    excerpt:
      "Loi Le Meur, numéro d'enregistrement à 13 chiffres, taxe de séjour 2026, changement d'usage, plafond 120 jours : tout ce qu'un propriétaire doit savoir pour louer son Airbnb à Dinard dans les règles.",
    image: "/images/blog/reglementation-airbnb-dinard-declaration-taxe-sejour-2026.jpg",
    imageAlt: "Hôtel de ville de Dinard et démarches administratives, réglementation Airbnb et déclaration meublé de tourisme Côte d'Émeraude",
    author: "L'équipe Terre et Mer",
    date: "2026-05-14",
    readTime: 9,
    keywords:
      "réglementation airbnb dinard, déclaration mairie airbnb dinard, taxe de séjour dinard 2026, numéro enregistrement airbnb dinard, loi le meur dinard, changement d'usage dinard, plafond 120 jours airbnb dinard, meublé de tourisme dinard, cerfa airbnb dinard, sanctions airbnb dinard",
    relatedCities: ["dinard", "saint-lunaire", "pleurtuit", "saint-malo"],
    content: `
## Réglementation Airbnb à Dinard en 2026 : ce qui a changé

Depuis la **loi Le Meur du 19 novembre 2024**, les règles de location courte durée en France ont été profondément durcies, et Dinard, comme toutes les communes touristiques de plus de 1 500 habitants, applique désormais ce cadre strict. Pour louer votre logement en Airbnb à Dinard en 2026, vous devez impérativement respecter **4 obligations légales** :

1. La **déclaration en mairie** + obtention du numéro d'enregistrement à 13 chiffres
2. Le **respect du plafond 120 jours/an** si résidence principale
3. La **collecte de la taxe de séjour** (automatique via Airbnb/Booking)
4. Le **changement d'usage** (uniquement pour résidence secondaire dans certaines configurations)

Cet article détaille **chaque obligation pas à pas**, avec les démarches précises à Dinard, les sanctions en cas de non-respect, et les évolutions à anticiper pour 2026-2027.

> ⚠️ **Cet article a une vocation informative.** Pour toute situation particulière (copropriété spéciale, bail en cours, indivision, SCI), consultez un notaire ou un avocat fiscaliste. Notre équipe Terre et Mer prend en charge l'intégralité des démarches mairie pour ses propriétaires, mais ne se substitue pas à un conseil juridique.

## Obligation 1, La déclaration en mairie de Dinard (CERFA n° 14004*04)

### Qui doit déclarer ?

Depuis la loi Le Meur, **toute mise en Airbnb à Dinard nécessite une déclaration en mairie**, que vous soyez :

- **Propriétaire en résidence principale** (vous y habitez plus de 8 mois/an), démarche obligatoire depuis novembre 2024 (alors qu'avant la loi, vous étiez exempté).
- **Propriétaire en résidence secondaire** (vous y séjournez moins de 4 mois/an), démarche obligatoire depuis 2018, et toujours en vigueur.
- **Locataire avec accord écrit du bailleur** (sous-location), démarche obligatoire.

### Démarche pas à pas

1. **Télécharger le formulaire CERFA n° 14004*04** sur [service-public.fr](https://www.service-public.fr) (Démarches → Logement → Meublé de tourisme).
2. **Le remplir intégralement** : adresse exacte du logement, surface, nombre de pièces, nombre de couchages, classement éventuel, qualité du déclarant (propriétaire / locataire / gestionnaire).
3. **Joindre** : pièce d'identité, justificatif de propriété (titre de propriété ou taxe foncière), plan du logement.
4. **Déposer le dossier** à la **mairie de Dinard, 47 Boulevard Féart, 35800 Dinard**, soit en personne (lundi-vendredi 8h30-12h / 13h30-17h, samedi 9h-12h) soit par **lettre recommandée avec accusé de réception**.
5. **Recevoir sous 3 à 5 jours ouvrés** votre **numéro d'enregistrement à 13 chiffres** au format *22XXXX-X-XX-XXXXX*.

### Pourquoi c'est obligatoire sur l'annonce

Ce numéro à 13 chiffres doit **obligatoirement figurer sur votre annonce Airbnb, Booking, Vrbo et tout autre site de location courte durée**. Sans lui :

- Airbnb suspend automatiquement votre annonce sous 30 jours (croisement obligatoire avec le fichier ministère).
- Booking applique la même règle depuis mai 2025.
- Vous risquez une **amende administrative pouvant aller jusqu'à 15 000 € par bien** (sanction prévue par la loi Le Meur).

> 🚀 **Bonne nouvelle** : pour nos propriétaires Terre et Mer, notre équipe se charge intégralement de la démarche mairie (formulaire, pièces, dépôt). Délai moyen : 3 à 5 jours ouvrés. Vous recevez votre numéro par e-mail sans rien faire.

## Obligation 2, Le plafond 120 jours/an (résidence principale uniquement)

### Quand s'applique-t-il ?

Le plafond **120 jours par an** s'applique uniquement aux **résidences principales** (logement où vous habitez plus de 8 mois/an). Concrètement, si vous louez votre appartement de Dinard pendant vos absences (week-ends à Paris, congés à l'étranger), **vous ne pouvez pas dépasser 120 nuitées Airbnb par année civile**.

Depuis la loi Le Meur de 2024, ce plafond a été **renforcé** : il s'applique automatiquement et est contrôlé directement par les plateformes (Airbnb bloque les réservations au-delà de 120 nuits).

### Et pour une résidence secondaire à Dinard ?

**Pas de plafond annuel à Dinard en 2026.** La ville n'a pas instauré de mesure de compensation ni de quota, contrairement à Paris, Lyon, Bordeaux ou Saint-Malo intra-muros. Vous pouvez donc louer votre résidence secondaire **365 jours/an** si vous le souhaitez, à condition d'avoir le numéro d'enregistrement et de respecter la copropriété.

> ⚠️ **À surveiller** : la mairie de Dinard a évoqué en conseil municipal (mai 2025) la possibilité d'instaurer une **mesure de compensation** type Saint-Malo intra-muros à l'horizon 2027-2028 si la pression locative continue à augmenter. Pour l'instant aucune date n'est arrêtée. Notre équipe suit le dossier et vous tiendra informé en cas d'évolution.

## Obligation 3, La taxe de séjour à Dinard en 2026

### Montants 2026 à Dinard

La taxe de séjour est due **par personne et par nuitée**, hors mineurs de moins de 18 ans. Tarifs 2026 à Dinard (vote du conseil municipal de décembre 2025) :

| Type d'hébergement | Tarif HT / pers / nuit | + Taxe additionnelle départementale 10 % | **Total TTC** |
|---|---|---|---|
| Palace | 4,30 € | 0,43 € | **4,73 €** |
| Hôtel 5★ / résidence tourisme 5★ | 3,10 € | 0,31 € | **3,41 €** |
| Hôtel 4★ / résidence tourisme 4★ | 2,40 € | 0,24 € | **2,64 €** |
| Hôtel 3★ / résidence tourisme 3★ | 1,55 € | 0,16 € | **1,71 €** |
| Hôtel 2★ / résidence tourisme 2★ / **meublé classé 3 ou 4★** | 0,95 € | 0,10 € | **1,05 €** |
| Meublé classé 1 ou 2★ / chambres d'hôtes | 0,80 € | 0,08 € | **0,88 €** |
| **Meublé non classé** | **5 % du prix de la nuit / pers** (plafond 4,30 €) | +10 % | **Variable** |

**Cas le plus fréquent** : votre Airbnb n'est pas classé "meublé de tourisme" (démarche optionnelle auprès d'un organisme accrédité type Atout France). Vous tombez donc dans la catégorie **"meublé non classé"** = **5 % du prix de la nuit/pers, plafonnée à 4,30 €**.

Exemple concret : pour un T2 loué 120 € la nuit à 2 voyageurs :
- Taxe = 120 € × 5 % / 2 personnes = 3 € par personne par nuit
- 2 personnes × 3 € = 6 € par nuit
- +10 % départementale = 6,60 € / nuit total

### Qui la collecte ?

- **Airbnb / Booking / Vrbo** la collectent **automatiquement** depuis 2019 et la reversent directement à la commune de Dinard. **Vous n'avez rien à gérer**.
- **Locations en direct** (gîtes de France, contact direct) : vous devez la collecter vous-même et la reverser trimestriellement à la mairie de Dinard via la plateforme [taxesejour.impots.gouv.fr](https://taxesejour.impots.gouv.fr).

> 💡 **Astuce pour les meublés classés** : faire classer son meublé en 3★ ou 4★ (~250 € tous les 5 ans via Atout France) divise la taxe de séjour par 4 à 6. Pour un bien qui tire 30 000 €/an de revenus, économie possible : 200 à 350 €/an + image premium pour l'annonce. Notre équipe peut vous orienter vers les bons organismes.

## Obligation 4, Le changement d'usage (cas spécifiques)

### Qui est concerné ?

Le **changement d'usage** est une **autorisation préfectorale** distincte de la déclaration en mairie. Il s'applique à Dinard dans **un cas précis** :

- Vous êtes **propriétaire d'une résidence secondaire** à Dinard
- ET vous voulez la louer **plus de 4 mois/an en courte durée**
- ET le logement est situé dans un secteur soumis à autorisation (à Dinard, la ville **n'a pas encore mis en œuvre** le changement d'usage généralisé en 2026)

En clair, **à Dinard en 2026 : pas de changement d'usage requis** pour les résidences secondaires. Mais cela peut évoluer (voir plus haut).

### Si Dinard active le dispositif en 2027

Si la mairie active le changement d'usage, vous devrez :

1. Déposer une **demande préalable** en mairie
2. Justifier d'une **compensation** (acquisition d'un local commercial transformé en habitation à équivalent surface)
3. Recevoir l'autorisation (3 à 6 mois en moyenne)

C'est lourd. **Anticipez** si vous achetez aujourd'hui à Dinard : privilégiez les biens en résidence secondaire **déjà en location courte durée depuis 2 ans minimum** (droit acquis) ou les biens neufs avec autorisation d'usage incluse.

## Cas particuliers à connaître

### Copropriété avec clause d'habitation bourgeoise

Environ **15 % des copropriétés dinardaises** (notamment Boulevard Wilson, Boulevard Féart, La Vicomté) contiennent une **clause d'habitation bourgeoise** qui **interdit toute activité commerciale**, y compris la location courte durée Airbnb. À vérifier impérativement avant tout investissement. Une assemblée générale peut lever la clause à la majorité des 2/3, mais c'est rare.

### Bail à votre nom (locataire qui sous-loue)

Si vous êtes **locataire** (non propriétaire), la sous-location en Airbnb est **interdite sans accord écrit du bailleur**. À Dinard, peu de bailleurs acceptent. Risque : résiliation du bail + restitution des loyers Airbnb perçus.

### SCI familiale

Une SCI peut détenir un bien Airbnb à Dinard. Régime fiscal : la SCI doit opter pour l'**IS** (impôt sur les sociétés) si l'activité courte durée représente plus de 10 % des revenus de la SCI. Sinon, l'administration fiscale peut requalifier d'office. Voir un expert-comptable spécialisé.

### Bail mobilité / location moyenne durée

Une alternative à Airbnb : le **bail mobilité** (1 à 10 mois, étudiants ou professionnels en mobilité). Moins lucratif que Airbnb mais **pas soumis au plafond 120 jours** et **pas de changement d'usage**. Intéressant pour des biens à Dinard centre proche gare TGV. Notre équipe peut combiner les 2 stratégies sur l'année.

## Sanctions en cas de non-respect

Voici les sanctions prévues par la loi Le Meur 2024 (en vigueur depuis le 1er janvier 2025) :

| Manquement | Sanction |
|---|---|
| Pas de numéro d'enregistrement sur l'annonce | Jusqu'à **15 000 € par bien** |
| Dépassement du plafond 120 jours (résidence principale) | Jusqu'à **10 000 € par bien** |
| Non-collecte de la taxe de séjour (location en direct) | Jusqu'à **2 500 € par infraction** |
| Absence de changement d'usage (quand requis) | Jusqu'à **50 000 € par bien** |

Ces sanctions sont **applicables par la commune** et **les contrôles se sont intensifiés** depuis 2025 grâce au croisement automatique des données plateformes / fichier ministère.

## Le rôle de notre conciergerie dans la conformité

Quand vous nous confiez votre bien à Dinard, nous prenons en charge **l'intégralité des démarches réglementaires** :

- Préparation et dépôt du formulaire CERFA en mairie de Dinard
- Suivi du numéro d'enregistrement
- Ajout automatique du numéro sur Airbnb, Booking, Vrbo
- Configuration de la collecte automatique de taxe de séjour
- Veille réglementaire (changements 2027 anticipés)
- Conseil sur le classement meublé 3★ ou 4★ (optionnel mais rentable)
- Conformité loi Le Meur en continu

**Aucune amende n'a été reçue par nos propriétaires Dinard depuis la création de Terre et Mer.** C'est notre engagement.

[**Confier la conformité de mon bien Dinard à Terre et Mer →**](/contact)
[**Voir notre offre Dinard →**](/villes/dinard)

## Évolutions à anticiper pour 2026-2027

- **Renforcement contrôle Airbnb** : croisement automatique avec la déclaration fiscale BIC 2042-C-PRO (revenus locatifs courte durée), risque de redressement si revenus déclarés < revenus plateformes.
- **Création possible d'un quota Dinard** : pas voté à ce jour, mais évoqué en conseil municipal de mai 2025. Suivez les actualités du conseil pour anticiper.
- **Hausse possible de la taxe de séjour** : tendance nationale +5 à +8 % par an. Dinard reste pour l'instant dans une fourchette modérée.
- **Loi PACTE 2 (à voter fin 2026)** : pourrait étendre le plafond 120 jours aux résidences secondaires dans les zones tendues. Dinard est en zone tendue.

## Pour aller plus loin

- [Mettre son logement en Airbnb à Dinard : le guide propriétaire 2026](/blog/mettre-airbnb-dinard-guide-proprietaire-2026)
- [Combien rapporte un Airbnb à Dinard ? Revenus 2026 par type de bien](/blog/combien-rapporte-airbnb-dinard-revenus-2026)
- [Le guide propriétaire Dinard 2026](/blog/louer-airbnb-dinard-guide-proprietaire)
- [Réglementation 2026 sur Saint-Malo, Dinard et Cancale](/blog/reglementation-airbnb-saint-malo-dinard-cancale-2026)
- [LMNP Bretagne, fiscalité 2026](/blog/lmnp-airbnb-bretagne-fiscalite-2026)

> 🤝 **Besoin d'un accompagnement complet ?** Notre équipe Côte d'Émeraude (basée à Pleurtuit, 5 min de Dinard) prend en charge l'intégralité des démarches : déclaration mairie, conformité plateformes, taxe de séjour, classement meublé optionnel. Vous récupérez votre numéro d'enregistrement sous 3 à 5 jours ouvrés. [Demander une étude gratuite sous 24 h →](/contact)
`,
  },

];

// Get article by slug
export const getArticle = (slug) =>
  BLOG_ARTICLES.find((a) => a.slug === slug);

// Get related articles (same category, exclude current; fallback: most recent overall)
export const getRelatedArticles = (currentSlug, limit = 3) => {
  const current = getArticle(currentSlug);
  if (!current) return [];
  const sameCat = BLOG_ARTICLES
    .filter((a) => a.slug !== currentSlug && a.category === current.category)
    .slice(0, limit);
  if (sameCat.length >= limit) return sameCat;
  // Fallback : fill with most recent articles from other categories
  const fillers = BLOG_ARTICLES
    .filter((a) => a.slug !== currentSlug && a.category !== current.category)
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .slice(0, limit - sameCat.length);
  return [...sameCat, ...fillers];
};
